import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import { handleCnicInput } from '../utils/cnic';
import { GUARDIAN_RELATIONSHIPS, guardianRelLabel, describeGuardian } from '../constants/guardians';
import { EMPTY_NEW_GUARDIAN, accompaniedByOf, isMinorPatient, isParentOption, newGuardianPayload, newGuardianProblem, parentGuardianPayload, parentOptionsFor } from '../utils/guardians';
import { PATIENT_PAYMENT_MODES, PAYMENT_MODE_LABEL, paymentNeedsProof } from '../constants/payments';
import PaymentProofUpload from './PaymentProofUpload';
import { useCashDrawer, isDrawerCancelled } from './cashDrawer/CashDrawerContext';
import { createPortal } from 'react-dom';
import {
  Activity,
  AlertCircle,
  AlertTriangle,
  ArrowDownWideNarrow,
  ArrowRight,
  ArrowUpNarrowWide,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  ClipboardList,
  Edit2,
  Eye,
  FileText,
  FlaskConical,
  Heart,
  History,
  Image,
  Lightbulb,
  Lock,
  MapPin,
  NotebookPen,
  Paperclip,
  Phone,
  Pill,
  Plus,
  RefreshCw,
  Search,
  Shield,
  Sparkles,
  Stethoscope,
  Trash2,
  TrendingUp,
  Upload,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { patientAPI, visitAPI, doctorAPI, medicalServiceAPI, patientDocumentAPI, prescriptionAPI, invoiceAPI, referralAPI, patientReportNoteAPI, intakeAPI } from '../services/api';
import { reportNoteCategory } from '../utils/reportNotes';
import PrescriptionDetailModal from './PrescriptionDetailModal';
import ProfilePicturePicker from './ProfilePicturePicker';
import RowActionsMenu from './RowActionsMenu';
import CountryCodeSelect from './CountryCodeSelect';
import usePhoneCountryCode from '../hooks/usePhoneCountryCode';
import TagInput from './TagInput';
import DatePicker from './DatePicker';
import { useToast, useConfirm } from './UIProvider';
import useDialog from '../hooks/useDialog';
import { buildPhone, parsePhone, getPhoneLength } from '../utils/phone';
import { toTitleCase } from '../utils/text';
import { tagBadgeClasses, nameAvatarClasses, nameInitials } from '../utils/tagColors';
import { FEATURES } from '../config/features';
import { authedMediaUrl } from '../utils/media';
import { formatClinicDate, formatClinicDateTime, clinicToday, toClinicDate, clinicNow } from '../utils/clinicTime';
import { certainOf, cnicTakenBy, lookalikeQueryFrom, needsAcknowledgement } from '../utils/lookalikes';
import ImageLightbox from './ImageLightbox';
import CnicDocUpload from './CnicDocUpload';
import { FilePreviewEye } from './MediaPreview';
import { scrollContentToTop, useScrollToTopOnChange } from '../utils/scroll';
import Select from './Select';
import TablePager, { DEFAULT_PAGE_SIZE, usePagedRows } from './TablePager';
import { dobFromAge, formatAge, formatPatientAge } from '../utils/age';
import { getAgeBandKey, formatBmi, roundBmi } from '../utils/vitals';
import { onActivateKey, ROW_FOCUS_RING, keepEnterFromSubmitting, focusFirstInvalidField } from '../utils/keyboard';
import {
  NAME_MAX_LENGTH, PATIENT_FORM_FIELDS, calculateAgeYears, describeBlockers, focusFieldByName,
  identityPayload, patientFormBlockers, statusNeedsRecording, statusPayload, whileTyping,
} from '../utils/patientFormRules';
// The token receipt (markup, print stylesheet and token formatting) is shared with Token Queue →
// Reprint, so a duplicate slip is guaranteed identical to the original issued here.
import TokenPrintModal, { formatVisitToken, printTokenReceipt } from './TokenPrintModal';

// Module-level "closer" for whichever patient-avatar preview is currently open, so opening a
// new one closes the previous (only one preview visible at a time across the whole list).
let CLOSE_ACTIVE_PREVIEW = null;

// Patient-list avatar. Shows the profile photo (or colour-hashed initials). When the patient
// has a photo, hovering the avatar pops an enlarged preview bubble for quick identification;
// the bubble stays open until the user clicks its X or clicks outside (not on mouse-leave, so
// it's easy to move the cursor onto it). Renders nothing clickable into the row — clicks on the
// avatar area are swallowed so they don't also open the patient details modal.
const PatientRowAvatar = ({ patient }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const avatarRef = useRef(null);
  const bubbleRef = useRef(null);
  const closeTimerRef = useRef(null);
  const name = patient.fullName || `${patient.firstName} ${patient.lastName}`;
  const imgUrl = patient.profileImageUrl ? authedMediaUrl(patient.profileImageUrl) : null;

  // Hover close uses a short grace delay so moving the cursor from the avatar across the
  // small gap into the preview bubble doesn't trip the mouseleave-close before mouseenter
  // on the bubble cancels it.
  const cancelClose = () => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => setOpen(false), 120);
  };

  // Compute a viewport-fixed position next to the avatar (rendered via portal so the table's
  // overflow can't clip it). Prefers the right of the avatar; flips left if it would overflow,
  // and clamps vertically into the viewport.
  const openPreview = () => {
    cancelClose();
    if (!imgUrl || !avatarRef.current) return;
    // Only one preview open at a time — hovering a new avatar closes the previous one.
    if (CLOSE_ACTIVE_PREVIEW) CLOSE_ACTIVE_PREVIEW();
    CLOSE_ACTIVE_PREVIEW = () => setOpen(false);
    const r = avatarRef.current.getBoundingClientRect();
    const W = 210, H = 232, GAP = 8;
    let left = r.right + GAP;
    if (left + W > window.innerWidth - GAP) left = r.left - W - GAP;
    if (left < GAP) left = GAP;
    let top = r.top - 8;
    if (top + H > window.innerHeight - GAP) top = window.innerHeight - H - GAP;
    if (top < GAP) top = GAP;
    setPos({ left, top });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (avatarRef.current?.contains(e.target)) return;
      if (bubbleRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    // Capture phase, stopped: Escape closes the preview and nothing behind it.
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  // Clear any pending close timer on unmount.
  useEffect(() => cancelClose, []);

  return (
    <div
      ref={avatarRef}
      className="flex-shrink-0"
      onClick={(e) => e.stopPropagation()}
      onMouseLeave={scheduleClose}
    >
      {imgUrl ? (
        <img
          src={imgUrl}
          alt={name}
          onMouseEnter={openPreview}
          className="w-16 h-16 rounded-full object-cover border border-gray-200 cursor-pointer"
        />
      ) : (
        <div className={`w-16 h-16 rounded-full flex items-center justify-center text-lg font-bold border ${nameAvatarClasses(name)}`}>
          {nameInitials(name)}
        </div>
      )}
      {open && imgUrl && pos && createPortal(
        <div
          ref={bubbleRef}
          style={{ position: 'fixed', left: pos.left, top: pos.top, zIndex: 60 }}
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          className="w-52 bg-white rounded-2xl shadow-2xl border border-gray-200 p-3"
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-gray-200 shadow flex items-center justify-center text-gray-500 hover:text-red-500"
            title="Close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <img src={imgUrl} alt={name} className="w-full h-44 object-cover rounded-xl" />
          <div className="mt-2 text-xs font-semibold text-gray-800 truncate text-center">{name}</div>
        </div>,
        document.body,
      )}
    </div>
  );
};

// FilePreviewEye (CNIC eye + hover image preview) now lives in ./MediaPreview, shared with the
// User Management module.

// Marital status options — values match backend enum, labels are user-facing.
// Alphabetical by label.
const MARITAL_STATUSES = [
  { value: 'DIVORCED',  label: 'Divorced' },
  { value: 'MARRIED',   label: 'Married' },
  { value: 'SEPARATED', label: 'Separated' },
  { value: 'SINGLE',    label: 'Single' },
  { value: 'WIDOWED',   label: 'Widowed' },
];
const maritalStatusLabel = (value) =>
  MARITAL_STATUSES.find(m => m.value === value)?.label || value;

// Guardian relationships and their labels live in ../constants/guardians, shared with the token
// slip and the queue.

const GUARDIAN_INPUT = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500';

// The fields for an adult being added to a child's file, shared by "Someone else" in the Issue
// Token dialog and "Add guardian" on the record. Name and relationship are what the desk knows
// for certain; the CNIC is what later finds the child from this adult, when they have it on them.
const GuardianFields = ({ value, onChange }) => {
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
        <input
          type="text"
          value={value.name}
          onChange={(e) => set({ name: e.target.value })}
          maxLength={100}
          placeholder="Full name"
          className={GUARDIAN_INPUT}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Relationship *</label>
        <Select
          value={value.relationship}
          onChange={(v) => set({ relationship: v })}
          options={GUARDIAN_RELATIONSHIPS}
          placeholder="Select relationship"
          buttonClassName="px-3 py-2 rounded-lg"
        />
      </div>
      {value.relationship === 'OTHER' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Relationship (specify) *</label>
          <input
            type="text"
            value={value.relationshipOther}
            onChange={(e) => set({ relationshipOther: e.target.value })}
            maxLength={100}
            placeholder="e.g. Step-father"
            className={GUARDIAN_INPUT}
          />
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">CNIC</label>
        <input
          type="text"
          value={value.cnic}
          onChange={(e) => handleCnicInput(e, value.cnic, (v) => set({ cnic: v }))}
          maxLength={15}
          placeholder="42401-1234567-8"
          className={GUARDIAN_INPUT}
        />
      </div>
      {/* The payload and the guardian row have carried a phone number all along; nothing ever
          asked for one. It is how the desk reaches whoever actually has the child today. */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
        <input
          type="tel"
          value={value.phoneNumber || ''}
          onChange={(e) => set({ phoneNumber: e.target.value })}
          maxLength={20}
          placeholder="0300-1234567"
          className={GUARDIAN_INPUT}
        />
      </div>
    </div>
  );
};

// Patient lifecycle status — labels + colors shared by the view modal, the table row,
// and any other surface that wants to render a status badge. Keep the keys in sync with
// the backend Patient.PatientStatus enum.
const PATIENT_STATUS_CONFIG = {
  ACTIVE:      { label: 'Active',      badge: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
  DECEASED:    { label: 'Deceased',    badge: 'bg-slate-200  text-slate-800   border-slate-300',    dot: 'bg-slate-500'   },
  SUSPENDED:   { label: 'Suspended',   badge: 'bg-amber-100  text-amber-800   border-amber-200',    dot: 'bg-amber-500'   },
  BLACKLISTED: { label: 'Blacklisted', badge: 'bg-red-100    text-red-700     border-red-200',      dot: 'bg-red-500'     },
};
const patientStatusConfig = (status) => PATIENT_STATUS_CONFIG[status] || PATIENT_STATUS_CONFIG.ACTIVE;

// The list shows only the patients that match a search. Without one, nothing is fetched and the
// list stays empty. Listing every patient on arrival put names, phone numbers and addresses on
// screen for anyone at the desk, a confidentiality risk the owner raised (2026-09-29); the
// registration-date filters went with it, as the owner no longer needed them.
const hasListCriteria = (term) => !!(term || '').trim();

// Standalone age display — used by both the main component and child components
const PatientManagement = ({ searchQuery = '', onNavigateToToken, hasPermission }) => {
  const { withDrawer } = useCashDrawer();
  // Permission-check helper — defaults to fail-open when no `hasPermission` prop has been
  // wired (e.g. the component is used in a context without an auth shell). The shell in
  // App.js passes the real one, which respects the role's effective permissions.
  const can = useCallback(
    (action) => (typeof hasPermission === 'function' ? hasPermission('PATIENTS', action) : true),
    [hasPermission],
  );
  const notify = useToast();
  const confirm = useConfirm();
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false); // separate from `loading` — true only during in-flight search
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchTotal, setSearchTotal] = useState(0);
  // Why a search of an identifier found nothing, when the server could tell us.
  const [searchHint, setSearchHint] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');   // backend whitelist field
  const [sortDir, setSortDir] = useState('desc');
  const [showAddForm, setShowAddForm] = useState(false);
  // CNIC document staged from the inline upload icon. Uploaded after the patient is
  // created or updated via patientAPI.uploadCnicDocument. Reset on form close.
  const [cnicFile, setCnicFile] = useState(null); // CNIC front
  const [cnicBackFile, setCnicBackFile] = useState(null); // CNIC back
  // Guardian CNIC document (minor patients) — same staging+upload-after-save pattern as cnicFile.
  const [guardianCnicFile, setGuardianCnicFile] = useState(null); // guardian CNIC front
  const [guardianCnicBackFile, setGuardianCnicBackFile] = useState(null); // guardian CNIC back
  // Profile picture (file OR captured from the camera). Same staging+upload-after-save
  // pattern as cnicFile.
  const [profileImageFile, setProfileImageFile] = useState(null);
  // True when the user removed an existing profile photo while editing (and didn't stage a
  // replacement) — on save we DELETE it from the backend.
  const [removeProfileImage, setRemoveProfileImage] = useState(false);
  const [editingPatientId, setEditingPatientId] = useState(null); // null = creating, UUID = editing
  // Full patient object held in edit mode so the form can surface things like the
  // existing cnicDocumentUrl (the green-check state on the CNIC upload icon).
  const [editingPatient, setEditingPatient] = useState(null);
  // Remember the patient's status at the moment we entered edit mode, so the Status
  // section can prompt for a reason on EVERY toggle (including back to Active) — not
  // just when the new status is non-Active.
  const [originalPatientStatus, setOriginalPatientStatus] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  // Which tab the details modal should open on. Defaults to 'info' when the modal is opened
  // via the View button; set to 'documents' / 'visits' / 'invoices' by the deep-link row
  // actions so those tabs come into view without the user clicking through.
  const [selectedPatientTab, setSelectedPatientTab] = useState('info');
  const [searchTerm, setSearchTerm] = useState(searchQuery);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenPatient, setTokenPatient] = useState(null);
  const tokenDialogRef = useDialog(() => setShowTokenModal(false));
  const searchAbortRef = useRef(null);
  // Whether a search has been typed. Until it has, the list shows a prompt, never patients.
  const listing = hasListCriteria(searchTerm);
  // The list is paged on the server; the shared pager draws its count and buttons from this.
  const pagerTopRef = useRef(null);
  const patientPaging = {
    page: currentPage,
    pageSize,
    total: listing ? searchTotal : 0,
    pages: Math.max(1, totalPages),
    setPage: setCurrentPage,
    setPageSize: (n) => { setPageSize(n); setCurrentPage(0); },
    topRef: pagerTopRef,
  };
  // Sort options surfaced in the toolbar dropdown. Values match the backend sort whitelist.
  const SORT_OPTIONS = [
    { value: 'createdAt', label: 'Registration date' },
    { value: 'firstName', label: 'Name' },
    { value: 'mrn', label: 'MR No.' },
    { value: 'phoneNumber', label: 'Phone' },
  ];
  
  // NEW: Success modal state for after patient registration
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [newlyCreatedPatient, setNewlyCreatedPatient] = useState(null);
  
  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success', tokenNumber: null });
  
  // Print receipt state
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printData, setPrintData] = useState(null);
  const printRef = useRef(null);
  
  // Doctors and services for token issuance
  const [doctors, setDoctors] = useState([]);
  const [medicalServices, setMedicalServices] = useState([]);
  
  // Initial form state
  const initialFormState = {
    title: '',
    firstName: '',
    lastName: '',
    fatherName: '',
    motherName: '',
    husbandName: '',
    phoneNumber: '',
    cnic: '',
    dateOfBirth: '',
    gender: '',
    maritalStatus: '',
    area: '',
    town: '',
    city: '',
    fatherCnic: '',
    motherCnic: '',
    // Which parent's CNIC the file is kept under. Not persisted: which column holds a value says
    // it already. It exists so the form can ask once and show one field instead of two.
    parentCnicOwner: '',
    bFormNumber: '',
    // When the family does not know the date: the age they gave, and the flag that marks the date
    // derived from it as an estimate.
    dobEstimated: false,
    ageYears: '',
    ageMonths: '',
    tagNames: [], // free-text tags; backend resolves to existing tags case-insensitively or creates new
    // Lifecycle status — defaults to ACTIVE for new patients. When set to any non-ACTIVE
    // value in the Edit modal, statusChangedAt + statusReason become required.
    status: 'ACTIVE',
    statusChangedAt: '',
    statusReason: '',
  };
  
  const [formData, setFormData] = useState(initialFormState);
  const [touched, setTouched] = useState({});
  // The server's "this CNIC is already registered" reply: { cnic, message }. Only the server can
  // know it, so it is kept apart from the field rules, and it stands for as long as the CNIC typed is
  // the one it refused. Leaving the field, or a save attempt, no longer wipes it.
  const [duplicateCnic, setDuplicateCnic] = useState(null);

  // What the rules need to know besides the form: on an edit, the status the file opened with and
  // the file as it was loaded. The footer, the button, the inline messages and the save all read the
  // rules through this one object.
  const ruleOptions = { originalStatus: originalPatientStatus, onFile: editingPatient };
  const duplicateCnicShown = duplicateCnic && duplicateCnic.cnic === String(formData.cnic || '').trim()
    ? duplicateCnic
    : null;
  // The message under each field, worked out from the form as it is now, never stored. A stored
  // message went stale: switching between a date and an age, or changing the status twice, left a
  // message written against the values from before the change. A field still shows its message
  // only once it has been visited (`touched`).
  const formErrors = {};
  patientFormBlockers(formData, ruleOptions).forEach((b) => { formErrors[b.field] = b.message; });
  if (!formErrors.cnic && duplicateCnicShown) formErrors.cnic = duplicateCnicShown.message;

  // Token form state
  const initialTokenFormState = {
    doctorId: '',
    medicalServiceId: '',
    // Discount is selected as a percentage (0/10/20/.../100); the Rs amount sent to the
    // backend is derived from the selected service's baseFee × discountPercent / 100. Keeping
    // discountAmount in state too so existing submit/print paths don't need to know about
    // the percent — they read the same Rs field as before.
    discountPercent: 0,
    discountAmount: 0,
    emergency: false,
    emergencyReason: '',
    revisitReason: '',
    // OPD fee payment mode. Defaults to CASH (most common). Hidden in the UI when the
    // computed final fee is zero — backend forces NONE in that case.
    paymentMode: 'CASH',
  };
  // Today's visit footprint for the patient currently being issued a token. Drives the
  // duplicate-token block (hasActiveVisit) and the revisit-reason gating (hasCompletedVisit).
  // Fetched when the Issue-Token modal opens; null = still loading / no patient selected.
  const [patientTodayStatus, setPatientTodayStatus] = useState(null);
  const [patientTodayStatusLoading, setPatientTodayStatusLoading] = useState(false);
  // Credit held in the patient's name (from referrals) — offered against the token fee.
  const [patientCredit, setPatientCredit] = useState(0);
  const [applyCredit, setApplyCredit] = useState(true);
  // Who brought a child in. The adults on the child's file, the one picked (a guardian id, or
  // 'NEW' for "Someone else"), and the new adult being typed in that case. An adult's token
  // skips the question.
  const [tokenGuardians, setTokenGuardians] = useState([]);
  const [broughtBy, setBroughtBy] = useState('');
  const [newGuardian, setNewGuardian] = useState(EMPTY_NEW_GUARDIAN);
  const tokenPatientIsMinor = isMinorPatient(tokenPatient);
  // The child's own father and mother, offered alongside the adults already on the file. Picking
  // one records it without asking for anything: the name and CNIC are on the child's record.
  const tokenParentOptions = tokenPatientIsMinor ? parentOptionsFor(tokenPatient, tokenGuardians) : [];
  const tokenParentPick = tokenParentOptions.find(o => o.value === broughtBy) || null;

  const [tokenForm, setTokenForm] = useState(initialTokenFormState);
  // The transfer screenshot for an OPD fee paid by wallet or bank transfer.
  const [tokenProofFiles, setTokenProofFiles] = useState([]);
  const [tokenProofError, setTokenProofError] = useState(null);

  // Load doctors and services on component mount
  useEffect(() => {
    loadTokenData();
  }, []);

  const loadTokenData = async () => {
    // Load doctors - handle error independently
    try {
      const doctorsData = await doctorAPI.getAll();
      setDoctors(Array.isArray(doctorsData) ? doctorsData : []);
    } catch (error) {
      console.error('Error loading doctors:', error);
      setDoctors([]);
    }
    // Services are doctor-scoped — loaded on demand when a doctor is selected (see effect below).
  };

  // When the selected doctor changes in the token form, refetch the services available for
  // that doctor (general + their own). If no doctor is selected, the dropdown stays empty.
  useEffect(() => {
    const loadServicesForDoctor = async () => {
      if (!tokenForm.doctorId) {
        setMedicalServices([]);
        return;
      }
      try {
        const servicesData = await medicalServiceAPI.getForDoctor(tokenForm.doctorId);
        const list = Array.isArray(servicesData) ? servicesData : [];
        setMedicalServices(list);
        // If the previously-picked service is no longer in the filtered list, clear it
        setTokenForm(prev => {
          if (prev.medicalServiceId && !list.find(s => s.id === prev.medicalServiceId)) {
            return { ...prev, medicalServiceId: '' };
          }
          return prev;
        });
      } catch (error) {
        console.error('Error loading services for doctor:', error);
        setMedicalServices([]);
      }
    };
    loadServicesForDoctor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenForm.doctorId]);

  // The field checks (name, CNIC, phone, date of birth) and calculateAgeYears live in
  // ../utils/patientFormRules, with the rules the save, the Register button and the footer share.

  // Alias for the module-level formatAge — used in the form hint and passed to child components
  const calculateAge = formatAge;

  const isAdult = (dateOfBirth) => {
    return calculateAgeYears(dateOfBirth) >= 18;
  };

  // Husband name applies only to adult women whose marital status is married or widowed.
  // Used at submit time to strip stale values when conditions no longer hold.
  const isHusbandNameApplicable = (data) =>
    data.gender === 'FEMALE' &&
    !!data.dateOfBirth &&
    isAdult(data.dateOfBirth) &&
    (data.maritalStatus === 'MARRIED' || data.maritalStatus === 'WIDOWED');

  // Handle Issue Token - Direct workflow
  const handleIssueToken = async (patient) => {
    setTokenPatient(patient);
    setTokenForm(initialTokenFormState);
    setShowTokenModal(true);
    // Close any other modals that could otherwise stack behind the Token modal
    setSelectedPatient(null);
    setShowSuccessModal(false);
    setNewlyCreatedPatient(null);

    // Credit held in the patient's name (e.g. from a referral) — offered against this fee.
    setPatientCredit(0);
    setApplyCredit(true);
    referralAPI.getCredit(patient.id)
      .then(c => setPatientCredit(Number(c?.balance || 0)))
      .catch(() => setPatientCredit(0));

    // A child arrives with an adult, and not always the same one: offer the adults on the file,
    // the primary first, and "Someone else" for one who is not on it yet.
    setTokenGuardians([]);
    setNewGuardian(EMPTY_NEW_GUARDIAN);
    setBroughtBy('');
    if (isMinorPatient(patient)) {
      patientAPI.getGuardians(patient.id)
        .then(list => {
          const rows = Array.isArray(list) ? list : [];
          setTokenGuardians(rows);
          // With nobody on the file yet, leave it unpicked: the parents are offered below, and
          // landing on "Someone else" would have the desk retype a father already on the record.
          setBroughtBy(rows.find(g => g.isPrimary)?.id || rows[0]?.id || '');
        })
        .catch(() => setBroughtBy(''));
    }

    // Fetch today's visit footprint for this patient so the modal can render the right
    // gating (block if active visit exists; require revisit reason after a completed one).
    setPatientTodayStatus(null);
    setPatientTodayStatusLoading(true);
    try {
      const status = await visitAPI.getPatientTodayStatus(patient.id);
      setPatientTodayStatus(status);
    } catch (err) {
      console.error('Failed to fetch patient today-status:', err);
      // Soft-fail: don't block token issuance just because the lookup hit a network blip;
      // the backend still enforces the rules on submit and will return a structured error.
      setPatientTodayStatus({ hasActiveVisit: false, hasCompletedVisit: false });
    } finally {
      setPatientTodayStatusLoading(false);
    }
  };

  // Close success modal
  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    setNewlyCreatedPatient(null);
  };

  // Show toast notification
  const showToast = (message, type = 'success', tokenNumber = null, patientName = null) => {
    setToast({ show: true, message, type, tokenNumber, patientName });
    // Auto-hide after 4 seconds
    setTimeout(() => {
      setToast({ show: false, message: '', type: 'success', tokenNumber: null, patientName: null });
    }, 4000);
  };

  // Issue token directly
  const issueTokenForPatient = async (e) => {
    e.preventDefault();
    
    if (!tokenForm.doctorId || !tokenForm.medicalServiceId) {
      showToast('Please select both doctor and medical service', 'error');
      return;
    }

    if (tokenForm.emergency && !tokenForm.emergencyReason.trim()) {
      showToast('Emergency reason is required for emergency tokens', 'error');
      return;
    }

    if (tokenPatientIsMinor) {
      const problem = !broughtBy
        ? 'Please record who brought the child in'
        : (broughtBy === 'NEW' ? newGuardianProblem(newGuardian) : null);
      if (problem) {
        showToast(problem, 'error');
        return;
      }
    }

    // Block early if the modal already knows there's an active visit.
    if (patientTodayStatus?.hasActiveVisit) {
      showToast(
        `This patient already has an active token today (token ${patientTodayStatus.activeTokenNumber}). Complete or cancel it before issuing a new one.`,
        'error'
      );
      return;
    }

    // Require revisit reason when this is a same-day return visit.
    if (patientTodayStatus?.hasCompletedVisit && !tokenForm.revisitReason.trim()) {
      showToast('Please provide a reason for the revisit.', 'error');
      return;
    }

    setLoading(true);
    try {
      // Backend forces NONE when finalFee is zero — match here so the wire payload is
      // consistent with what's persisted. Credit (if applied) reduces the fee after discount;
      // the backend caps it at both the balance and the fee.
      const feeAfterDiscount = (getSelectedService()?.baseFee || 0) - (tokenForm.discountAmount || 0);
      const creditToApply = applyCredit && patientCredit > 0 ? Math.min(patientCredit, feeAfterDiscount) : 0;
      const finalFee = feeAfterDiscount - creditToApply;
      const effectivePaymentMode = finalFee > 0 ? tokenForm.paymentMode : 'NONE';

      const tokenData = {
        ...tokenForm,
        paymentMode: effectivePaymentMode,
        appliedCredit: creditToApply > 0 ? creditToApply : null,
        patientId: tokenPatient.id,
        // Who brought a child in: an adult already on the file (by id), or one added to it with
        // this token — either a parent taken off the child's own record, or someone else typed in.
        accompaniedByGuardianId:
          tokenPatientIsMinor && broughtBy && broughtBy !== 'NEW' && !isParentOption(broughtBy)
            ? broughtBy
            : null,
        newGuardian: !tokenPatientIsMinor
          ? null
          : broughtBy === 'NEW'
            ? newGuardianPayload(newGuardian)
            : tokenParentPick
              ? parentGuardianPayload(tokenParentPick)
              : null,
      };

      // A fee that settles outside the system cannot be taken without its screenshot. Checked on
      // the effective mode: a zero fee is NONE and needs nothing.
      if (paymentNeedsProof(effectivePaymentMode) && tokenProofFiles.length === 0) {
        setTokenProofError('Attach the payment screenshot for this method.');
        setLoading(false);
        return;
      }

      // A cash fee goes into the cashier's drawer, which is opened first if it is not already.
      const response = await withDrawer(
        () => visitAPI.create(tokenData, tokenProofFiles),
        { cash: effectivePaymentMode === 'CASH' },
      );

      // Get selected doctor and service details for receipt
      const selectedDoctor = doctors.find(d => d.id === tokenForm.doctorId);
      const selectedService = medicalServices.find(s => s.id === tokenForm.medicalServiceId);

      // Prepare print data. tokenDisplay carries the prefixed form (e.g. "AB-1") for the
      // receipt + success toast; prefix comes from the saved visit, falling back to the doctor.
      const receiptData = {
        tokenNumber: response?.tokenNumber || 'N/A',
        tokenDisplay:
          response?.tokenNumber != null
            ? formatVisitToken({
                tokenNumber: response.tokenNumber,
                tokenPrefix: response.tokenPrefix || selectedDoctor?.tokenPrefix,
              })
            : 'N/A',
        patient: tokenPatient,
        doctor: selectedDoctor,
        service: selectedService,
        originalFee: selectedService?.baseFee || 0,
        discountAmount: tokenForm.discountAmount || 0,
        creditApplied: response?.finalFee != null ? creditToApply : 0,
        finalFee: response?.finalFee != null ? Number(response.finalFee) : finalFee,
        paymentMode: effectivePaymentMode,
        isEmergency: tokenForm.emergency,
        emergencyReason: tokenForm.emergencyReason,
        visitDate: new Date(),
        visitId: response?.id,
        // Who brought a child in, as the visit recorded it.
        accompaniedBy: accompaniedByOf(response),
        // Get waiting count from response if available
        waitingCount: response?.waitingCount || 0
      };
      
      // Close token modal
      setShowTokenModal(false);
      setTokenPatient(null);
      
      // Show print modal with receipt data
      setPrintData(receiptData);
      setShowPrintModal(true);
      
    } catch (error) {
      if (isDrawerCancelled(error)) return;
      console.error('Issue token error:', error);
      // Translate the structured backend errors into user-facing messages
      if (error?.code === 'DUPLICATE_ACTIVE_VISIT') {
        showToast(
          `Patient already has an active token today (token ${error.body?.activeTokenNumber}).`,
          'error'
        );
        // Refresh the status so the modal re-renders with the block banner
        setPatientTodayStatus({
          hasActiveVisit: true,
          activeTokenNumber: error.body?.activeTokenNumber,
          hasCompletedVisit: false,
        });
      } else if (error?.code === 'REVISIT_REASON_REQUIRED') {
        showToast('A revisit reason is required for this patient today.', 'error');
        setPatientTodayStatus(prev => ({ ...(prev || {}), hasCompletedVisit: true }));
      } else if (error?.code === 'PATIENT_NOT_ACTIVE') {
        // Backend block — UI should normally prevent this, but a stale page or a
        // direct API hit can still reach here. Surface the server's specific reason.
        showToast(error?.body?.message || 'Cannot issue token: patient is not active.', 'error');
      } else {
        showToast(error?.message || 'Error issuing token. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Print token receipt. The print window + stylesheet live in TokenPrintModal so the reprint
  // path in Token Queue produces a byte-identical slip.
  const handlePrintToken = () => {
    if (!printTokenReceipt(printRef.current)) {
      showToast('Could not open the print window. Please allow pop-ups for this site.', 'error');
      return;
    }
    // Auto-close the receipt modal after printing
    closePrintModal();
  };

  // Close print modal
  const closePrintModal = () => {
    setShowPrintModal(false);
    setPrintData(null);
    showToast('Token issued successfully!', 'success', printData?.tokenDisplay || printData?.tokenNumber,
      printData?.patient?.fullName || `${printData?.patient?.firstName} ${printData?.patient?.lastName}`);
  };

  // Form validation. The rules themselves are in ../utils/patientFormRules, so the message under a
  // field (formErrors, above), the Register button, the footer's "Still needed" line and the save all
  // use the same ones. Father's name has been asked of every child outright since 2026-09-20; it used
  // to be copied from the guardian's name, which left a child brought by an uncle with no father's
  // name at all.
  //
  // Nothing is erased when the date of birth changes. It used to clear the CNIC and marital status
  // whenever the date said child, but an age is typed one digit at a time and 45 passes through 4,
  // so correcting an adult's age wiped the CNIC and the marital status (and with it the husband's
  // name). The values stay; a field the date hides is simply not sent (identityPayload, and the
  // adult-only checks in submitPatient).
  //
  // Several values that change together (an age and the date it stands for; a status and its date)
  // go in one update. `touched: false` is for a change the receptionist did not make to that field
  // themselves, such as switching between a date and an age: that is not a visit, so the field's
  // message waits until they have been there.
  const handleFieldsChange = (changes, { touched: visited = true } = {}) => {
    setFormData(prev => ({ ...prev, ...changes }));
    setTouched(prev => {
      const next = { ...prev };
      Object.keys(changes).forEach((name) => { next[name] = visited; });
      return next;
    });
  };

  const handleFieldChange = (name, value) => handleFieldsChange({ [name]: value });

  // Fields where we auto-Title-Case the value when the user leaves the field.
  // We only apply this to identifying / locational text fields where Pakistani name
  // and address conventions expect capitalization ("adil waheed" → "Adil Waheed",
  // "shahkot" → "Shahkot"). Skip CNIC/phone/tags/free-text fields where the user's
  // exact casing matters.
  const TITLE_CASE_FIELDS = new Set([
    'firstName', 'lastName', 'fatherName', 'motherName', 'husbandName',
    'area', 'town', 'city',
  ]);

  // Leaving a field marks it visited, so its message (worked out from the current form) shows.
  const handleFieldBlur = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));

    // Auto-capitalize on blur for the configured fields. Done in the central blur
    // handler so every input that wires through onFieldBlur benefits automatically.
    if (TITLE_CASE_FIELDS.has(name)) {
      setFormData(prev => {
        const raw = prev[name] || '';
        const formatted = toTitleCase(raw);
        return formatted === raw ? prev : { ...prev, [name]: formatted };
      });
    }
  };

  // The save's own check: the same list the Register button and the footer read, so a form the
  // button lets through is one the save accepts. It includes the status on an edit (see
  // statusNeedsRecording). On a failed save every field shows its message.
  const validateForm = () => {
    const blockers = patientFormBlockers(formData, ruleOptions);
    setTouched(PATIENT_FORM_FIELDS.reduce((acc, field) => ({ ...acc, [field]: true }), {}));
    return blockers.length === 0;
  };

  // "Take me there" in the form's footer, and a disabled Register losing focus: the field it goes to
  // shows its own message, as if the receptionist had visited it and left it.
  const handleRevealField = (name) => {
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const resetForm = () => {
    setFormData(initialFormState);
    setDuplicateCnic(null);
    setTouched({});
    setOriginalPatientStatus(null);
    setEditingPatient(null);
    setCnicFile(null);
    setCnicBackFile(null);
    setGuardianCnicFile(null);
    setGuardianCnicBackFile(null);
    setProfileImageFile(null);
    setRemoveProfileImage(false);
  };

  const handleCloseForm = () => {
    setShowAddForm(false);
    setEditingPatientId(null);
    resetForm();
  };

  const handleShowForm = () => {
    resetForm();
    setEditingPatientId(null);
    setShowAddForm(true);
  };

  // Pre-populate the form with an existing patient's data and switch to edit mode.
  // "This is them" on the look-alike panel: leave the form and open the record already on file,
  // so the visit is issued against it instead of a second registration.
  const openExistingPatient = async (candidate) => {
    try {
      const patient = await patientAPI.getById(candidate.id);
      handleCloseForm();
      setSelectedPatientTab('info');
      setSelectedPatient(patient);
    } catch (error) {
      notify('error', 'Could not open that patient: ' + (error?.message || 'unknown error'));
    }
  };

  const handleEditPatient = (patient) => {
    const status = patient.status || 'ACTIVE';
    // A status other than Active with no date or reason on file (the server refuses to save one) is
    // opened as if it were being recorded now: the date on file or today, the reason on file if
    // any, so the Reason box shows and asks for what is missing.
    const recordStatusNow = statusNeedsRecording({ status }, { originalStatus: status, onFile: patient });
    setFormData({
      title: patient.title || '',
      firstName: patient.firstName || '',
      lastName: patient.lastName || '',
      fatherName: patient.fatherName || '',
      motherName: patient.motherName || '',
      husbandName: patient.husbandName || '',
      phoneNumber: patient.phoneNumber || '',
      cnic: patient.cnic || '',
      dateOfBirth: patient.dateOfBirth || '',
      // A file with no gender opens with none, so the footer asks for it. Filling in Male here
      // meant the next save wrote Male onto the record without anyone choosing it.
      gender: patient.gender || '',
      maritalStatus: patient.maritalStatus || '',
      area: patient.area || '',
      town: patient.town || '',
      city: patient.city || '',
      fatherCnic: patient.fatherCnic || '',
      motherCnic: patient.motherCnic || '',
      parentCnicOwner: patient.fatherCnic ? 'FATHER' : (patient.motherCnic ? 'MOTHER' : ''),
      // A file written before 2026-09-20 has no mother's name and may have no parent CNIC. Both
      // are asked for, neither blocks the save: the alternative is a receptionist unable to
      // correct a phone number until they can reach the family. Cleared by initialFormState, so
      // a new registration is always held to the full rule. The parent CNIC is let off only while
      // the child has a guardian's CNIC or a B-Form instead, as the server requires
      // (patientFormRules, parentCnicExcused).
      _motherNameOptional: !patient.motherName,
      _parentCnicOptional: !patient.fatherCnic && !patient.motherCnic,
      bFormNumber: patient.bFormNumber || '',
      // An estimated date opens the form on the age it stands for, whole years, or months for a
      // baby; the stored date only changes if the age is retyped.
      dobEstimated: !!patient.dobEstimated,
      ageYears: patient.dobEstimated && patient.dateOfBirth ? String(calculateAgeYears(patient.dateOfBirth)) : '',
      ageMonths: patient.dobEstimated && patient.dateOfBirth && calculateAgeYears(patient.dateOfBirth) === 0
        ? String(Math.max(0, Math.floor((clinicNow() - toClinicDate(patient.dateOfBirth)) / (30.44 * 86400000))))
        : '',
      tagNames: Array.isArray(patient.tags) ? patient.tags.map(t => t.name) : [],
      status,
      // Reason+date are gathered fresh on every toggle, so they start empty, except for a
      // status the file holds without them (recordStatusNow, above). A status left alone goes
      // back to the server with the date and reason on file (statusPayload).
      statusChangedAt: recordStatusNow ? (patient.statusChangedAt || clinicToday()) : '',
      statusReason: recordStatusNow ? (patient.statusReason || '') : '',
    });
    setOriginalPatientStatus(status);
    setEditingPatient(patient);
    setDuplicateCnic(null);
    setTouched({});
    setEditingPatientId(patient.id);
    setShowAddForm(true);
    // If we were viewing the patient details modal, close it so the edit form takes the screen
    setSelectedPatient(null);
  };

  // Fetch the patients matching the search. Without one, the server is not asked at all (it would
  // return every patient) and the list is emptied: see hasListCriteria. Cancels any in-flight
  // request so stale results don't overwrite newer ones.
  const fetchPatients = async (term, page, size, sortField = sortBy, direction = sortDir) => {
    if (searchAbortRef.current) searchAbortRef.current.abort();
    if (!hasListCriteria(term)) {
      searchAbortRef.current = null;
      setSearchResults([]);
      setSearchTotal(0);
      setTotalPages(0);
      setSearchHint('');
      setSearching(false);
      return;
    }
    const controller = new AbortController();
    searchAbortRef.current = controller;

    setSearching(true);
    try {
      const response = await patientAPI.search((term || '').trim(), {
        page,
        size,
        sortBy: sortField,
        sortDir: direction,
        signal: controller.signal,
      });
      const patients = response?.content || response?.data?.content || response || [];
      setSearchResults(patients);
      setSearchTotal(response?.totalElements ?? patients.length);
      setTotalPages(response?.totalPages ?? 1);
      // An MR number, CNIC or phone number has to be given in full enough to identify somebody, so
      // half of one deliberately matches nothing. The server says when that is why there are no
      // rows, rather than leaving the reader to conclude the patient is not on file.
      setSearchHint(response?.searchHint || '');
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Fetch error:', error);
      notify('error', 'Error loading patients: ' + (error.message || 'Please try again'));
    } finally {
      // Only the newest request ends the loading state: one cancelled by a newer search must not
      // end the newer one's loading rows.
      if (searchAbortRef.current === controller) {
        searchAbortRef.current = null;
        setSearching(false);
      }
    }
  };

  // Soft-delete (archive) a patient — permission-gated (Patients → Delete). Records are kept; the
  // patient is just hidden from lists. Removes the row locally on success.
  const handleDeletePatient = async (patient) => {
    const name = patient.fullName || `${patient.firstName || ''} ${patient.lastName || ''}`.trim();
    const ok = await confirm({
      title: 'Delete patient?',
      subtitle: 'Archived, not erased',
      message: `Delete ${name}${patient.mrn ? ` (${patient.mrn})` : ''}? They'll be hidden from all lists, but their records (visits, prescriptions, invoices, documents) are kept.`,
      confirmText: 'Delete patient',
      tone: 'danger',
    });
    if (!ok) return false;
    try {
      await patientAPI.delete(patient.id);
      notify('success', `${name || 'Patient'} deleted`);
      setSearchResults(prev => prev.filter(p => p.id !== patient.id));
      setSearchTotal(t => Math.max(0, (t ?? 1) - 1));
      return true;
    } catch (e) {
      notify('error', e.message || 'Failed to delete patient');
      return false;
    }
  };

  // Reset to page 0 when the search term changes; debounce typing by 300ms.
  // Skips the initial render — the page/size effect handles the first fetch.
  const isFirstSearchRender = useRef(true);
  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }
    // The text typed is no longer what any request in flight was for: cancel it. A search that
    // will list patients shows the loading rows straight away, so the typing pause neither
    // flashes "No patients found" nor shows the previous search's rows.
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
      searchAbortRef.current = null;
    }
    setSearching(hasListCriteria(searchTerm));
    const timer = setTimeout(() => {
      if (currentPage !== 0) setCurrentPage(0);
      else fetchPatients(searchTerm, 0, pageSize);
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Refetch on page, page-size or sort change (no debounce — user clicked). Also handles the
  // initial mount fetch, which asks for nothing unless the page opened with a search.
  useEffect(() => {
    fetchPatients(searchTerm, currentPage, pageSize, sortBy, sortDir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, sortBy, sortDir]);

  // Jump back to the top of the list when the page changes (Next/Prev/First/Last) — otherwise the
  // new page opens scrolled to the bottom where the pagination control was.
  useEffect(() => { scrollContentToTop(); }, [currentPage]);

  // Click handler for column headers — first click on a new column starts ASC,
  // subsequent clicks on the same column flip the direction.
  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    if (currentPage !== 0) setCurrentPage(0);
  };

  // Sort dropdown — pick a new field but keep the current direction. Defaults the
  // registration-date sort to newest-first since that's the most useful starting point.
  const changeSortField = (field) => {
    if (field === sortBy) return;
    setSortBy(field);
    setSortDir(field === 'createdAt' ? 'desc' : 'asc');
    if (currentPage !== 0) setCurrentPage(0);
  };

  // shownLookalikes: the matches the form was showing when Register was pressed, so the new file
  // remembers what it was warned about (the Potential Duplicates report judges them again later).
  const submitPatient = async (e, shownLookalikes = []) => {
    e.preventDefault();

    if (!validateForm()) {
      notify('warning', 'Please fix the validation errors shown in the form');
      return;
    }

    setLoading(true);

    try {
      // Both parents' names are typed now, so nothing is derived from the guardian any more.
      // It goes the other way: when a PARENT brought the child, the guardian columns are filled
      // from that parent rather than asked for twice. That happens in PatientService, so it holds
      // for any caller and there is only one copy of the rule to keep right.
      const trimmedOrNull = (v) => (String(v || '').trim() === '' ? null : String(v).trim());

      const cleanedData = {
        ...formData,
        title: formData.title.trim() === '' ? null : formData.title,
        fatherName: trimmedOrNull(formData.fatherName),
        motherName: trimmedOrNull(formData.motherName),
        // The adult's CNIC, the child's B-Form and the chosen parent's CNIC. A field the date of
        // birth hides sends what the file already holds, never what was typed into it while it was
        // on screen: a half-typed parent CNIC left behind by a child's date corrected to an adult's
        // was refused by the server, about a field no longer shown. Blank goes as null: the backend
        // checks the format of anything present.
        ...identityPayload(formData, ruleOptions),
        // UI-only: which CNIC to ask for, and whether this file predates the rule. Never persisted.
        parentCnicOwner: undefined,
        _motherNameOptional: undefined,
        _parentCnicOptional: undefined,
        // Husband name is only persisted when the conditional is satisfied (clears stale data
        // if the user changed gender/marital/DOB after typing it)
        husbandName: isHusbandNameApplicable(formData) && formData.husbandName.trim()
          ? formData.husbandName.trim()
          : null,
        // Only persist marital status for adults — clears it if DOB makes the patient a minor
        maritalStatus: (formData.dateOfBirth && isAdult(formData.dateOfBirth) && formData.maritalStatus) ? formData.maritalStatus : null,
        // A date derived from an age is stored as an estimate; the age fields themselves are only
        // the way it was typed and stay on the form.
        dobEstimated: !!formData.dobEstimated,
        ageYears: undefined,
        ageMonths: undefined,
        // What the form warned about and the receptionist went past; nothing on an edit.
        acknowledgedLookalikes:
          editingPatientId != null
            ? null
            : (shownLookalikes || []).map((c) => ({ id: c.id, strength: c.strength, rules: c.rules || [] })),
        area: formData.area.trim() === '' ? null : formData.area,
        town: formData.town.trim() === '' ? null : formData.town,
        // Backend resolves tagNames to Tag entities (case-insensitive find-or-create)
        tagNames: Array.isArray(formData.tagNames) ? formData.tagNames : [],
        // Lifecycle status. A status that toggled (in either direction, including back to
        // ACTIVE) goes with the captured reason and date. A status other than ACTIVE that was
        // left alone goes with the date and reason on file: the server refuses such a status
        // without them, which made every edit of a Deceased, Suspended or Blacklisted patient fail.
        ...statusPayload(formData, ruleOptions),
      };

      const isEditing = editingPatientId != null;
      const response = isEditing
        ? await patientAPI.update(editingPatientId, cleanedData)
        : await patientAPI.create(cleanedData);

      // Post-save file uploads — best-effort, each toasts a warning on failure but doesn't
      // roll back the patient save. These hit independent endpoints (and the backend now does
      // column-targeted writes, so they don't clobber each other), so we run them CONCURRENTLY
      // instead of awaiting up to 5 serial round-trips — wall-clock drops to the slowest single
      // upload. Each entry is [label, taskFn]; Promise.allSettled lets every upload finish and
      // we surface a per-file warning for any that reject.
      const patientIdForUpload = isEditing ? editingPatientId : response?.id;
      if (patientIdForUpload) {
        const uploadTasks = [];
        if (cnicFile)
          uploadTasks.push(['CNIC front upload', () => patientAPI.uploadCnicDocument(patientIdForUpload, cnicFile, 'front')]);
        if (cnicBackFile)
          uploadTasks.push(['CNIC back upload', () => patientAPI.uploadCnicDocument(patientIdForUpload, cnicBackFile, 'back')]);
        if (guardianCnicFile)
          uploadTasks.push(['guardian CNIC front upload', () => patientAPI.uploadCnicDocument(patientIdForUpload, guardianCnicFile, 'front', 'guardian')]);
        if (guardianCnicBackFile)
          uploadTasks.push(['guardian CNIC back upload', () => patientAPI.uploadCnicDocument(patientIdForUpload, guardianCnicBackFile, 'back', 'guardian')]);
        if (profileImageFile) {
          uploadTasks.push(['profile picture upload', () => patientAPI.uploadProfileImage(patientIdForUpload, profileImageFile)]);
        } else if (removeProfileImage && isEditing) {
          // User cleared the existing photo without staging a replacement → delete it.
          uploadTasks.push(['removing the profile picture', () => patientAPI.deleteProfileImage(patientIdForUpload)]);
        }

        const results = await Promise.allSettled(uploadTasks.map(([, fn]) => fn()));
        results.forEach((r, i) => {
          if (r.status === 'rejected') {
            notify('warning', `Patient saved, but ${uploadTasks[i][0]} failed: ${r.reason?.message || 'unknown error'}`);
          }
        });
      }

      // Refresh the table either way. For create, jumping to page 0 surfaces the new patient
      // (which is sorted to the top by createdAt DESC); the page-effect triggers fetchPatients.
      // For edit, the patient stays on its current page so we refetch in place.
      if (isEditing) {
        handleCloseForm();
        notify('success', 'Patient updated successfully');
        fetchPatients(searchTerm, currentPage, pageSize);
      } else if (response && response.id) {
        handleCloseForm();
        setNewlyCreatedPatient(response);
        setShowSuccessModal(true);
        if (currentPage !== 0) setCurrentPage(0);
        else fetchPatients(searchTerm, 0, pageSize);
      } else {
        handleCloseForm();
        notify('success', 'Patient registered successfully!');
        if (currentPage !== 0) setCurrentPage(0);
        else fetchPatients(searchTerm, 0, pageSize);
      }
    } catch (error) {
      console.error('Submit error:', error);
      const action = editingPatientId != null ? 'update' : 'register';

      // Duplicate-CNIC conflict — pin the error to the CNIC field with a useful message
      // pointing the user to the existing record instead of just a generic toast. The server's
      // message is shown as it is: only it knows when the file holding the CNIC was deleted or
      // merged, and what to do about that.
      if (error?.body?.code === 'DUPLICATE_CNIC') {
        const conflictMrn = error.body.conflictMrn;
        const conflictName = error.body.conflictName;
        const inlineMessage = error.body.message
          || (conflictMrn
            ? `This CNIC is already registered to ${conflictName || 'another patient'} (${conflictMrn}).`
            : 'This CNIC is already registered to another patient.');
        // Kept against the number it was about: it shows under CNIC and holds the button for as
        // long as that number is typed, since the server would refuse it again.
        setDuplicateCnic({ cnic: String(formData.cnic || '').trim(), message: inlineMessage });
        setTouched(prev => ({ ...prev, cnic: true }));
        notify('error', `Cannot ${action} patient - duplicate CNIC.`);
      } else if (error?.body?.message) {
        notify('error', `Failed to ${action} patient: ${error.body.message}`);
      } else if (error?.message) {
        notify('error', `Failed to ${action} patient: ${error.message}`);
      } else {
        notify('error', `Error ${action === 'update' ? 'updating' : 'registering'} patient. Please check all required fields.`);
      }
    } finally {
      setLoading(false);
    }
  };

  // CNIC formatting, with the caret kept in place: ../utils/cnic. buildPhone, parsePhone imported
  // from ../utils/phone

  useEffect(() => {
    if (searchQuery) {
      // The debounced effect on searchTerm will pick this up automatically.
      setSearchTerm(searchQuery);
    }
  }, [searchQuery]);

  const getSelectedService = () => {
    return medicalServices.find(s => s.id === tokenForm.medicalServiceId);
  };

  const calculateFinalFee = () => {
    const service = getSelectedService();
    if (!service) return 0;
    const discount = parseFloat(tokenForm.discountAmount) || 0;
    return Math.max(0, service.baseFee - discount);
  };
  // Patient credit consumed by this token when the Apply toggle is on (capped at fee + balance).
  const creditAppliedPreview = () =>
    applyCredit && patientCredit > 0 ? Math.min(patientCredit, calculateFinalFee()) : 0;
  // What the patient actually pays now: fee after discount and applied credit.
  const payableNow = () => Math.max(0, calculateFinalFee() - creditAppliedPreview());

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      {/* Toast Notification */}
      {toast.show && (
        <TokenSuccessToast 
          tokenNumber={toast.tokenNumber}
          patientName={toast.patientName}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      {/* Print Token Modal */}
      <TokenPrintModal
        show={showPrintModal}
        printData={printData}
        printRef={printRef}
        onPrint={handlePrintToken}
        onClose={closePrintModal}
      />
      
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
                <Users className="h-7 w-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Patient Management
                </h1>
                <p className="text-gray-600 mt-1">Find a patient to open their record or issue a token</p>
              </div>
            </div>
            {can('CREATE') && (
              <button
                onClick={handleShowForm}
                className="flex items-center space-x-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-5 py-2.5 font-medium transition-colors duration-200"
              >
                <Plus className="h-5 w-5" />
                <span>Add New Patient</span>
              </button>
            )}
          </div>
        </div>

        {/* Search Section — debounced as-you-type, Enter bypasses the wait */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  // Bypass the 300ms debounce — fire immediately
                  if (currentPage !== 0) setCurrentPage(0);
                  else fetchPatients(searchTerm, 0, pageSize);
                }
              }}
              placeholder="Search by name, guardian name, CNIC, phone number, or MRN..."
              className="w-full pl-12 pr-24 py-3.5 border-2 border-gray-200 rounded-xl focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all"
              autoFocus
            />
            {/* Right-side: spinner when searching, clear (X) when there's text */}
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {searching && (
                <RefreshCw className="h-4 w-4 text-gray-400 animate-spin" />
              )}
              {searchTerm && !searching && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                  title="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Patients Table — empty until a search asks for patients (hasListCriteria), then those
            rows with pagination controls below */}
        <div className="bg-white rounded-2xl shadow-lg mb-8">
          {/* Title and count (left), sort control (right). */}
          <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-baseline gap-3">
              <h2 className="text-lg font-bold text-gray-900">Patients</h2>
              {listing && (
                <span className="text-sm text-gray-500">
                  {searching ? (
                    'Loading…'
                  ) : searchTotal === 0 ? (
                    'No patients'
                  ) : (
                    <><span className="font-semibold text-gray-900">{searchTotal}</span> found</>
                  )}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Sort by
              </span>
              <Select
                value={sortBy}
                onChange={changeSortField}
                options={SORT_OPTIONS}
                className="min-w-[10rem]"
                buttonClassName="px-2 py-1.5 rounded-lg"
              />
              <button
                type="button"
                onClick={() => { setSortDir(d => (d === 'asc' ? 'desc' : 'asc')); if (currentPage !== 0) setCurrentPage(0); }}
                title={sortDir === 'asc' ? 'Sort descending' : 'Sort ascending'}
                className="p-1.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
              >
                {sortDir === 'asc' ? <ArrowUpNarrowWide className="h-4 w-4" /> : <ArrowDownWideNarrow className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <TablePager paging={patientPaging} noun="patient" busy={searching} rounded={false} />
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  {(() => {
                    const SortIcon = ({ field }) => (
                      sortBy === field
                        ? (sortDir === 'asc' ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />)
                        : <ChevronDown className="h-3.5 w-3.5 opacity-30" />
                    );
                    const sortableTh = (label, field, extraCls = '') => (
                      <th
                        className={`text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider select-none ${extraCls}`}
                        aria-sort={sortBy === field ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                      >
                        <button
                          type="button"
                          onClick={() => toggleSort(field)}
                          className="flex items-center gap-1 w-full uppercase tracking-wider rounded hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                        >
                          <span>{label}</span>
                          <SortIcon field={field} />
                        </button>
                      </th>
                    );
                    return (
                      <>
                        {/* Proportional column widths so the page width is shared evenly instead of
                            one column over-expanding (or being crushed). Percentages apply on xl
                            where all columns show; the browser scales them up when a column is
                            hidden at smaller breakpoints. */}
                        {sortableTh('Patient', 'firstName', 'w-[32%]')}
                        {sortableTh('DOB', 'dateOfBirth', 'hidden lg:table-cell w-[16%] whitespace-nowrap')}
                        {sortableTh('Phone', 'phoneNumber', 'hidden md:table-cell w-[16%] whitespace-nowrap')}
                        <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden xl:table-cell w-[22%]">Address</th>
                        <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-[14%]">Actions</th>
                      </>
                    );
                  })()}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {!listing ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Search className="h-6 w-6 text-emerald-600" />
                      </div>
                      <h3 className="text-base font-semibold text-gray-700 mb-1">Search for a patient</h3>
                      <p className="text-sm text-gray-500 max-w-lg mx-auto">
                        Type a name, MRN, CNIC, phone number or guardian name above. Patients are
                        listed only when they match, to keep their details private.
                      </p>
                    </td>
                  </tr>
                ) : searching ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      <td className="px-6 py-4"><div className="h-4 w-40 bg-gray-200 rounded animate-pulse" /></td>
                      <td className="px-6 py-4 hidden lg:table-cell"><div className="h-4 w-24 bg-gray-200 rounded animate-pulse" /></td>
                      <td className="px-6 py-4 hidden md:table-cell"><div className="h-4 w-28 bg-gray-200 rounded animate-pulse" /></td>
                      <td className="px-6 py-4 hidden xl:table-cell"><div className="h-4 w-32 bg-gray-200 rounded animate-pulse" /></td>
                      <td className="px-6 py-4"></td>
                    </tr>
                  ))
                ) : searchResults.length > 0 ? (
                  searchResults.map((patient) => {
                    // Tint the whole row emerald for minors so the receptionist spots pediatric
                    // patients at a glance — not just the age pill. Adults render in the
                    // default white background. Tint is intentionally noticeable but kept
                    // light enough that text and chips remain readable.
                    const isMinor = patient.adult === false || patient.minor === true;
                    // Non-Active patients (Deceased / Suspended / Blacklisted) get a faint
                    // gray tint + muted text via opacity so they read as inactive in the
                    // table without disappearing entirely. Status takes precedence over
                    // the minor highlight so a deceased minor still reads as inactive.
                    const status = patient.status || 'ACTIVE';
                    const isInactive = status !== 'ACTIVE';
                    const rowBase = isInactive
                      ? 'bg-gray-50/80 hover:bg-gray-100 border-l-4 border-gray-300 opacity-70'
                      : isMinor
                        ? 'bg-emerald-50 hover:bg-emerald-100/80 border-l-4 border-emerald-400'
                        : 'hover:bg-emerald-50/40 border-l-4 border-transparent';
                    return (
                    <tr
                      key={patient.id}
                      tabIndex={0}
                      className={`${rowBase} transition-colors cursor-pointer ${ROW_FOCUS_RING}`}
                      onClick={() => setSelectedPatient(patient)}
                      onKeyDown={onActivateKey(() => setSelectedPatient(patient))}
                    >
                      <td className="px-6 py-4 align-top">
                        <div className="flex items-center gap-3">
                          {/* Avatar — profile photo (with hover-to-enlarge preview) or
                              colour-hashed initials. Helps recognise a patient at a glance. */}
                          <PatientRowAvatar patient={patient} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-gray-900">
                                {patient.fullName || `${patient.firstName} ${patient.lastName}`}
                              </span>
                          {/* Lifecycle status — pill rendered only for non-Active patients
                              so Active doesn't add visual noise to the typical row. Title
                              attribute surfaces the reason on hover for quick context. */}
                          {isInactive && (() => {
                            const conf = patientStatusConfig(status);
                            return (
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${conf.badge}`}
                                title={patient.statusReason || conf.label}
                              >
                                <span className={`w-1.5 h-1.5 rounded-full ${conf.dot}`} />
                                {conf.label}
                              </span>
                            );
                          })()}
                          {/* Age inline with the name. Adults render as muted plain text. Minors
                              render as a flashing emerald pill so the receptionist spots
                              pediatric patients at a glance. */}
                          {patient.adult ? (
                            <span className="text-xs font-medium text-amber-600">
                              · {patient.dateOfBirth ? formatPatientAge(patient) : `${patient.age || '—'} years`}
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-medium animate-pulse"
                              title="Minor"
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                              {patient.dateOfBirth ? formatPatientAge(patient) : `${patient.age || '—'} years`}
                            </span>
                          )}
                        </div>
                        {/* MRN sits directly under the name (its own column was removed),
                            bold mono in blue so it reads as a distinct identifier. Gender follows it. */}
                        <div className="text-xs font-mono font-bold text-blue-600 mt-0.5">
                          {patient.mrn}
                          {patient.gender && (
                            <span className="font-sans font-medium text-gray-500 ml-2">· {patient.gender.charAt(0) + patient.gender.slice(1).toLowerCase()}</span>
                          )}
                        </div>
                        {/* One-line "relation" sub-row under the name, picked in this order:
                              1. Minor with a guardian → Guardian name (+ relationship)
                              2. Adult with a husband name on file → Husband (overrides father, per
                                 Pakistani convention for married/widowed women)
                              3. Anyone else with a father name → Father
                            Renders nothing if none apply. */}
                        {(() => {
                          if (!patient.adult && patient.guardianName) {
                            // A child is known by their father. When the guardian is someone
                            // else, say the father first, then who is responsible.
                            const fatherFirst = patient.fatherName && patient.guardianRelationship !== 'FATHER';
                            return (
                              <div className="text-xs text-gray-500 mt-0.5">
                                {fatherFirst && (
                                  <span className="mr-1"><span className="font-medium">{patient.gender === 'FEMALE' ? 'd/o' : 's/o'}</span> {patient.fatherName} ·</span>
                                )}
                                <span className="font-medium">Guardian:</span> {patient.guardianName}
                                {patient.guardianRelationship && (
                                  <span className="text-gray-400"> ({guardianRelLabel(patient.guardianRelationship, patient.guardianRelationshipOther)})</span>
                                )}
                              </div>
                            );
                          }
                          if (patient.husbandName) {
                            return (
                              <div className="text-xs text-gray-500 mt-0.5">
                                <span className="font-medium">Husband:</span> {patient.husbandName}
                              </div>
                            );
                          }
                          if (patient.fatherName) {
                            return (
                              <div className="text-xs text-gray-500 mt-0.5">
                                <span className="font-medium">Father:</span> {patient.fatherName}
                              </div>
                            );
                          }
                          return null;
                        })()}
                        {FEATURES.patientTags && patient.tags && patient.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5">
                            {patient.tags.slice(0, 2).map((tag) => (
                              <span
                                key={tag.id || tag.name}
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border font-sans ${tagBadgeClasses(tag)}`}
                              >
                                {tag.name}
                              </span>
                            ))}
                            {patient.tags.length > 2 && (
                              <span className="text-[10px] text-gray-400 font-sans">+{patient.tags.length - 2}</span>
                            )}
                          </div>
                        )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 align-top text-sm text-gray-600 hidden lg:table-cell whitespace-nowrap">
                        {patient.dateOfBirth ? formatClinicDate(patient.dateOfBirth, { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td className="px-6 py-4 align-top text-sm text-gray-600 hidden md:table-cell">{patient.phoneNumber}</td>
                      <td className="px-6 py-4 align-top text-sm text-gray-600 hidden xl:table-cell">
                        {(() => {
                          // Two-line address: street part (area + town) on top, city on its own
                          // line below. Falls back to a single line if only one piece is present.
                          const street = [patient.area, patient.town].filter(Boolean).join(', ');
                          const city = patient.city;
                          if (!street && !city) return '—';
                          return (
                            <>
                              {street && <div>{street}</div>}
                              {city && <div className={street ? 'text-gray-500' : ''}>{city}</div>}
                            </>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 align-top">
                        <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                          {/* Token stays as a primary CTA — it's the everyday receptionist
                              action, fastest path. Secondary actions (View / Edit) live in
                              the kebab to keep the row uncluttered. Non-Active patients
                              can't be tokened — render a disabled pill explaining why so
                              the receptionist isn't left wondering where the button went. */}
                          {can('ISSUE_TOKEN') && (
                            isInactive ? (
                              <span
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-400 text-xs font-semibold rounded-lg cursor-not-allowed border border-gray-200"
                                title={`Cannot issue token - patient is ${patientStatusConfig(status).label.toLowerCase()}${patient.statusReason ? ` (${patient.statusReason})` : ''}`}
                              >
                                <Plus className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">Token</span>
                              </span>
                            ) : (
                              <button
                                onClick={() => handleIssueToken(patient)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
                                title={`Issue token for ${patient.fullName || patient.firstName}`}
                              >
                                <Plus className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">Token</span>
                              </button>
                            )
                          )}
                          {(() => {
                            // Build the kebab list dynamically — each entry depends on a
                            // sub-permission. Hide the whole menu when nothing remains visible.
                            // Ordered: read-style actions first (view full record, then deep-
                            // links to specific tabs), then add-style (upload). Editing lives in
                            // the Details modal itself.
                            const items = [];
                            items.push({ key: 'view', label: 'View details', icon: Eye, iconClass: 'text-blue-500',
                              onClick: () => { setSelectedPatientTab('info'); setSelectedPatient(patient); } });
                            if (can('VIEW_VISITS_TAB')) items.push({ key: 'visits', label: 'View visit history', icon: Calendar, iconClass: 'text-indigo-500',
                              onClick: () => { setSelectedPatientTab('visits'); setSelectedPatient(patient); } });
                            if (can('VIEW_INVOICES_TAB')) items.push({ key: 'invoices', label: 'View invoices', icon: Wallet, iconClass: 'text-amber-500',
                              onClick: () => { setSelectedPatientTab('invoices'); setSelectedPatient(patient); } });
                            if (can('UPLOAD_DOCUMENT')) items.push({ key: 'upload', label: 'Upload document', icon: Upload, iconClass: 'text-emerald-500',
                              onClick: () => { setSelectedPatientTab('documents'); setSelectedPatient(patient); } });
                            if (can('DELETE')) items.push({ key: 'delete', label: 'Delete patient', icon: Trash2, iconClass: 'text-red-500', danger: true,
                              onClick: () => handleDeletePatient(patient) });
                            return <RowActionsMenu items={items} />;
                          })()}
                        </div>
                      </td>
                    </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Search className="h-6 w-6 text-gray-400" />
                      </div>
                      <h3 className="text-base font-semibold text-gray-700 mb-1">
                        {searchHint ? 'Keep typing' : 'No patients found'}
                      </h3>
                      <p className="text-sm text-gray-500 max-w-lg mx-auto">
                        {searchHint || (
                          <>
                            No matches for "<span className="font-medium">{searchTerm}</span>". Try a different name, MRN, CNIC, phone, guardian, or address.
                          </>
                        )}
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <TablePager paging={patientPaging} noun="patient" busy={searching} position="bottom" />
        </div>

        {/* Quick Token Issuance Modal */}
        {showTokenModal && tokenPatient && (
          <div ref={tokenDialogRef} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full mx-4 max-h-[95vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Issue Token</h2>
                  <p className="text-gray-600 mt-1">
                    For: {tokenPatient.firstName} {tokenPatient.lastName} ({tokenPatient.mrn})
                  </p>
                </div>
                <button
                  aria-label="Close"
                  onClick={() => setShowTokenModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                >
                  <X className="h-6 w-6 text-gray-500" />
                </button>
              </div>
              
              <form onSubmit={issueTokenForPatient} onKeyDown={keepEnterFromSubmitting} className="p-6 space-y-6">
                {/* Loading shimmer while we fetch the patient's today-visit footprint */}
                {patientTodayStatusLoading && (
                  <div className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-500">
                    Checking patient's today status…
                  </div>
                )}

                {/* Block: patient already has an active token today — don't allow another */}
                {!patientTodayStatusLoading && patientTodayStatus?.hasActiveVisit && (
                  <div className="px-4 py-3 bg-red-50 border-l-4 border-red-500 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-red-800">Patient already in queue</p>
                        <p className="text-sm text-red-700 mt-0.5">
                          This patient currently has an active token (
                          <span className="font-mono font-bold">{patientTodayStatus.activeTokenNumber}</span>)
                          for today. Complete or cancel it before issuing another.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Revisit Reason — required when patient has a COMPLETED visit today (and no active one) */}
                {!patientTodayStatusLoading
                  && !patientTodayStatus?.hasActiveVisit
                  && patientTodayStatus?.hasCompletedVisit && (
                  <div className="px-4 py-3 bg-amber-50 border-l-4 border-amber-500 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-semibold text-amber-800">Revisit detected</p>
                        <p className="text-sm text-amber-700 mt-0.5 mb-3">
                          This patient already completed a visit earlier today. Please record why they came back.
                        </p>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Reason for revisit *
                        </label>
                        <textarea
                          value={tokenForm.revisitReason}
                          onChange={(e) => setTokenForm({ ...tokenForm, revisitReason: e.target.value })}
                          rows="3"
                          maxLength={500}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
                          placeholder="e.g. forgot to mention chest pain, reaction to medicine, follow-up clarification…"
                          required
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Who brought a child in: the adults on the file, the primary first, or someone new */}
                {tokenPatientIsMinor && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Brought by *
                    </label>
                    <Select
                      value={broughtBy}
                      onChange={(v) => setBroughtBy(v)}
                      placeholder="Who is with the child?"
                      options={[
                        ...tokenGuardians.map(g => ({
                          value: g.id,
                          label: describeGuardian(g) + (g.isPrimary ? ' · primary' : ''),
                        })),
                        ...tokenParentOptions.map(o => ({
                          value: o.value,
                          label: describeGuardian(o),
                        })),
                        { value: 'NEW', label: 'Someone else' },
                      ]}
                      buttonClassName="px-3 py-3 rounded-lg"
                    />
                    {tokenParentPick && (
                      <p className="mt-2 text-sm text-gray-500">
                        Taken from {tokenPatient.firstName}'s record. This token puts them on the
                        file, so next time they are offered by name.
                      </p>
                    )}
                    {broughtBy === 'NEW' && (
                      <div className="mt-3 p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-3">
                        <p className="text-sm text-gray-600">
                          This adult goes onto {tokenPatient.firstName}'s file, so next time they are offered here.
                        </p>
                        <GuardianFields value={newGuardian} onChange={setNewGuardian} />
                      </div>
                    )}
                  </div>
                )}

                {/* Doctor Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Doctor *
                  </label>
                  <Select
                    value={tokenForm.doctorId}
                    onChange={(v) => setTokenForm({ ...tokenForm, doctorId: v })}
                    placeholder="Select a doctor"
                    options={doctors.map((doctor) => ({
                      value: doctor.id,
                      label: doctor.fullName + (doctor.specialization ? ' - ' + doctor.specialization : ''),
                    }))}
                    buttonClassName="px-3 py-3 rounded-lg"
                  />
                </div>

                {/* Medical Service — filtered to general + selected doctor's services */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Medical Service *
                  </label>
                  <Select
                    value={tokenForm.medicalServiceId}
                    disabled={!tokenForm.doctorId}
                    placeholder={!tokenForm.doctorId
                      ? 'Select a doctor first'
                      : medicalServices.length === 0 ? 'No services available for this doctor' : 'Select a service'}
                    onChange={(newServiceId) => {
                      // Re-derive the discount Rs amount against the new service's fee so
                      // the same % chosen earlier stays applied to the new total.
                      const newService = (medicalServices || []).find(sv => sv.id === newServiceId);
                      const pct = tokenForm.discountPercent || 0;
                      const amount = Math.round(((newService?.baseFee || 0) * pct) / 100);
                      setTokenForm({ ...tokenForm, medicalServiceId: newServiceId, discountAmount: amount });
                    }}
                    options={medicalServices.map((service) => ({
                      value: service.id,
                      label: service.name + ' - \u20a8' + service.baseFee + (service.doctor ? ' (doctor-specific)' : ''),
                    }))}
                    buttonClassName="px-3 py-3 rounded-lg"
                  />
                </div>

                {/* Emergency Checkbox */}
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="emergency"
                    checked={tokenForm.emergency}
                    onChange={(e) => setTokenForm({...tokenForm, emergency: e.target.checked})}
                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  <label htmlFor="emergency" className="text-sm font-medium text-gray-700">
                    Mark as Emergency (Priority Queue)
                  </label>
                </div>

                {/* Emergency Reason */}
                {tokenForm.emergency && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Emergency Reason *
                    </label>
                    <textarea
                      value={tokenForm.emergencyReason}
                      onChange={(e) => setTokenForm({...tokenForm, emergencyReason: e.target.value})}
                      rows="3"
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="Describe the emergency situation..."
                      required
                    />
                  </div>
                )}

                {/* Discount — selected as a percentage; the Rs amount is derived from the
                    chosen service's baseFee so it auto-adjusts if the user changes service. */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discount (%)
                  </label>
                  <Select
                    value={tokenForm.discountPercent || 0}
                    onChange={(v) => {
                      const pct = parseInt(v, 10) || 0;
                      const baseFee = getSelectedService()?.baseFee || 0;
                      const amount = Math.round((baseFee * pct) / 100);
                      setTokenForm({ ...tokenForm, discountPercent: pct, discountAmount: amount });
                    }}
                    options={[
                      { value: 0, label: 'No discount' },
                      ...[10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map(p => ({ value: p, label: p + '%' })),
                    ]}
                    buttonClassName="px-3 py-3 rounded-lg"
                  />
                  {getSelectedService() && (
                    <p className="text-sm text-gray-500 mt-1">
                      Service Fee: ₨{getSelectedService().baseFee}
                      {tokenForm.discountPercent > 0 && (
                        <span className="ml-2">· Discount: ₨{tokenForm.discountAmount} ({tokenForm.discountPercent}% off)</span>
                      )}
                    </p>
                  )}
                </div>

                {/* Patient credit (from a referral) — offered against this fee. */}
                {patientCredit > 0 && getSelectedService() && (
                  <label className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-violet-200 bg-violet-50 cursor-pointer">
                    <span className="flex items-center gap-2 text-sm font-medium text-violet-800">
                      <input
                        type="checkbox"
                        checked={applyCredit}
                        onChange={(e) => setApplyCredit(e.target.checked)}
                        className="w-4 h-4 text-violet-600 rounded"
                      />
                      Apply patient credit
                    </span>
                    <span className="text-sm font-semibold text-violet-800">
                      ₨{patientCredit.toLocaleString()} available
                      {creditAppliedPreview() > 0 && creditAppliedPreview() < patientCredit && (
                        <span className="font-normal text-violet-600"> · using ₨{creditAppliedPreview().toLocaleString()}</span>
                      )}
                    </span>
                  </label>
                )}

                {/* Mode of Payment — OPD consultation fee. Hidden when nothing is payable
                    (free service, 100% discount, or fully covered by credit); backend
                    records NONE. */}
                {getSelectedService() && payableNow() > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mode of Payment *
                    </label>
                    <Select
                      value={tokenForm.paymentMode}
                      onChange={(v) => setTokenForm({ ...tokenForm, paymentMode: v })}
                      // Split and Other are deliberately not offered. They still exist on the
                      // Visit.PaymentMode enum and in PAYMENT_MODE_LABEL so the handful of older
                      // records carrying them keep rendering and stay filterable on OPD Receipts.
                      options={[
                        ...PATIENT_PAYMENT_MODES,
                      ]}
                      buttonClassName="px-3 py-3 rounded-lg"
                    />
                    {paymentNeedsProof(tokenForm.paymentMode) && (
                      <div className="mt-3">
                        <PaymentProofUpload
                          paymentMode={tokenForm.paymentMode}
                          files={tokenProofFiles}
                          onFiles={setTokenProofFiles}
                          error={tokenProofError}
                          onClearError={() => setTokenProofError(null)}
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Fee Summary */}
                {getSelectedService() && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Fee Summary</h4>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-blue-700">Service Fee:</span>
                        <span className="text-blue-900">₨{getSelectedService().baseFee}</span>
                      </div>
                      {tokenForm.discountAmount > 0 && (
                        <div className="flex justify-between">
                          <span className="text-blue-700">
                            Discount{tokenForm.discountPercent > 0 ? ` (${tokenForm.discountPercent}%)` : ''}:
                          </span>
                          <span className="text-blue-900">-₨{tokenForm.discountAmount}</span>
                        </div>
                      )}
                      {creditAppliedPreview() > 0 && (
                        <div className="flex justify-between">
                          <span className="text-blue-700">Credit applied:</span>
                          <span className="text-blue-900">-₨{creditAppliedPreview().toLocaleString()}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-medium border-t border-blue-300 pt-1">
                        <span className="text-blue-800">Final Amount:</span>
                        <span className="text-blue-900">₨{payableNow()}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Form Actions */}
                <div className="flex space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowTokenModal(false)}
                    className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      loading
                      || !tokenForm.doctorId
                      || !tokenForm.medicalServiceId
                      || patientTodayStatusLoading
                      || patientTodayStatus?.hasActiveVisit
                      || (tokenPatientIsMinor && !broughtBy)
                    }
                    className="flex-1 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
                    title={
                      patientTodayStatus?.hasActiveVisit
                        ? `Patient already has active token ${patientTodayStatus.activeTokenNumber}`
                        : ''
                    }
                  >
                    {loading ? 'Issuing...' : 'Issue Token'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Patient Details Modal */}
        {selectedPatient && (
          <PatientDetailsModal
            patient={selectedPatient}
            initialTab={selectedPatientTab}
            onClose={() => { setSelectedPatient(null); setSelectedPatientTab('info'); }}
            onEdit={handleEditPatient}
            onIssueToken={handleIssueToken}
            onDelete={handleDeletePatient}
            onPatientChanged={(updated) => {
              // A guardian change rewrote the record's guardian columns: show them, and let the
              // row behind the modal catch up.
              setSelectedPatient(updated);
              fetchPatients(searchTerm, currentPage, pageSize);
            }}
            can={can}
          />
        )}

        {/* Add Patient Modal */}
        {showAddForm && (
          <AddPatientModal
            formData={formData}
            formErrors={formErrors}
            touched={touched}
            loading={loading}
            isEditing={editingPatientId != null}
            ruleOptions={ruleOptions}
            duplicateCnic={duplicateCnicShown}
            existingPatient={editingPatient}
            cnicFile={cnicFile}
            setCnicFile={setCnicFile}
            cnicBackFile={cnicBackFile}
            setCnicBackFile={setCnicBackFile}
            guardianCnicFile={guardianCnicFile}
            setGuardianCnicFile={setGuardianCnicFile}
            guardianCnicBackFile={guardianCnicBackFile}
            setGuardianCnicBackFile={setGuardianCnicBackFile}
            profileImageFile={profileImageFile}
            setProfileImageFile={setProfileImageFile}
            removeProfileImage={removeProfileImage}
            setRemoveProfileImage={setRemoveProfileImage}
            onFieldChange={handleFieldChange}
            onFieldsChange={handleFieldsChange}
            onFieldBlur={handleFieldBlur}
            onRevealField={handleRevealField}
            onSubmit={submitPatient}
            onClose={handleCloseForm}
            onReset={resetForm}
            onUseExisting={openExistingPatient}
            buildPhone={buildPhone}
            parsePhone={parsePhone}
            calculateAge={calculateAge}
            isAdult={isAdult}
          />
        )}

        {/* Success Modal after patient registration */}
        {showSuccessModal && newlyCreatedPatient && (
          <RegistrationSuccessModal
            patient={newlyCreatedPatient}
            onClose={closeSuccessModal}
            onIssueToken={() => handleIssueToken(newlyCreatedPatient)}
          />
        )}
      </div>
    </div>
  );
};

// Beautiful Patient Details Modal Component with Modern Aesthetics
/**
 * Report Notes tab — read-only view of the doctor's written record of reports a patient showed
 * without leaving a copy ("Seen past CBC, all normal except RBC").
 *
 * These are authored in the prescription writer: either directly in its Report Notes rail, or by
 * typing into the "Past Lab Reports" field, which the backend copies here when the prescription
 * completes. Until this tab existed there was no way to read them outside a consultation.
 *
 * Deliberately read-only. The writer stays the single place they're created and edited, so a note
 * is always attached to the visit context it was written in.
 */
const PatientReportNotesTab = ({ notes, loading }) => {
  if (loading || notes === null) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (notes.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <NotebookPen className="h-8 w-8 text-gray-300" />
        </div>
        <p className="text-gray-700 font-semibold">No report notes yet</p>
        <p className="text-gray-400 text-sm mt-1 max-w-md mx-auto">
          When a doctor records what they saw in a report the patient brought but did not leave a
          copy of, it appears here. Notes are added from the prescription screen.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {notes.map((note) => {
        const cat = reportNoteCategory(note.category);
        return (
          <div key={note.id} className="rounded-xl border border-gray-200 p-4">
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cat.pill}`}>
              {cat.label}
            </span>
            <p className="text-sm text-gray-800 mt-2 whitespace-pre-wrap break-words">{note.noteText}</p>
            <p className="text-[11px] text-gray-400 mt-2">
              {note.authorName ? `${note.authorName} · ` : ''}
              {note.noteDate ? formatClinicDateTime(note.noteDate) : ''}
            </p>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Full patient profile. Also opened read-only from the Prescription module (Today's Queue,
 * Today's Prescriptions, Prescription History) so a doctor can review someone before calling them
 * in — hence the `reviewOnly` flag.
 *
 * `reviewOnly` suppresses the footer's mutating actions. Edit already disappears on its own when
 * no `onEdit` is passed, but Delete is gated on the permission alone and Issue Token isn't gated at
 * all, so without this both would render as dead buttons for a caller that supplies no handlers.
 */
export const PatientDetailsModal = ({ patient, onClose, onEdit, onIssueToken, onDelete, onPatientChanged, initialTab = 'info', can, reviewOnly = false }) => {
  // Fail-open when `can` isn't supplied (e.g. legacy callers / tests). The App-level shell
  // always passes the real permission checker.
  const allowed = useCallback((action) => (typeof can === 'function' ? can(action) : true), [can]);
  const [activeTab, setActiveTab] = useState(initialTab);
  // Lab Tests holds two child tabs: the ordered tests and their reports, and the doctor's written
  // notes about reports a patient showed but left no copy of. They belong together — both answer
  // "what do we know about this patient's lab work" — and keeping Report Notes as a tenth
  // top-level tab pushed the row onto two lines while hiding how related the two are.
  const [labSubTab, setLabSubTab] = useState('reports'); // 'reports' | 'notes'
  // The modal body is its own scroller, so switching tabs kept the previous tab's scroll offset —
  // most obvious now that Lab Tests has children, where moving from a long list of tests to the
  // notes landed you part-way down. Covers the top-level tabs too, which had the same behaviour.
  const modalBodyRef = useRef(null);
  useScrollToTopOnChange(modalBodyRef, [activeTab, labSubTab]);
  const [zoomPic, setZoomPic] = useState(false); // profile-photo lightbox open?
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState('');
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploadDate, setUploadDate] = useState(''); // The date the document/test was performed
  // Files picked but not yet uploaded — each row carries an editable display name (prefilled
  // with the filename minus extension) so the user can rename before confirming the upload.
  const [pendingFiles, setPendingFiles] = useState([]);

  // Lab-report specific state — populated lazily when the user picks "Lab Report" in the
  // document-type picker. Stays untouched for other doc types.
  const [labTests, setLabTests] = useState(null);            // null = not loaded yet
  const [labTestsLoading, setLabTestsLoading] = useState(false);
  const [selectedTestIds, setSelectedTestIds] = useState(new Set()); // ordered test ids this upload covers
  const [isExternalReport, setIsExternalReport] = useState(false);   // external = no test linkage
  const [showCompletedTests, setShowCompletedTests] = useState(false);
  const notify = useToast();
  const confirm = useConfirm();
  // ImageLightbox closes itself on Escape without marking the key handled, so leave Escape to it
  // while the photo is open; otherwise one press would close the whole record too.
  const detailsDialogRef = useDialog(onClose);

  // The adults on a child's file beyond the primary (the guardian shown in the details). Loaded
  // when the record opens; adding, promoting and removing act on the file at once. Promoting also
  // rewrites the guardian columns on the patient, so the record is re-read for the caller.
  const isChild = isMinorPatient(patient);
  const [guardians, setGuardians] = useState([]);
  const [guardianForm, setGuardianForm] = useState(null); // null = closed
  const [guardianSaving, setGuardianSaving] = useState(false);
  const loadGuardians = useCallback(() => {
    if (!isChild || !patient?.id) return;
    patientAPI.getGuardians(patient.id)
      .then(list => setGuardians(Array.isArray(list) ? list : []))
      .catch(() => setGuardians([]));
  }, [isChild, patient?.id]);
  useEffect(() => { loadGuardians(); }, [loadGuardians]);
  const otherGuardians = guardians.filter(g => !g.isPrimary);
  // The files folded into this one: their MRNs are still on old slips, so the record says so.
  const [mergedFiles, setMergedFiles] = useState([]);
  useEffect(() => {
    if (!patient?.id) return;
    patientAPI.getMerges(patient.id)
      .then(list => setMergedFiles(Array.isArray(list) ? list : []))
      .catch(() => setMergedFiles([]));
  }, [patient?.id]);
  const patientChanged = async () => {
    loadGuardians();
    if (!onPatientChanged) return;
    try {
      onPatientChanged(await patientAPI.getById(patient.id));
    } catch {
      // The list refresh behind the modal catches up on its own.
    }
  };
  const saveGuardian = async () => {
    const problem = newGuardianProblem(guardianForm);
    if (problem) {
      notify('error', problem);
      return;
    }
    setGuardianSaving(true);
    try {
      await patientAPI.addGuardian(patient.id, newGuardianPayload(guardianForm));
      notify('success', `${guardianForm.name.trim()} added to ${patient.firstName}'s file`);
      setGuardianForm(null);
      await patientChanged();
    } catch (e) {
      notify('error', e?.message || 'Could not add the guardian');
    } finally {
      setGuardianSaving(false);
    }
  };
  const makeGuardianPrimary = async (g) => {
    const ok = await confirm({
      title: 'Make primary guardian?',
      message: `${g.name} will be the guardian shown on ${patient.firstName}'s file and offered first at the desk.`,
      confirmText: 'Make primary',
    });
    if (!ok) return;
    try {
      await patientAPI.makeGuardianPrimary(patient.id, g.id);
      notify('success', `${g.name} is now the primary guardian`);
      await patientChanged();
    } catch (e) {
      notify('error', e?.message || 'Could not change the primary guardian');
    }
  };
  const removeGuardian = async (g) => {
    const ok = await confirm({
      title: 'Remove guardian?',
      message: `Remove ${g.name} from ${patient.firstName}'s file? Past visits still say who brought the child.`,
      confirmText: 'Remove',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await patientAPI.removeGuardian(patient.id, g.id);
      notify('success', `${g.name} removed`);
      loadGuardians();
    } catch (e) {
      notify('error', e?.message || 'Could not remove the guardian');
    }
  };

  const DOC_TYPES = [
    // Alphabetical by label; OTHER pinned to the end as a universal UX convention.
    { value: 'CONSENT_FORM', label: 'Consent Form' },
    { value: 'CT_SCAN', label: 'CT Scan' },
    { value: 'DISCHARGE_SUMMARY', label: 'Discharge Summary' },
    { value: 'ECG', label: 'ECG' },
    { value: 'LAB_REPORT', label: 'Lab Report' },
    { value: 'MRI', label: 'MRI' },
    { value: 'PRESCRIPTION', label: 'Prescription' },
    { value: 'REFERRAL', label: 'Referral Letter' },
    { value: 'ULTRASOUND', label: 'Ultrasound' },
    { value: 'XRAY', label: 'X-Ray' },
    { value: 'OTHER', label: 'Other' },
  ];

  const loadDocuments = useCallback(async () => {
    setDocsLoading(true);
    try {
      const data = await patientDocumentAPI.getByPatient(patient.id);
      setDocuments(Array.isArray(data) ? data : []);
    } catch (err) { console.error('Failed to load documents:', err); }
    finally { setDocsLoading(false); }
  }, [patient.id]);

  useEffect(() => { if (activeTab === 'documents') loadDocuments(); }, [activeTab, loadDocuments]);

  // Safety net: if the current tab is disallowed by permissions (e.g. user lands on the
  // Visits tab via a deep-link but the role lost VIEW_VISITS_TAB), bounce back to Patient
  // Info. Patient Info has no permission check and is always reachable when the modal opens.
  const TAB_PERMS = {
    documents: 'VIEW_DOCUMENTS_TAB',
    visits: 'VIEW_VISITS_TAB',
    vitals: 'VIEW_VITALS_TAB',
    'clinical-notes': 'VIEW_CLINICAL_NOTES_TAB',
    prescriptions: 'VIEW_PRESCRIPTIONS_TAB',
    medicines: 'VIEW_MEDICINES_TAB',
    // Lab Tests is a container now: either child being permitted is enough to open it, and the
    // child tabs below are filtered individually. Gating the parent on VIEW_LAB_TESTS_TAB alone
    // would strand anyone granted only Report Notes.
    'lab-tests': null,
    'radiology-reports': 'VIEW_RADIOLOGY_TAB',
    'medical-history': 'VIEW_MEDICAL_HISTORY_TAB',
    'report-notes': 'VIEW_REPORT_NOTES_TAB',
    invoices: 'VIEW_INVOICES_TAB',
  };
  useEffect(() => {
    // 'report-notes' is no longer a top-level tab; it is a child of Lab Tests. A caller still
    // passing it as initialTab (or an old bookmark) lands where the content actually lives now.
    if (activeTab === 'report-notes') {
      setActiveTab('lab-tests');
      setLabSubTab('notes');
      return;
    }
    // A parked tab has no button, but a caller can still pass it as initialTab — bounce to Info
    // so its content can't be reached with no way back to it.
    if (activeTab === 'clinical-notes' && !FEATURES.patientClinicalNotesTab) {
      setActiveTab('info');
      return;
    }
    const required = TAB_PERMS[activeTab];
    if (required && !allowed(required)) setActiveTab('info');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, allowed]);

  // Load the patient's ordered lab tests on open — powers the Lab Tests tab count badge and
  // the "Lab Report" upload test-picker. Refetches whenever something resets labTests to null
  // (e.g. after a lab-report upload).
  useEffect(() => {
    if (labTests !== null) return; // already loaded
    let cancelled = false;
    (async () => {
      setLabTestsLoading(true);
      try {
        const data = await prescriptionAPI.getPatientLabTestsHistory(patient.id);
        if (!cancelled) setLabTests(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setLabTests([]);
      } finally {
        if (!cancelled) setLabTestsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [labTests, patient.id]);

  // Lab Tests count: ordered tests plus uploaded lab reports not linked to a test (linked ones
  // already surface on their test's row). Mirrors what the tab actually lists, and is read by both
  // the top-level tab badge and the Reports child tab.
  const labTabBadge = (() => {
    const orderedTests = Array.isArray(labTests) ? labTests : [];
    const linkedDocIds = new Set(orderedTests.flatMap(t => t.documentIds || []));
    return orderedTests.length
      + documents.filter(d => d.documentType === 'LAB_REPORT' && !linkedDocIds.has(d.id)).length;
  })();

  // Report notes — the doctor's written record of reports the patient showed but left no copy of.
  // Written in the prescription writer (its "Past Lab Reports" field is copied here when the
  // prescription completes); this modal is where they can be read outside a consultation.
  const [reportNotes, setReportNotes] = useState(null); // null = not loaded yet
  const [reportNotesLoading, setReportNotesLoading] = useState(false);
  useEffect(() => {
    if (reportNotes !== null) return;
    let cancelled = false;
    (async () => {
      setReportNotesLoading(true);
      try {
        const data = await patientReportNoteAPI.list(patient.id);
        if (!cancelled) setReportNotes(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) setReportNotes([]);
      } finally {
        if (!cancelled) setReportNotesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reportNotes, patient.id]);

  // Reset lab-test specific state when the doc type leaves LAB_REPORT.
  useEffect(() => {
    if (uploadType !== 'LAB_REPORT') {
      setSelectedTestIds(new Set());
      setIsExternalReport(false);
      setShowCompletedTests(false);
    }
  }, [uploadType]);

  // Picking files STAGES them (with an editable display name each) instead of uploading
  // immediately — the actual upload happens in handleConfirmUpload. Picking again adds to the
  // staged batch.
  const handleFilesPicked = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    const accepted = [];
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        notify('error', `"${file.name}" exceeds the 5MB limit`);
        continue;
      }
      accepted.push({ file, name: file.name.replace(/\.[^.]+$/, '') });
    }
    if (accepted.length > 0) setPendingFiles(prev => [...prev, ...accepted]);
  };

  // Multi-file upload of the staged batch. For non-LAB_REPORT, loops over each file. For
  // LAB_REPORT, attaches `linkedLabTestIds` (unless flagged external) — every file in this
  // batch is linked to the same set of tests, since that's the common case (one panel =
  // multiple scanned sheets of the same report).
  const handleConfirmUpload = async () => {
    if (pendingFiles.length === 0) return;
    if (!uploadType) {
      notify('warning', 'Please select a document type before uploading');
      return;
    }

    // Build the linked-test list for LAB_REPORT uploads. Empty for external reports or other
    // doc types — backend treats that as a standard upload.
    const isLabReport = uploadType === 'LAB_REPORT';
    const linkedIds = (isLabReport && !isExternalReport) ? Array.from(selectedTestIds) : [];

    setUploading(true);
    let okCount = 0;
    let failed = null;
    try {
      for (const pf of pendingFiles) {
        try {
          await patientDocumentAPI.upload(patient.id, pf.file, uploadType, uploadDesc, uploadDate, linkedIds, pf.name);
          okCount++;
        } catch (err) {
          failed = err;
          break;
        }
      }
      if (okCount > 0) {
        const linkSuffix = linkedIds.length > 0
          ? ` · ${linkedIds.length} test${linkedIds.length === 1 ? '' : 's'} marked completed`
          : '';
        notify('success', `${okCount} file${okCount === 1 ? '' : 's'} uploaded${linkSuffix}`);
      }
      if (failed) {
        notify('error', failed.message || 'Some files failed to upload');
        // Keep the files that didn't make it so the user can retry without re-picking.
        setPendingFiles(prev => prev.slice(okCount));
      } else {
        setPendingFiles([]);
      }
      // Reset only the per-upload fields, keep the type so the user can upload more of the same.
      setUploadDesc('');
      setUploadDate('');
      setSelectedTestIds(new Set());
      setIsExternalReport(false);
      loadDocuments();
      // Refresh lab tests so newly-completed ones show up correctly in the picker for the next
      // upload, AND so the Lab Tests tab reflects the change next time it's opened.
      if (isLabReport) {
        setLabTests(null);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    // Admins delete permanently; everyone else soft-deletes → the document goes to the admin
    // "Deleted Documents" review queue. The backend enforces this by role; here we just set the
    // right expectation in the confirm + toast.
    const isAdmin = JSON.parse(localStorage.getItem('clinic_auth_user') || '{}')?.role === 'ADMIN';
    const ok = await confirm({
      title: 'Delete document?',
      subtitle: isAdmin ? 'This permanently removes it' : 'Sent to admin for review',
      message: isAdmin
        ? 'This permanently deletes the document from the server. This cannot be undone.'
        : 'This removes the document from view and sends it to an admin to review (they can restore it or delete it permanently).',
      confirmText: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await patientDocumentAPI.delete(patient.id, docId);
      notify('success', isAdmin ? 'Document permanently deleted' : 'Document deleted - sent for admin review');
      setDocuments(prev => prev.filter(d => d.id !== docId));
      // Nudge the admin "Deleted Documents" badge to refresh (no-op for admin hard-deletes).
      window.dispatchEvent(new Event('clinic:deleted-docs-changed'));
    } catch (err) { notify('error', 'Failed to delete document'); }
  };

  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
  const authToken = localStorage.getItem('clinic_auth_token');
  const getDocUrl = (docId) => `${apiBase}/patients/${patient.id}/documents/${docId}/download?token=${authToken}`;

  return (
  <div ref={detailsDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
    {/* Full-size profile-photo lightbox (above this modal); opened by clicking the avatar. */}
    <ImageLightbox
      src={zoomPic ? authedMediaUrl(patient.profileImageUrl) : null}
      alt={patient.fullName || `${patient.firstName} ${patient.lastName}`}
      onClose={() => setZoomPic(false)}
    />
    <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col overflow-hidden transform transition-all duration-300 ease-out animate-in">
      {/* Sticky Header — stays visible while content scrolls */}
      <div className="flex-shrink-0 relative bg-gradient-to-br from-navy-800 via-navy-900 to-navy-950 text-white overflow-hidden">
        <div className="relative p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              {/* Patient Avatar — shows uploaded profile photo when present, falls
                  back to first-last initials otherwise. When a photo exists the avatar is
                  clickable to open a full-size lightbox for identification. */}
              <div className="w-20 h-20 bg-emerald-700/40 rounded-2xl flex items-center justify-center border-2 border-white border-opacity-30 overflow-hidden flex-shrink-0">
                {patient.profileImageUrl ? (
                  <button
                    type="button"
                    onClick={() => setZoomPic(true)}
                    aria-label="Enlarge photo"
                    title="Click to enlarge"
                    className="block w-full h-full focus:outline-none focus-visible:outline-emerald-500 focus-visible:-outline-offset-2"
                  >
                    {/* Outline, not ring: this box's overflow-hidden would clip a ring. */}
                    <img
                      src={authedMediaUrl(patient.profileImageUrl)}
                      alt={patient.fullName || `${patient.firstName} ${patient.lastName}`}
                      className="w-full h-full object-cover cursor-zoom-in"
                    />
                  </button>
                ) : (
                  <span className="text-3xl font-bold text-white">
                    {patient.firstName[0]}{patient.lastName[0]}
                  </span>
                )}
              </div>
              
              <div>
                <h1 className="text-4xl font-bold mb-2 text-white">
                  {patient.fullName || `${patient.firstName} ${patient.lastName}`}
                </h1>
                <div className="flex items-center flex-wrap gap-x-4 gap-y-1.5 text-white text-opacity-90">
                  <div className="flex items-center space-x-2">
                    <Shield className="h-5 w-5 opacity-70" />
                    <span className="text-lg font-semibold text-white">
                      {patient.mrn}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-5 w-5 opacity-70" />
                    <span
                      className="text-lg font-semibold text-white"
                      style={{ textShadow: '0 0 10px rgba(255,255,255,0.45), 0 0 2px rgba(255,255,255,0.5)' }}
                    >
                      {patient.dateOfBirth ? formatPatientAge(patient) : `${patient.age} years`}
                    </span>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold border shadow-sm
                    ${patient.adult
                      ? 'bg-sky-100 border-sky-200 text-sky-800'
                      : 'bg-amber-100 border-amber-200 text-amber-800'
                    }`}
                  >
                    {patient.adult ? 'Adult Patient' : 'Minor Patient'}
                  </span>
                  {(() => {
                    // Dynamic status pill — solid lighter fills so each status reads as a
                    // distinct chip against the emerald header gradient. Non-Active also
                    // gets a detail card lower in the modal with the change date + reason.
                    const status = patient.status || 'ACTIVE';
                    const tone = {
                      ACTIVE:      'bg-white border-white text-emerald-700',
                      DECEASED:    'bg-slate-200 border-slate-100 text-slate-800',
                      SUSPENDED:   'bg-orange-100 border-orange-200 text-orange-800',
                      BLACKLISTED: 'bg-rose-100 border-rose-200 text-rose-800',
                    }[status] || 'bg-white border-white text-emerald-700';
                    return (
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-semibold border shadow-sm ${tone}`}
                      >
                        {patientStatusConfig(status).label}
                      </span>
                    );
                  })()}
                </div>
                {patient.createdAt && (
                  <p className="text-white text-opacity-75 text-sm mt-1.5">
                    Registered on{' '}
                    <span
                      className="font-semibold text-white"
                      style={{ textShadow: '0 0 8px rgba(255,255,255,0.4)' }}
                    >
                      {formatClinicDate(patient.createdAt, { day: 'numeric', month: 'long', year: 'numeric' })}
                    </span>
                  </p>
                )}
                {FEATURES.patientTags && patient.tags && patient.tags.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 mt-2">
                    {patient.tags.map((tag) => (
                      <span
                        key={tag.id || tag.name}
                        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-xs font-medium shadow-sm ${tagBadgeClasses(tag)}`}
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {onEdit && allowed('EDIT') && (
                <button
                  onClick={() => onEdit(patient)}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 border border-white border-opacity-30 rounded-lg text-white text-sm font-semibold backdrop-blur-sm transition-all duration-200"
                  title="Edit patient"
                >
                  <Edit2 className="h-4 w-4" />
                  <span>Edit</span>
                </button>
              )}
              <button
                aria-label="Close"
                onClick={onClose}
                className="p-3 hover:bg-white hover:bg-opacity-20 rounded-full transition-all duration-200 group"
              >
                <X className="h-6 w-6 text-white group-hover:text-white group-hover:scale-110 transition-all duration-200" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Tabs — pill-style with strong active fill so the second tab is obviously
          a separate destination, not just decoration. The non-implemented tabs are
          placeholders today; tab content will be filled in as those modules come online.
          flex-wrap so the row stays readable when several tabs are present.

          Each tab declares its own permission key. Patient Info is always shown — it's the
          modal's landing screen and is gated by the module-level VIEW (without which the
          whole modal can't be opened anyway). Other tabs are filtered against the
          permission catalog so an admin can hide, e.g., Clinical Notes from receptionists. */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-3">
        {/* Even grid so the tabs split into balanced rows (5 per row on the modal width) with
            uniform pill widths, instead of an uneven wrap. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
          {(() => {
          // Radiology counts imaging documents; the lab count is computed once at component scope
          // (labTabBadge) because the Lab Tests child bar shows the same number.
          const radiologyTabBadge = documents.filter(d => RADIOLOGY_DOC_TYPES.has(d.documentType)).length;
          return [
            { key: 'info',           label: 'Patient Info',       icon: <Users           className="h-4 w-4" />, badge: null,                perm: null                       },
            { key: 'documents',      label: 'Documents',          icon: <FileText        className="h-4 w-4" />, badge: documents.length,    perm: 'VIEW_DOCUMENTS_TAB'       },
            { key: 'visits',         label: 'Visits',             icon: <Calendar        className="h-4 w-4" />, badge: null,                perm: 'VIEW_VISITS_TAB'          },
            { key: 'vitals',         label: 'Vitals',             icon: <Activity        className="h-4 w-4" />, badge: null,                perm: 'VIEW_VITALS_TAB'          },
            // Clinical Notes is parked behind FEATURES.patientClinicalNotesTab — see config/features.js.
            ...(FEATURES.patientClinicalNotesTab
              ? [{ key: 'clinical-notes', label: 'Clinical Notes', icon: <ClipboardList className="h-4 w-4" />, badge: null, perm: 'VIEW_CLINICAL_NOTES_TAB' }]
              : []),
            { key: 'prescriptions',  label: 'Prescriptions',      icon: <Stethoscope     className="h-4 w-4" />, badge: null,                perm: 'VIEW_PRESCRIPTIONS_TAB'   },
            { key: 'lab-tests',      label: 'Lab Tests',          icon: <FlaskConical    className="h-4 w-4" />, badge: labTabBadge,         perms: ['VIEW_LAB_TESTS_TAB', 'VIEW_REPORT_NOTES_TAB'] },
            { key: 'radiology-reports', label: 'Radiology Reports', icon: <Image         className="h-4 w-4" />, badge: radiologyTabBadge,   perm: 'VIEW_RADIOLOGY_TAB'       },
            { key: 'medical-history', label: 'Medical History',   icon: <Heart           className="h-4 w-4" />, badge: null,                perm: 'VIEW_MEDICAL_HISTORY_TAB' },
            { key: 'invoices',       label: 'Invoices',           icon: <Wallet          className="h-4 w-4" />, badge: null,                perm: 'VIEW_INVOICES_TAB'        },
          ].filter(tab => (tab.perms ? tab.perms.some(allowed) : (!tab.perm || allowed(tab.perm)))).map(tab => {
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                  isActive
                    ? 'bg-navy-800 text-white border-navy-800 shadow-sm'
                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 hover:text-gray-800'
                }`}
              >
                {tab.icon}
                <span className="whitespace-nowrap">{tab.label}</span>
                {tab.badge !== null && tab.badge > 0 && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                    isActive ? 'bg-white/25 text-white' : 'bg-emerald-100 text-emerald-700'
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          });
          })()}
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div ref={modalBodyRef} className="flex-1 overflow-y-auto p-8">
        {activeTab === 'info' ? (<>
        {/* Status banner — only for non-Active. Sits at the top of the Info tab so the
            admin sees the reason and effective date the moment they open the record. */}
        {patient.status && patient.status !== 'ACTIVE' && (() => {
          const conf = patientStatusConfig(patient.status);
          const fmtDate = formatClinicDate(patient.statusChangedAt, { day: 'numeric', month: 'long', year: 'numeric' });
          return (
            <div className={`mb-6 rounded-2xl border-2 p-5 ${conf.badge}`}>
              <div className="flex items-start gap-4">
                <span className={`mt-1 w-3 h-3 rounded-full ${conf.dot} flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-bold text-lg">{conf.label}</span>
                    <span className="text-sm opacity-80">since {fmtDate}</span>
                  </div>
                  {patient.statusReason && (
                    <p className="mt-1.5 text-sm leading-relaxed whitespace-pre-wrap break-words">
                      {patient.statusReason}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Personal Information Card */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100 shadow-lg">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-navy-700 to-navy-900 rounded-xl flex items-center justify-center">
                <Users className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800">
                Personal Information
              </h3>
            </div>
            
            <div className="space-y-4">
              <DetailRow
                icon={<Users className="h-5 w-5 text-blue-600" />}
                label="Full Name"
                value={patient.fullName || `${patient.firstName} ${patient.lastName}`}
                highlight
              />
              {/* Father vs Husband Name — Husband shown for adult married/widowed women;
                  otherwise Father. Placed alongside Full Name (matches Pakistani name
                  convention where father/husband name follows the given name). */}
              {patient.adult
                && patient.gender === 'FEMALE'
                && (patient.maritalStatus === 'MARRIED' || patient.maritalStatus === 'WIDOWED') ? (
                <DetailRow
                  icon={<Users className="h-5 w-5 text-blue-600" />}
                  label="Husband Name"
                  value={patient.husbandName || 'Not Provided'}
                  isOptional={!patient.husbandName}
                />
              ) : (
                <DetailRow
                  icon={<Users className="h-5 w-5 text-blue-600" />}
                  label="Father Name"
                  value={patient.fatherName || 'Not Provided'}
                  isOptional={!patient.fatherName}
                />
              )}
              {!patient.adult && (
                <DetailRow
                  icon={<Users className="h-5 w-5 text-blue-600" />}
                  label="Mother Name"
                  value={patient.motherName || 'Not Provided'}
                  isOptional={!patient.motherName}
                />
              )}
              <DetailRow
                icon={<Phone className="h-5 w-5 text-emerald-600" />}
                label="Phone Number"
                value={patient.phoneNumber}
              />
              {patient.dateOfBirth && patient.dobEstimated ? (
                // An estimate has no date worth printing: the family gave an age, not a day.
                <DetailRow
                  icon={<Calendar className="h-5 w-5 text-purple-600" />}
                  label="Age"
                  value={`${formatPatientAge(patient)} (estimated, date of birth not known)`}
                />
              ) : patient.dateOfBirth ? (
                <DetailRow
                  icon={<Calendar className="h-5 w-5 text-purple-600" />}
                  label="Date of Birth"
                  value={`${formatClinicDate(patient.dateOfBirth, { day: 'numeric', month: 'long', year: 'numeric' })} (${formatAge(patient.dateOfBirth)})`}
                />
              ) : (
                <DetailRow
                  icon={<Calendar className="h-5 w-5 text-purple-600" />}
                  label="Age"
                  value={`${patient.age} years`}
                />
              )}
              <DetailRow
                icon={<Users className="h-5 w-5 text-pink-600" />}
                label="Gender"
                value={patient.gender}
              />
              {/* Marital status — only meaningful for adults */}
              {patient.adult && (
                <DetailRow
                  icon={<Users className="h-5 w-5 text-pink-600" />}
                  label="Marital Status"
                  value={patient.maritalStatus ? maritalStatusLabel(patient.maritalStatus) : 'Not Provided'}
                  isOptional={!patient.maritalStatus}
                />
              )}
            </div>
          </div>
          
          {/* Identity & Location Card */}
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-100 shadow-lg">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-navy-700 to-navy-900 rounded-xl flex items-center justify-center">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800">
                Identity & Location
              </h3>
            </div>
            
            <div className="space-y-4">
              {/* Patient CNIC — not shown for minors (they don't have one; the Guardian CNIC applies) */}
              {(patient.adult || patient.cnic) && (
                <DetailRow
                  icon={<Shield className="h-5 w-5 text-emerald-600" />}
                  label="Patient CNIC"
                  value={patient.cnic ? (
                    <span className="inline-flex items-center gap-2 flex-wrap">
                      <span>{patient.cnic}</span>
                      <FilePreviewEye url={patient.cnicDocumentUrl} label="Front" />
                      <FilePreviewEye url={patient.cnicBackDocumentUrl} label="Back" />
                    </span>
                  ) : 'Not Provided'}
                  isOptional={!patient.cnic}
                />
              )}
              {/* A child's identity: the parent's CNIC, then whoever brought them in. */}
              {!patient.adult && (patient.fatherCnic || patient.motherCnic) && (
                <DetailRow
                  icon={<Shield className="h-5 w-5 text-teal-600" />}
                  label={patient.fatherCnic ? "Father's CNIC" : "Mother's CNIC"}
                  value={(
                    <span className="inline-flex items-center gap-2 flex-wrap">
                      <span>{patient.fatherCnic || patient.motherCnic}</span>
                      <FilePreviewEye url={patient.guardianCnicDocumentUrl} label="Front" />
                      <FilePreviewEye url={patient.guardianCnicBackDocumentUrl} label="Back" />
                    </span>
                  )}
                />
              )}
              {/* Guardian details — only relevant for minors */}
              {!patient.adult && patient.guardianCnic && (
                <DetailRow
                  icon={<Shield className="h-5 w-5 text-teal-600" />}
                  label="Guardian CNIC"
                  value={(
                    <span className="inline-flex items-center gap-2 flex-wrap">
                      <span>{patient.guardianCnic}</span>
                      <FilePreviewEye url={patient.guardianCnicDocumentUrl} label="Front" />
                      <FilePreviewEye url={patient.guardianCnicBackDocumentUrl} label="Back" />
                    </span>
                  )}
                />
              )}
              {!patient.adult && patient.guardianName && (
                <DetailRow
                  icon={<Shield className="h-5 w-5 text-teal-600" />}
                  label="Guardian Name"
                  value={patient.guardianName}
                />
              )}
              {!patient.adult && patient.guardianRelationship && (
                <DetailRow
                  icon={<Shield className="h-5 w-5 text-teal-600" />}
                  label="Relationship"
                  value={guardianRelLabel(patient.guardianRelationship, patient.guardianRelationshipOther)}
                />
              )}
              {/* Legacy: older minors may have a B-Form on file instead of guardian details */}
              {!patient.adult && patient.bFormNumber && (
                <DetailRow
                  icon={<Shield className="h-5 w-5 text-teal-600" />}
                  label="B-Form Number"
                  value={patient.bFormNumber}
                />
              )}
              {!patient.adult && !patient.guardianCnic && !patient.bFormNumber
                && !patient.fatherCnic && !patient.motherCnic && (
                <DetailRow
                  icon={<Shield className="h-5 w-5 text-teal-600" />}
                  label="Guardian Info"
                  value="Not Provided"
                  isOptional={true}
                />
              )}
              {mergedFiles.length > 0 && (
                <DetailRow
                  icon={<Shield className="h-5 w-5 text-gray-500" />}
                  label="Also known as"
                  value={mergedFiles
                    .map(m => `${m.mergedMrn} (merged ${formatClinicDate(m.createdAt, { day: 'numeric', month: 'short', year: 'numeric' })})`)
                    .join(', ')}
                />
              )}
              {/* The other adults on a child's file. The primary is the guardian shown above. */}
              {isChild && (
                <div className="pt-3 border-t border-emerald-100">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <p className="text-sm font-semibold text-gray-700">Other guardians on file</p>
                    {allowed('EDIT') && !reviewOnly && guardianForm === null && (
                      <button
                        type="button"
                        onClick={() => setGuardianForm(EMPTY_NEW_GUARDIAN)}
                        className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-800"
                      >
                        <Plus className="h-4 w-4" /> Add guardian
                      </button>
                    )}
                  </div>
                  {otherGuardians.length === 0 && guardianForm === null && (
                    <p className="text-sm text-gray-500">
                      None yet. When another adult brings {patient.firstName} in, the Issue Token dialog adds them here.
                    </p>
                  )}
                  {otherGuardians.length > 0 && (
                    <ul className="space-y-2">
                      {otherGuardians.map(g => (
                        <li key={g.id} className="flex items-center justify-between gap-3 bg-white border border-gray-200 rounded-lg px-3 py-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">{describeGuardian(g)}</p>
                            <p className="text-xs text-gray-500 truncate">
                              {g.cnic || 'No CNIC on file'}
                              {g.createdByName ? ` · added by ${g.createdByName}` : ''}
                            </p>
                          </div>
                          {allowed('EDIT') && !reviewOnly && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button
                                type="button"
                                onClick={() => makeGuardianPrimary(g)}
                                className="px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 rounded-lg"
                              >
                                Make primary
                              </button>
                              <button
                                type="button"
                                onClick={() => removeGuardian(g)}
                                aria-label={`Remove ${g.name}`}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {guardianForm !== null && (
                    <div className="mt-3 p-4 bg-white border border-gray-200 rounded-lg space-y-3">
                      <GuardianFields value={guardianForm} onChange={setGuardianForm} />
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setGuardianForm(null)}
                          className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={saveGuardian}
                          disabled={guardianSaving}
                          className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                        >
                          {guardianSaving ? 'Saving...' : 'Add to file'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <DetailRow
                icon={<MapPin className="h-5 w-5 text-orange-600" />}
                label="Address"
                value={patient.area || 'Not Specified'}
                isOptional={!patient.area}
              />
              {patient.town && (
                <DetailRow
                  icon={<MapPin className="h-5 w-5 text-amber-600" />}
                  label="Town"
                  value={patient.town}
                />
              )}
              <DetailRow
                icon={<MapPin className="h-5 w-5 text-red-600" />}
                label="City"
                value={patient.city || 'Not Specified'}
                isOptional={!patient.city}
              />
            </div>
          </div>
        </div>
        
        
        </>) : activeTab === 'documents' ? (
        /* Documents Tab */
        <div>
          {/* Upload Section */}
          <div className="bg-gray-50 rounded-2xl p-5 border-2 border-dashed border-gray-300 mb-6">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[180px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
                <Select
                  value={uploadType}
                  onChange={setUploadType}
                  placeholder="Select type"
                  options={DOC_TYPES}
                  buttonClassName="px-3 py-2 rounded-lg"
                />
              </div>
              <div className="w-44">
                <label className="block text-sm font-medium text-gray-700 mb-1">Document Date</label>
                <DatePicker
                  value={uploadDate}
                  onChange={(v) => setUploadDate(v || '')}
                  max={clinicToday()}
                  placeholder="Date of test/report"
                />
              </div>
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={uploadDesc}
                  onChange={(e) => setUploadDesc(e.target.value)}
                  placeholder="e.g., Blood test, post-surgery"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm"
                />
              </div>
              <label
                title={!uploadType ? 'Select a document type first' : ''}
                className={`relative flex items-center gap-2 px-5 py-2.5 rounded-lg font-medium text-sm transition-all has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-emerald-500 has-[:focus-visible]:ring-offset-2 ${
                uploading || !uploadType
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : pendingFiles.length > 0
                  ? 'bg-white text-emerald-700 border border-emerald-300 hover:bg-emerald-50 cursor-pointer'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-md cursor-pointer'
              }`}>
                <Upload className="h-4 w-4" />
                <span>{pendingFiles.length > 0 ? 'Add More Files' : 'Choose File(s)'}</span>
                <input
                  type="file"
                  accept="image/jpeg,image/png,application/pdf"
                  multiple
                  onChange={handleFilesPicked}
                  disabled={uploading || !uploadType}
                  className="sr-only"
                />
              </label>
            </div>
            <p className="text-xs text-gray-400 mt-2">Accepted: JPEG, PNG, PDF (max 5MB each). Multiple files supported.</p>

            {/* Staged files — rename before upload. The name entered here becomes the document's
                display name in the table (extension is kept automatically). */}
            {pendingFiles.length > 0 && (
              <div className="mt-4 bg-white rounded-xl border border-gray-200 p-4">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
                  Ready to upload · edit the name each document should be saved with
                </div>
                <div className="space-y-2">
                  {pendingFiles.map((pf, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-emerald-600 flex-none" />
                      <input
                        type="text"
                        value={pf.name}
                        onChange={(e) => setPendingFiles(prev => prev.map((p, j) => j === i ? { ...p, name: e.target.value } : p))}
                        placeholder={pf.file.name}
                        disabled={uploading}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                      <span className="text-xs text-gray-400 whitespace-nowrap w-28 truncate" title={pf.file.name}>
                        {(pf.file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                      <button
                        type="button"
                        onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))}
                        disabled={uploading}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50"
                        title="Remove from this upload"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => setPendingFiles([])}
                    disabled={uploading}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmUpload}
                    disabled={uploading || !uploadType}
                    title={!uploadType ? 'Select a document type first' : ''}
                    className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white shadow-md disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                  >
                    {uploading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    {uploading ? 'Uploading...' : `Upload ${pendingFiles.length} File${pendingFiles.length === 1 ? '' : 's'}`}
                  </button>
                </div>
              </div>
            )}

            {/* Lab-report enrichment: when the user picks "Lab Report", show the ordered-tests
                picker so they can attach the upload to specific tests (auto-marking each
                completed) — or tick "Not from PAMH" to upload an external report. */}
            {uploadType === 'LAB_REPORT' && (
              <LabReportTestsPicker
                tests={labTests}
                loading={labTestsLoading}
                selectedIds={selectedTestIds}
                setSelectedIds={setSelectedTestIds}
                isExternal={isExternalReport}
                setIsExternal={setIsExternalReport}
                showCompleted={showCompletedTests}
                setShowCompleted={setShowCompletedTests}
              />
            )}
          </div>

          {/* Document List — compact table. A card grid burned too much space when a
              patient had several lab reports / scans / prescriptions; switching to rows
              keeps a dozen+ documents scannable without scrolling. The first column is a
              small thumbnail (or PDF/image icon) so docs are still quickly recognisable. */}
          {docsLoading ? (
            <div className="text-center py-10 text-gray-400"><RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />Loading documents...</div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No documents uploaded yet</p>
              <p className="text-gray-400 text-sm mt-1">Upload prescriptions, lab reports, or other medical records</p>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">
                    <th className="px-3 py-2.5 w-14"></th>
                    <th className="px-3 py-2.5">Name</th>
                    <th className="px-3 py-2.5">Type</th>
                    <th className="px-3 py-2.5">Uploaded</th>
                    <th className="px-3 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {documents.map(doc => {
                    const isPdf = doc.contentType === 'application/pdf';
                    const docDateFmt = doc.documentDate
                      ? formatClinicDate(doc.documentDate, { day: 'numeric', month: 'short', year: 'numeric' })
                      : null;
                    // Tooltip on the name surfaces the secondary metadata (description, doc
                    // date, size) that used to be their own columns. Browser-native title
                    // attribute keeps things lightweight — no popover library needed.
                    const nameTooltip = [
                      doc.fileName,
                      doc.description ? `Description: ${doc.description}` : null,
                      docDateFmt ? `Document date: ${docDateFmt}` : null,
                      doc.fileSizeDisplay ? `Size: ${doc.fileSizeDisplay}` : null,
                    ].filter(Boolean).join('\n');
                    return (
                      <tr key={doc.id} className="hover:bg-gray-50/60">
                        <td className="px-3 py-2">
                          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                            {doc.hasThumbnail ? (
                              <img
                                src={authedMediaUrl(`${apiBase}/patients/${patient.id}/documents/${doc.id}/thumbnail`)}
                                alt={doc.fileName}
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex'; }}
                              />
                            ) : null}
                            <div className={`flex items-center justify-center text-gray-400 w-full h-full ${doc.hasThumbnail ? 'hidden' : ''}`}>
                              {isPdf ? <FileText className="h-5 w-5" /> : <Image className="h-5 w-5" />}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-2 max-w-xs">
                          <div className="font-medium text-gray-900 truncate" title={nameTooltip}>{doc.fileName}</div>
                          {doc.description && (
                            <div className="text-xs text-gray-500 truncate" title={nameTooltip}>{doc.description}</div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                            {DOC_TYPES.find(t => t.value === doc.documentType)?.label || doc.documentType}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500">
                          {formatClinicDateTime(doc.uploadedAt, { withZone: true })}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-3">
                            <a
                              href={getDocUrl(doc.id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-emerald-600 hover:text-emerald-700"
                              title="View"
                            >
                              <Eye className="h-4 w-4" />
                            </a>
                            <button
                              onClick={() => handleDelete(doc.id)}
                              className="text-red-500 hover:text-red-600"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        ) : activeTab === 'visits' ? (
        <PatientVisitsTab patientId={patient.id} />
        ) : activeTab === 'vitals' ? (
        <PatientVitalsTab patientId={patient.id} dateOfBirth={patient.dateOfBirth} />
        ) : activeTab === 'clinical-notes' ? (
        <PatientPrescriptionNotesTab patientId={patient.id} type="clinical" emptyLabel="clinical notes" />
        ) : activeTab === 'prescriptions' ? (
        <PatientPrescriptionsTab patientId={patient.id} />
        ) : activeTab === 'lab-tests' ? (
        (() => {
          // Child tabs, filtered by the same permissions the top-level row used to apply. When only
          // one is permitted the bar is dropped entirely rather than shown with a single option.
          const labChildren = [
            { key: 'reports', label: 'Reports',      badge: labTabBadge,                 perm: 'VIEW_LAB_TESTS_TAB'    },
            { key: 'notes',   label: 'Report Notes', badge: reportNotes?.length ?? null, perm: 'VIEW_REPORT_NOTES_TAB' },
          ].filter(c => allowed(c.perm));
          const current = labChildren.some(c => c.key === labSubTab)
            ? labSubTab
            : (labChildren[0]?.key ?? 'reports');
          return (
            <div>
              {labChildren.length > 1 && (
                <div className="flex gap-2 mb-4">
                  {labChildren.map(c => {
                    const on = current === c.key;
                    return (
                      <button
                        key={c.key}
                        onClick={() => setLabSubTab(c.key)}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                          on
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-800'
                        }`}
                      >
                        <span>{c.label}</span>
                        {c.badge ? (
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                            on ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'
                          }`}>{c.badge}</span>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              )}
              {current === 'notes'
                ? <PatientReportNotesTab notes={reportNotes} loading={reportNotesLoading} />
                : <PatientLabTestsTab patientId={patient.id} />}
            </div>
          );
        })()
        ) : activeTab === 'radiology-reports' ? (
        <PatientRadiologyReportsTab patientId={patient.id} />
        ) : activeTab === 'medical-history' ? (
        <PatientMedicalHistoryTab patientId={patient.id} />
        ) : activeTab === 'invoices' ? (
        <PatientInvoicesTab patientId={patient.id} />
        ) : (
        /* Placeholder for tabs not yet implemented. Looks up the active tab's metadata
           from the map above so each placeholder reads with the right title + icon. */
        <PatientTabPlaceholder tabKey={activeTab} />
        )}
      </div>

      {/* Persistent footer — flat buttons consistent with the app, always visible on every tab
          (was previously inside the scrollable Info tab, so it only showed there and scrolled
          out of view). */}
      <div className="flex-shrink-0 border-t border-gray-200 px-6 py-3 bg-gray-50 flex items-center justify-end gap-3">
        {!reviewOnly && can && can('DELETE') && (
          <button
            onClick={async () => { if (onDelete && (await onDelete(patient))) onClose(); }}
            className="mr-auto px-5 py-2.5 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors flex items-center gap-2"
            title="Delete (archive) this patient"
          >
            <Trash2 className="h-4 w-4" /> Delete
          </button>
        )}
        <button
          onClick={onClose}
          className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
        >
          Close
        </button>
        {!reviewOnly && (patient.status && patient.status !== 'ACTIVE' ? (
          <span
            className="px-5 py-2.5 text-sm font-medium bg-gray-100 text-gray-400 rounded-lg flex items-center gap-2 cursor-not-allowed border border-gray-200"
            title={`Cannot issue token - patient is ${patientStatusConfig(patient.status).label.toLowerCase()}`}
          >
            <Plus className="h-4 w-4" /> Issue Token
          </span>
        ) : (
          <button
            onClick={() => onIssueToken && onIssueToken(patient)}
            className="px-5 py-2.5 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2"
          >
            <Plus className="h-4 w-4" /> Issue Token
          </button>
        ))}
      </div>
    </div>
  </div>
  );
};

// Patient-detail tabs render their whole list at once. They used to reveal 5 rows at a time
// behind a "Show N more" button, which meant a patient's history was never actually on screen:
// reviewing 28 lab tests took five clicks, and nothing could be scanned or Ctrl-F'd in one pass.
// The modal body scrolls, which is the right control for a long list.

// Small colour-coded status pill used by Visits and Prescriptions tabs. Pulled out to module
// scope so we don't shadow the Pill icon from lucide-react when used inside the Medicines tab.
const StatusPill = ({ style, fallback }) => {
  const s = style || { label: fallback || '—', color: 'bg-gray-100 text-gray-500 border-gray-200' };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${s.color}`}>
      {s.label}
    </span>
  );
};

// =================== PATIENT VISITS TAB ===================
// Renders the full visit history for the patient — every token ever issued, with the
// doctor / service / fees and the prescription status against each visit. Lazy-loads on
// first render so the API call doesn't fire until the user opens the tab.

const VISIT_STATUS_STYLE = {
  PENDING_VITALS:    { label: 'Pending Vitals',     color: 'bg-purple-100 text-purple-700 border-purple-200' },
  READY_FOR_DOCTOR:  { label: 'Ready for Doctor',   color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  CALLED:            { label: 'Called',             color: 'bg-orange-100 text-orange-700 border-orange-200' },
  IN_CONSULTATION:   { label: 'In Consultation',    color: 'bg-blue-100 text-blue-700 border-blue-200' },
  COMPLETED:         { label: 'Completed',          color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  CANCELLED:         { label: 'Cancelled',          color: 'bg-gray-200 text-gray-700 border-gray-300' },
};

const RX_STATUS_STYLE = {
  INITIATED:  { label: 'Initiated',  color: 'bg-gray-100 text-gray-700 border-gray-200' },
  DRAFT:      { label: 'Draft',      color: 'bg-amber-100 text-amber-700 border-amber-200' },
  PRESCRIBED: { label: 'Prescribed', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  ISSUED:     { label: 'Issued',     color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  INVOICED:   { label: 'Invoiced',   color: 'bg-purple-100 text-purple-700 border-purple-200' },
  COMPLETED:  { label: 'Completed',  color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  CANCELLED:  { label: 'Cancelled',  color: 'bg-gray-200 text-gray-700 border-gray-300' },
};

const PatientVisitsTab = ({ patientId }) => {
  const [visits, setVisits] = useState(null); // null = unloaded, [] = loaded empty
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // How many rows are currently visible. The full list is fetched up-front but only the
  // first 5 are shown; "Show 5 more" reveals subsequent batches from the in-memory cache.

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await visitAPI.getPatientHistoryWithRx(patientId);
        if (!cancelled) setVisits(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load visits');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [patientId]);

  // Combine date + time into a single human-readable stamp; prefer createdAt for the actual
  // "token issued at" moment, fall back to visitDate+visitTime if missing.
  const formatIssuedAt = (v) => {
    if (v.createdAt) {
      return formatClinicDateTime(v.createdAt, { withZone: true });
    }
    if (v.visitDate) {
      const dateStr = formatClinicDate(v.visitDate, { day: '2-digit', month: 'short', year: 'numeric' });
      return v.visitTime ? `${dateStr}, ${v.visitTime.slice(0, 5)}` : dateStr;
    }
    return '—';
  };

  // Paged here, above the early returns, because it is a hook.
  const paging = usePagedRows(visits || [], { id: 'patient-visits', resetOn: [patientId] });

  if (loading) {
    return <div className="text-center text-gray-500 py-12">Loading visits…</div>;
  }
  if (error) {
    return <div className="text-center text-red-600 py-12">{error}</div>;
  }
  if (!visits || visits.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="text-center">
          <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 mb-1">No visits yet</h3>
          <p className="text-sm text-gray-500">Tokens issued to this patient will appear here.</p>
        </div>
      </div>
    );
  }

  const visible = paging.pageRows;

  return (
    <div className="space-y-4">
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
        <TablePager paging={paging} noun="visit" className="px-3" rounded={false} />
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-xs font-semibold text-gray-600 uppercase">
              <th className="px-3 py-2 text-left">Issued</th>
              <th className="px-3 py-2 text-center">Token</th>
              <th className="px-3 py-2 text-left">Doctor</th>
              <th className="px-3 py-2 text-left">Service</th>
              <th className="px-3 py-2 text-right">Fee</th>
              <th className="px-3 py-2 text-center">Visit Status</th>
              <th className="px-3 py-2 text-center">Prescription</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visible.map(v => (
              <tr key={v.visitId} className="hover:bg-gray-50/60 align-top">
                <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{formatIssuedAt(v)}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`inline-flex items-center justify-center min-w-[2rem] h-8 px-2 rounded-lg font-bold text-sm whitespace-nowrap ${
                    v.isEmergency ? 'bg-red-100 text-red-700 border-2 border-red-300' : 'bg-blue-50 text-blue-700'
                  }`}>{formatVisitToken(v)}</span>
                  {v.isRevisit && (
                    <div className="mt-1">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700">
                        Revisit
                      </span>
                    </div>
                  )}
                </td>
                <td className="px-3 py-2">
                  {v.doctorName ? (
                    <>
                      <div className="font-medium text-gray-900">{v.doctorName}</div>
                      {v.doctorSpecialization && (
                        <div className="text-xs text-gray-500">{v.doctorSpecialization}</div>
                      )}
                    </>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-gray-700">{v.serviceName || '—'}</td>
                <td className="px-3 py-2 text-right">
                  {v.finalFee != null ? (
                    <>
                      <div className="font-medium">Rs. {Number(v.finalFee).toLocaleString()}</div>
                      {v.discountAmount > 0 && v.originalFee && (
                        <div className="text-[10px] text-gray-400 line-through">Rs. {Number(v.originalFee).toLocaleString()}</div>
                      )}
                    </>
                  ) : <span className="text-gray-400">—</span>}
                </td>
                <td className="px-3 py-2 text-center">
                  <StatusPill style={VISIT_STATUS_STYLE[v.visitStatus]} fallback={v.visitStatus} />
                </td>
                <td className="px-3 py-2 text-center">
                  {v.prescriptionStatus ? (
                    <>
                      <StatusPill style={RX_STATUS_STYLE[v.prescriptionStatus]} fallback={v.prescriptionStatus} />
                      {v.prescriptionNumber && (
                        <div className="text-[10px] text-gray-400 font-mono mt-0.5">{v.prescriptionNumber}</div>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-gray-400 italic">No prescription</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePager paging={paging} noun="visit" className="px-3" rounded={false} position="bottom" />
      </div>
    </div>
  );
};

// =================== PATIENT VITALS TAB ===================
// Renders every visit where the nurse recorded vitals, newest first. The latest reading
// is pulled out into a summary card on top for at-a-glance reference; the full table below
// shows the trend across visits.

// Reference ranges used for adult traffic-lighting of BP and pulse. These are intentionally
// liberal — we're not trying to diagnose, just to make abnormal readings catch the eye.
const VITAL_FLAGS = {
  bpHigh: (sys, dia) => (sys != null && sys >= 140) || (dia != null && dia >= 90),
  bpLow:  (sys, dia) => (sys != null && sys < 90) || (dia != null && dia < 60),
  pulseHigh: (p) => p != null && p > 100,
  pulseLow:  (p) => p != null && p < 60,
  tempHigh:  (t) => t != null && t >= 100.4,
  spo2Low:   (s) => s != null && s < 95,
};

// Exported so the Prescription module can surface the same vitals views in its header.
// `initialView` ('readings' | 'trends') sets the starting sub-view; `hideTabs` suppresses the
// internal Readings/Trends toggle (used when the caller — e.g. the prescription Vitals popup —
// provides its own outer tabs and just wants one view).
export const PatientVitalsTab = ({ patientId, dateOfBirth, initialView = 'readings', hideTabs = false }) => {
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Sub-view inside the Vitals tab: tabular readings vs. trend charts.
  const [view, setView] = useState(initialView);
  // Keep the view in sync when a parent drives it via initialView (e.g. the prescription Vitals
  // popup, which renders one instance and flips initialView as its outer tab changes). Without
  // this, useState ignores the new initialView and the view gets "stuck" on the first one.
  useEffect(() => { setView(initialView); }, [initialView]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await visitAPI.getPatientVitalsHistory(patientId);
        if (!cancelled) setEntries(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load vitals');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [patientId]);

  const fmtDate = (d) => formatClinicDateTime(d, { withZone: true });

  const cell = (value, flagged) => {
    if (value === null || value === undefined || value === '') {
      return <span className="text-gray-300">—</span>;
    }
    return (
      <span className={flagged ? 'text-red-600 font-semibold' : 'text-gray-800'}>{value}</span>
    );
  };

  const paging = usePagedRows(entries || [], { id: 'patient-vitals', resetOn: [patientId] });

  if (loading) {
    return <div className="text-center text-gray-500 py-12">Loading vitals…</div>;
  }
  if (error) {
    return <div className="text-center text-red-600 py-12">{error}</div>;
  }
  if (!entries || entries.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="text-center">
          <Activity className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 mb-1">No vitals recorded yet</h3>
          <p className="text-sm text-gray-500">Vitals captured at the nurse station will appear here.</p>
        </div>
      </div>
    );
  }

  const latest = entries[0];
  const latestBpFlag = VITAL_FLAGS.bpHigh(latest.bpSystolic, latest.bpDiastolic)
    || VITAL_FLAGS.bpLow(latest.bpSystolic, latest.bpDiastolic);

  const bmi = formatBmi(latest);

  return (
    <div className="space-y-5">
      {/* Sub-tabs: Readings (snapshot + table) vs Trends (charts). Hidden when the caller
          supplies its own outer tabs (hideTabs). */}
      <div className={`flex items-center gap-2 border-b border-gray-200 ${hideTabs ? 'hidden' : ''}`}>
        {[
          { key: 'readings', label: 'Readings', icon: Activity },
          { key: 'trends',   label: 'Trends',   icon: TrendingUp },
        ].map(t => {
          const isActive = view === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setView(t.key)}
              className={`flex items-center gap-2 px-4 py-2 -mb-px font-medium text-sm border-b-2 ${
                isActive ? 'border-navy-800 text-navy-800' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon className="w-4 h-4" />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {view === 'trends' ? (
        <VitalsTrendsView entries={entries} dateOfBirth={dateOfBirth} />
      ) : (
      <>
      {/* Latest snapshot card */}
      <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Most Recent Reading</h3>
          <span className="text-xs text-gray-500">{fmtDate(latest.recordedAt)}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <VitalChip label="BP"          value={latest.bpSystolic && latest.bpDiastolic ? `${latest.bpSystolic}/${latest.bpDiastolic}` : null} unit="mmHg" alert={latestBpFlag} />
          <VitalChip label="Pulse"       value={latest.pulse} unit="bpm" alert={VITAL_FLAGS.pulseHigh(latest.pulse) || VITAL_FLAGS.pulseLow(latest.pulse)} />
          <VitalChip label="Temp"        value={latest.temperature} unit="°F" alert={VITAL_FLAGS.tempHigh(latest.temperature)} />
          <VitalChip label="SpO₂"        value={latest.spo2} unit="%" alert={VITAL_FLAGS.spo2Low(latest.spo2)} />
          <VitalChip label="Weight"      value={latest.weight} unit="kg" />
          {latest.height != null
            ? <VitalChip label="Height" value={latest.height} unit="cm" />
            : <VitalChip label="Length" value={latest.lengthCm} unit="cm" />}
          {latest.headCircumferenceCm != null && (
            <VitalChip label="Head Circ" value={latest.headCircumferenceCm} unit="cm" />
          )}
          {latest.muacCm != null && (
            <VitalChip label="MUAC" value={latest.muacCm} unit="cm" />
          )}
          <VitalChip label="Blood Sugar" value={latest.bloodSugar} unit="mg/dL" />
          <VitalChip label="Resp Rate"   value={latest.respiratoryRate} unit="/min" />
          {bmi && <VitalChip label="BMI" value={bmi} unit="kg/m²" />}
        </div>
        {latest.recordedByName && (
          <p className="text-xs text-gray-500 mt-3">Recorded by <span className="font-medium">{latest.recordedByName}</span></p>
        )}
      </div>

      {/* Full history table */}
      <div>
        {(() => {
          const visible = paging.pageRows;
          // Only render vital columns that have data in at least one reading across the full
          // history, not just this page, so the columns hold still as the pages turn.
          const activeCols = VITAL_TABLE_COLUMNS.filter(c => entries.some(v => vitalHasValue(c.get(v))));
          return (
            <>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">All Readings</h3>
            <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
            <TablePager paging={paging} noun="reading" className="px-3" rounded={false} />
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-xs font-semibold text-gray-600 uppercase">
                    <th className="px-3 py-2 text-left whitespace-nowrap">Recorded</th>
                    <th className="px-3 py-2 text-center">Token</th>
                    {activeCols.map(c => (
                      <th key={c.key} className="px-3 py-2 text-right whitespace-nowrap">{c.label}</th>
                    ))}
                    <th className="px-3 py-2 text-left">By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visible.map(v => (
                <tr key={v.visitId} className="hover:bg-gray-50/60">
                  <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{fmtDate(v.recordedAt)}</td>
                  <td className="px-3 py-2 text-center font-mono">{formatVisitToken(v)}</td>
                  {activeCols.map(c => (
                    <td key={c.key} className="px-3 py-2 text-right whitespace-nowrap">
                      {cell(c.get(v), c.flag ? c.flag(v) : false)}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-gray-600 text-xs whitespace-nowrap">{v.recordedByName || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
            </div>
            <TablePager paging={paging} noun="reading" className="px-3" rounded={false} position="bottom" />
            </div>
            </>
          );
        })()}
      </div>
      </>
      )}
    </div>
  );
};

// =================== VITALS TRENDS VIEW ===================
// Inline SVG sparkline charts for each vital that has at least 2 data points. Lightweight
// (no charting library) — enough for the typical 5-50 readings a patient accumulates.
// Systolic + diastolic are charted together as one two-line chart (see VitalBPSparkline);
// Pulse is intentionally not trended here. The rest each get their own single-line chart.
const BP_SERIES = {
  systolic:  { label: 'Systolic',  color: '#dc2626', normal: [90, 140] },
  diastolic: { label: 'Diastolic', color: '#f97316', normal: [60, 90] },
};
const VITAL_SERIES = [
  // Temperature trend chart intentionally omitted — a single fever reading skews the auto-scaled
  // axis and the chart isn't clinically meaningful as a trend.
  { key: 'spo2',               label: 'SpO₂',           unit: '%',    color: '#06b6d4', normal: [95, 100] },
  { key: 'respiratoryRate',    label: 'Resp Rate',      unit: '/min', color: '#00a0c4', normal: null },
  { key: 'weight',             label: 'Weight',         unit: 'kg',   color: '#3b82f6', normal: null },
  { key: 'height',             label: 'Height',         unit: 'cm',   color: '#6366f1', normal: null },
  { key: 'lengthCm',           label: 'Length',         unit: 'cm',   color: '#6366f1', normal: null },
  { key: 'headCircumferenceCm',label: 'Head Circ',      unit: 'cm',   color: '#a855f7', normal: null },
  { key: 'muacCm',             label: 'MUAC',           unit: 'cm',   color: '#84cc16', normal: [12.5, 25] },
  { key: 'bloodSugar',         label: 'Blood Sugar',    unit: 'mg/dL',color: '#7c3aed', normal: [70, 140] },
];

// Columns for the "All Readings" table. Each renders only if at least one reading has a value
// for it, so the table stays relevant to the patient: pediatric-only measures (Head Circ, MUAC,
// Length) and any never-recorded vital (Blood Sugar, Resp Rate, …) drop out for adults, while a
// minor's growth columns appear because they're populated. Recorded / Token / By are always
// shown and handled separately.
const VITAL_TABLE_COLUMNS = [
  { key: 'bp',     label: 'BP',       get: v => (v.bpSystolic && v.bpDiastolic) ? `${v.bpSystolic}/${v.bpDiastolic}` : null, flag: v => VITAL_FLAGS.bpHigh(v.bpSystolic, v.bpDiastolic) || VITAL_FLAGS.bpLow(v.bpSystolic, v.bpDiastolic) },
  { key: 'pulse',  label: 'Pulse',    get: v => v.pulse,         flag: v => VITAL_FLAGS.pulseHigh(v.pulse) || VITAL_FLAGS.pulseLow(v.pulse) },
  { key: 'temp',   label: 'Temp °F',  get: v => v.temperature,   flag: v => VITAL_FLAGS.tempHigh(v.temperature) },
  { key: 'spo2',   label: 'SpO₂',     get: v => v.spo2,          flag: v => VITAL_FLAGS.spo2Low(v.spo2) },
  { key: 'weight', label: 'Weight',   get: v => v.weight },
  { key: 'htlen',  label: 'Ht / Len', get: v => (v.height != null ? v.height : v.lengthCm) },
  { key: 'head',   label: 'Head',     get: v => v.headCircumferenceCm },
  { key: 'muac',   label: 'MUAC',     get: v => v.muacCm },
  { key: 'bs',     label: 'BS',       get: v => v.bloodSugar },
  { key: 'rr',     label: 'RR',       get: v => v.respiratoryRate },
  { key: 'bmi',    label: 'BMI',      get: v => formatBmi(v) },
];
const vitalHasValue = (val) => val !== null && val !== undefined && val !== '';

// Core vitals offered as trend charts (temperature intentionally excluded, see VITAL_SERIES above).
// Systolic and diastolic sit together here because they are charted as one Blood Pressure trend.
const COMBINED_VITALS = [
  { key: 'bpSystolic',      label: 'Systolic BP',  unit: 'mmHg',  color: '#3b82f6' },
  { key: 'bpDiastolic',     label: 'Diastolic BP', unit: 'mmHg',  color: '#22c55e' },
  { key: 'pulse',           label: 'Pulse',        unit: 'bpm',   color: '#ef4444' },
  { key: 'spo2',            label: 'SpO₂',         unit: '%',     color: '#06b6d4' },
  { key: 'respiratoryRate', label: 'Resp Rate',    unit: '/min',  color: '#00a0c4' },
  { key: 'bloodSugar',      label: 'Blood Sugar',  unit: 'mg/dL', color: '#7c3aed' },
  { key: 'weight',          label: 'Weight',       unit: 'kg',    color: '#f59e0b' },
  { key: 'muacCm',          label: 'MUAC',         unit: 'cm',    color: '#84cc16' },
];

// BMI is derived rather than stored, so the trend reads it through the same helper the table and
// the reading card use — one calculation, three places.
// Derives through roundBmi, not computeBmi: the chart plots the number it also prints as the
// headline reading, and the raw quotient renders as 33.21799307958478 next to a table that says
// 33.2 for the same reading.
const BMI_TREND = { key: 'bmi', label: 'BMI', unit: 'kg/m²', color: '#0ea5e9', derive: roundBmi };

// Chartable per-metric trends. This is COMBINED_VITALS plus the growth measures, which are left
// out of the combined chart on purpose (a 45 cm length next to a 200 mg/dL blood sugar squashes
// everything) but are exactly what you want to follow for a newborn or infant on its own axis.
const TREND_VITALS = [
  // "Resp Rate" was shortened to fit a chart legend; the capture form writes it out in full.
  ...COMBINED_VITALS.map(v => (v.key === 'respiratoryRate' ? { ...v, label: 'Respiratory Rate' } : v)),
  { key: 'height',              label: 'Height',             unit: 'cm', color: '#6366f1' },
  { key: 'lengthCm',            label: 'Length',             unit: 'cm', color: '#6366f1' },
  { key: 'headCircumferenceCm', label: 'Head Circumference', unit: 'cm', color: '#a855f7' },
  BMI_TREND,
];

// Pulse is the one vital the capture form renames by age: it asks for "Heart Rate" under five and
// "Pulse" from school age up. The chip should say what the nurse was asked for.
const TREND_LABEL_BY_BAND = {
  pulse: { NEWBORN: 'Heart Rate', INFANT: 'Heart Rate', YOUNG_CHILD: 'Heart Rate' },
};

// "Bump"-style smoothing (à la d3 curveBumpX): each segment is a cubic bezier whose control points
// sit at the horizontal midpoint, giving a smooth S-curve with horizontal tangents at every reading.
// This looks curvy even with just two readings (a Catmull-Rom spline through 2 points is straight).
const buildSmoothPath = (pts) => {
  if (pts.length === 0) return '';
  if (pts.length === 1) return `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const cpx = ((p1.x + p2.x) / 2).toFixed(1);
    d += ` C ${cpx} ${p1.y.toFixed(1)}, ${cpx} ${p2.y.toFixed(1)}, ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return d;
};

const VitalsTrendsView = ({ entries, dateOfBirth }) => {
  const hasVal = (v) => v !== null && v !== undefined && v !== '';
  const rows = [...(entries || [])].reverse(); // oldest-first so time flows left→right

  // Only offer metrics this patient actually has readings for, so the chips describe this patient:
  // a newborn gets Length and Head Circumference, an adult gets neither.
  // BMI is calculated, not stored, so a metric may carry a `derive` instead of naming a column.
  // Both the "does this patient have any" check and the chart itself read through this.
  const valueOf = (reading, metricKey) => {
    const def = TREND_VITALS.find(v => v.key === metricKey);
    return def?.derive ? def.derive(reading) : reading[metricKey];
  };
  const available = TREND_VITALS.filter(v => rows.some(e => hasVal(valueOf(e, v.key))));
  const hasBP = rows.some(e => hasVal(e.bpSystolic) && hasVal(e.bpDiastolic));
  // Systolic and diastolic are read as a pair, so they share one chip and one chart.
  const singles = available.filter(v => v.key !== 'bpSystolic' && v.key !== 'bpDiastolic');

  // Chip wording follows the capture form for this patient's age band — a newborn's "Heart Rate"
  // is an adult's "Pulse".
  const band = getAgeBandKey(dateOfBirth);
  const labelFor = (v) => TREND_LABEL_BY_BAND[v.key]?.[band] || v.label;

  const options = [
    ...(hasBP ? [{ key: 'bp', label: 'Blood Pressure' }] : []),
    ...singles.map(v => ({ key: v.key, label: labelFor(v) })),
  ];

  // Default to the first metric this patient has rather than a combined view.
  const [metric, setMetric] = useState(null);
  const active = metric && options.some(o => o.key === metric) ? metric : options[0]?.key;

  if (!entries || entries.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="text-center">
          <TrendingUp className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 mb-1">No vitals recorded yet</h3>
          <p className="text-sm text-gray-500">Trend charts will appear here once vitals are captured across visits.</p>
        </div>
      </div>
    );
  }

  if (!active) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="text-center">
          <TrendingUp className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-lg font-semibold text-gray-700 mb-1">Nothing to chart yet</h3>
          <p className="text-sm text-gray-500">These readings have no values that can be trended.</p>
        </div>
      </div>
    );
  }

  const selected = TREND_VITALS.find(v => v.key === active);
  // Normal bands come from VITAL_SERIES, which already carries the clinically meaningful ranges.
  const normalFor = (key) => VITAL_SERIES.find(v => v.key === key)?.normal || null;
  const activeLabel = options.find(o => o.key === active)?.label;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {options.map(o => (
          <button
            key={o.key}
            type="button"
            onClick={() => setMetric(o.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
              active === o.key
                ? 'bg-navy-800 text-white border-navy-800'
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-800'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>

      <div className="text-sm text-gray-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
        Showing <span className="font-semibold">{activeLabel}</span> across{' '}
        <span className="font-semibold">{entries.length}</span> reading{entries.length === 1 ? '' : 's'}, on its own scale. Hover any point for the exact value.
      </div>

      {active === 'bp' ? (
        <VitalBPSparkline
          rows={rows
            .filter(e => hasVal(e.bpSystolic) || hasVal(e.bpDiastolic))
            .map(e => ({
              date: e.recordedAt || e.visitDate,
              sys: hasVal(e.bpSystolic) ? Number(e.bpSystolic) : null,
              dia: hasVal(e.bpDiastolic) ? Number(e.bpDiastolic) : null,
            }))}
        />
      ) : (
        <VitalSparkline
          key={active}
          series={{
            label: activeLabel,
            unit: selected?.unit || '',
            color: selected?.color || '#3b82f6',
            normal: normalFor(active),
            points: rows
              .filter(e => hasVal(valueOf(e, active)))
              .map(e => ({ date: e.recordedAt || e.visitDate, value: Number(valueOf(e, active)) })),
          }}
        />
      )}
    </div>
  );
};

// Shared SVG hover tooltip for the trend charts. Positions a dark label box above the hovered
// point (flips below if near the top) and clamps horizontally to stay inside the chart.
const ChartHoverTip = ({ x, y, lines, chartW, padL, padR, padT }) => {
  const lineH = 16;
  const boxH = 10 + lines.length * lineH;
  const boxW = Math.max(80, ...lines.map(l => l.text.length * 6.4 + 18));
  let bx = x - boxW / 2;
  bx = Math.max(padL, Math.min(bx, chartW - padR - boxW));
  let by = y - boxH - 14;
  if (by < padT) by = y + 14; // flip below when too close to the top
  return (
    <g style={{ pointerEvents: 'none' }}>
      <rect x={bx} y={by} width={boxW} height={boxH} rx="6" fill="#111827" fillOpacity="0.92" />
      {lines.map((l, i) => (
        <text
          key={i}
          x={bx + boxW / 2}
          y={by + 17 + i * lineH}
          textAnchor="middle"
          fontSize="12"
          fontWeight={l.bold ? '700' : '400'}
          fill={l.color || '#ffffff'}
        >
          {l.text}
        </text>
      ))}
    </g>
  );
};

// SVG line chart with optional normal-range shading. Responsive via a viewBox; hovering a data
// point shows its date + value in a tooltip and highlights the point.
const VitalSparkline = ({ series }) => {
  const { points, color, normal, label, unit } = series;
  const [hoverIdx, setHoverIdx] = useState(null);
  const W = 760, H = 250, PAD_L = 46, PAD_R = 18, PAD_T = 18, PAD_B = 32;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  // Y-axis range — combines the data range with the normal range so both are visible.
  let minV = Math.min(...points.map(p => p.value));
  let maxV = Math.max(...points.map(p => p.value));
  if (normal) {
    minV = Math.min(minV, normal[0]);
    maxV = Math.max(maxV, normal[1]);
  }
  // Add 10% headroom either side so points don't touch the edges.
  const span = Math.max(1, maxV - minV);
  const yMin = minV - span * 0.1;
  const yMax = maxV + span * 0.1;

  // X coordinates spaced evenly by index (not time-proportional). For a clinic patient with
  // irregular visit cadence, time-proportional would put most points clumped at the right;
  // even spacing reads more clearly.
  const xFor = (i) => PAD_L + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
  const yFor = (v) => PAD_T + innerH - ((v - yMin) / (yMax - yMin)) * innerH;

  const pathD = buildSmoothPath(points.map((p, i) => ({ x: xFor(i), y: yFor(p.value) })));

  // 3 horizontal grid lines (ymin, mid, ymax).
  const yTicks = [yMin, (yMin + yMax) / 2, yMax];

  const latest = points[points.length - 1];
  const previous = points.length > 1 ? points[points.length - 2] : null;
  const delta = previous ? latest.value - previous.value : null;
  const deltaSign = delta == null ? '' : (delta > 0 ? '↑' : delta < 0 ? '↓' : '→');
  const deltaColor = delta == null ? 'text-gray-400' : (delta > 0 ? 'text-red-600' : delta < 0 ? 'text-blue-600' : 'text-gray-500');

  // Shaded normal range — translucent green band when defined.
  const normalBand = normal
    ? { yTop: yFor(normal[1]), yBottom: yFor(normal[0]) }
    : null;

  const fmt = (d) => formatClinicDate(d, { day: '2-digit', month: 'short' }, '');
  const fmtFull = (d) => formatClinicDate(d, { day: '2-digit', month: 'short', year: 'numeric' }, '');

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-base font-semibold text-gray-800">{label}</div>
          <div className="text-xs text-gray-500">{points.length} reading{points.length === 1 ? '' : 's'} · {fmt(points[0].date)} → {fmt(latest.date)}</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold" style={{ color }}>
            {latest.value} <span className="text-xs font-normal text-gray-400">{unit}</span>
          </div>
          {delta != null && (
            <div className={`text-xs font-medium ${deltaColor}`}>
              {deltaSign} {Math.abs(delta).toFixed(delta % 1 === 0 ? 0 : 1)} {unit} vs previous
            </div>
          )}
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        {/* Normal-range band */}
        {normalBand && (
          <rect
            x={PAD_L} y={normalBand.yTop}
            width={innerW} height={Math.max(0, normalBand.yBottom - normalBand.yTop)}
            fill="#009038" fillOpacity="0.08"
          />
        )}
        {/* Horizontal gridlines */}
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={PAD_L} y1={yFor(v)} x2={W - PAD_R} y2={yFor(v)} stroke="#e5e7eb" strokeDasharray="2 3" />
            <text x={PAD_L - 6} y={yFor(v) + 4} textAnchor="end" fontSize="11" fill="#9ca3af">
              {Number(v).toFixed(v % 1 === 0 ? 0 : 1)}
            </text>
          </g>
        ))}
        {/* Hover guide line */}
        {hoverIdx != null && (
          <line x1={xFor(hoverIdx)} y1={PAD_T} x2={xFor(hoverIdx)} y2={PAD_T + innerH} stroke={color} strokeOpacity="0.3" strokeDasharray="3 3" />
        )}
        {/* Line */}
        <path d={pathD} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {/* Data points */}
        {points.map((p, i) => (
          <circle key={i} cx={xFor(i)} cy={yFor(p.value)} r={hoverIdx === i ? 6 : 4} fill={color} stroke="white" strokeWidth="2" />
        ))}
        {/* X-axis date labels — show first, middle, last to avoid clutter */}
        {[0, Math.floor(points.length / 2), points.length - 1]
          .filter((v, i, a) => a.indexOf(v) === i) // dedupe when only 2 points
          .map((i) => (
            <text key={i} x={xFor(i)} y={H - 8} textAnchor="middle" fontSize="11" fill="#6b7280">
              {fmt(points[i].date)}
            </text>
          ))}
        {/* Hover tooltip */}
        {hoverIdx != null && (
          <ChartHoverTip
            x={xFor(hoverIdx)} y={yFor(points[hoverIdx].value)}
            chartW={W} padL={PAD_L} padR={PAD_R} padT={PAD_T}
            lines={[
              { text: fmtFull(points[hoverIdx].date), color: '#cbd5e1' },
              { text: `${points[hoverIdx].value} ${unit}`, color: '#ffffff', bold: true },
            ]}
          />
        )}
        {/* Transparent hit areas — generous radius so points are easy to hover */}
        {points.map((p, i) => (
          <circle
            key={`hit-${i}`}
            cx={xFor(i)} cy={yFor(p.value)} r="16" fill="transparent"
            style={{ cursor: 'pointer' }}
            onMouseEnter={() => setHoverIdx(i)}
            onMouseLeave={() => setHoverIdx(null)}
          />
        ))}
      </svg>
      {normal && (
        <div className="text-[10px] text-gray-400 mt-1">
          Green band: normal range ({normal[0]}–{normal[1]} {unit})
        </div>
      )}
    </div>
  );
};

// Combined Blood-Pressure chart — systolic + diastolic on one set of axes, each its own
// coloured line, sharing a y-scale. No green band (two overlapping ranges would be noisy);
// the normal ranges are noted in the footer instead. `rows` = [{ date, sys, dia }] oldest-first;
// each line skips its own null readings.
const VitalBPSparkline = ({ rows }) => {
  const [hoverIdx, setHoverIdx] = useState(null);
  const W = 760, H = 250, PAD_L = 46, PAD_R = 18, PAD_T = 18, PAD_B = 32;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const sysVals = rows.map(r => r.sys).filter(v => v != null);
  const diaVals = rows.map(r => r.dia).filter(v => v != null);
  const all = [...sysVals, ...diaVals, BP_SERIES.systolic.normal[1], BP_SERIES.diastolic.normal[0]];
  const minV = Math.min(...all);
  const maxV = Math.max(...all);
  const span = Math.max(1, maxV - minV);
  const yMin = minV - span * 0.1;
  const yMax = maxV + span * 0.1;

  const xFor = (i) => PAD_L + (rows.length === 1 ? innerW / 2 : (i / (rows.length - 1)) * innerW);
  const yFor = (v) => PAD_T + innerH - ((v - yMin) / (yMax - yMin)) * innerH;

  // Path connecting only the present points for a given measure (breaks across nulls).
  // Readings where only one of the two was taken are skipped, so the line bridges the gap
  // rather than dropping to zero.
  const buildPath = (accessor) => buildSmoothPath(
    rows
      .map((r, i) => ({ v: accessor(r), i }))
      .filter(({ v }) => v != null)
      .map(({ v, i }) => ({ x: xFor(i), y: yFor(v) }))
  );

  const renderPoints = (accessor, color) => rows.map((r, i) => {
    const v = accessor(r);
    if (v == null) return null;
    return <circle key={i} cx={xFor(i)} cy={yFor(v)} r={hoverIdx === i ? 6 : 4} fill={color} stroke="white" strokeWidth="2" />;
  });

  const lastOf = (accessor) => {
    const pts = rows.filter(r => accessor(r) != null);
    return pts.length ? accessor(pts[pts.length - 1]) : null;
  };
  const latestSys = lastOf(r => r.sys);
  const latestDia = lastOf(r => r.dia);

  const yTicks = [yMin, (yMin + yMax) / 2, yMax];
  const fmt = (d) => formatClinicDate(d, { day: '2-digit', month: 'short' }, '');
  const fmtFull = (d) => formatClinicDate(d, { day: '2-digit', month: 'short', year: 'numeric' }, '');
  const sys = BP_SERIES.systolic, dia = BP_SERIES.diastolic;

  // Tooltip y-anchor: the higher (smaller y) of the two points at the hovered reading.
  const hoverYAnchor = (() => {
    if (hoverIdx == null) return 0;
    const r = rows[hoverIdx];
    const ys = [r.sys, r.dia].filter(v => v != null).map(yFor);
    return ys.length ? Math.min(...ys) : PAD_T;
  })();

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-base font-semibold text-gray-800">Blood Pressure</div>
          <div className="text-xs text-gray-500">{rows.length} reading{rows.length === 1 ? '' : 's'} · {fmt(rows[0].date)} → {fmt(rows[rows.length - 1].date)}</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold">
            <span style={{ color: sys.color }}>{latestSys ?? '—'}</span>
            <span className="text-gray-300">/</span>
            <span style={{ color: dia.color }}>{latestDia ?? '—'}</span>
            <span className="text-xs font-normal text-gray-400 ml-1">mmHg</span>
          </div>
          <div className="flex items-center justify-end gap-3 mt-0.5 text-[10px] font-medium">
            <span className="inline-flex items-center gap-1" style={{ color: sys.color }}>
              <span className="w-2.5 h-0.5 rounded inline-block" style={{ background: sys.color }} />Systolic
            </span>
            <span className="inline-flex items-center gap-1" style={{ color: dia.color }}>
              <span className="w-2.5 h-0.5 rounded inline-block" style={{ background: dia.color }} />Diastolic
            </span>
          </div>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="xMidYMid meet">
        {yTicks.map((v, i) => (
          <g key={i}>
            <line x1={PAD_L} y1={yFor(v)} x2={W - PAD_R} y2={yFor(v)} stroke="#e5e7eb" strokeDasharray="2 3" />
            <text x={PAD_L - 6} y={yFor(v) + 4} textAnchor="end" fontSize="11" fill="#9ca3af">{Number(v).toFixed(0)}</text>
          </g>
        ))}
        {/* Hover guide line */}
        {hoverIdx != null && (
          <line x1={xFor(hoverIdx)} y1={PAD_T} x2={xFor(hoverIdx)} y2={PAD_T + innerH} stroke="#9ca3af" strokeOpacity="0.4" strokeDasharray="3 3" />
        )}
        <path d={buildPath(r => r.sys)} fill="none" stroke={sys.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d={buildPath(r => r.dia)} fill="none" stroke={dia.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {renderPoints(r => r.sys, sys.color)}
        {renderPoints(r => r.dia, dia.color)}
        {[0, Math.floor((rows.length - 1) / 2), rows.length - 1]
          .filter((v, i, a) => a.indexOf(v) === i)
          .map(i => (
            <text key={i} x={xFor(i)} y={H - 8} textAnchor="middle" fontSize="11" fill="#6b7280">{fmt(rows[i].date)}</text>
          ))}
        {/* Hover tooltip — shows both measures for the hovered reading */}
        {hoverIdx != null && (
          <ChartHoverTip
            x={xFor(hoverIdx)} y={hoverYAnchor}
            chartW={W} padL={PAD_L} padR={PAD_R} padT={PAD_T}
            lines={[
              { text: fmtFull(rows[hoverIdx].date), color: '#cbd5e1' },
              { text: `Systolic: ${rows[hoverIdx].sys ?? '—'}`, color: '#fca5a5', bold: true },
              { text: `Diastolic: ${rows[hoverIdx].dia ?? '—'}`, color: '#fdba74', bold: true },
            ]}
          />
        )}
        {/* Transparent full-height hit columns so hovering anywhere over a reading works */}
        {rows.map((r, i) => (
          <rect
            key={`hit-${i}`}
            x={xFor(i) - innerW / (2 * Math.max(1, rows.length))} y={PAD_T}
            width={innerW / Math.max(1, rows.length)} height={innerH}
            fill="transparent" style={{ cursor: 'pointer' }}
            onMouseEnter={() => setHoverIdx(i)}
            onMouseLeave={() => setHoverIdx(null)}
          />
        ))}
      </svg>
      <div className="text-[10px] text-gray-400 mt-1">
        Normal: systolic {sys.normal[0]}–{sys.normal[1]}, diastolic {dia.normal[0]}–{dia.normal[1]} mmHg
      </div>
    </div>
  );
};

// =================== PATIENT PRESCRIPTION NOTES TAB ===================
// Powers both the "Examination Notes" and "Clinical Notes" tabs — same shape, different
// underlying field on the prescription. type ∈ {"examination", "clinical"} drives the
// API query param and the empty-state copy.

const PatientPrescriptionNotesTab = ({ patientId, type, emptyLabel }) => {
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await prescriptionAPI.getPatientNotes(patientId, type);
        if (!cancelled) setEntries(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load notes');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [patientId, type]);

  const fmtDate = (d) => formatClinicDate(d, { day: '2-digit', month: 'short', year: 'numeric' });

  const paging = usePagedRows(entries || [], { id: 'patient-notes', resetOn: [patientId, type] });

  if (loading) {
    return <div className="text-center text-gray-500 py-12">Loading…</div>;
  }
  if (error) {
    return <div className="text-center text-red-600 py-12">{error}</div>;
  }
  if (!entries || entries.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <div className="text-center">
          {type === 'clinical' ? <ClipboardList className="w-12 h-12 mx-auto text-gray-300 mb-3" /> : <ClipboardCheck className="w-12 h-12 mx-auto text-gray-300 mb-3" />}
          <h3 className="text-lg font-semibold text-gray-700 mb-1">No {emptyLabel} yet</h3>
          <p className="text-sm text-gray-500">
            {emptyLabel === 'clinical notes'
              ? 'Clinical notes recorded on prescriptions will appear here.'
              : 'Examination findings recorded on prescriptions will appear here.'}
          </p>
        </div>
      </div>
    );
  }

  const visible = paging.pageRows;

  return (
    <div className="space-y-4">
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
        <TablePager paging={paging} noun="entry" nounPlural="entries" className="px-3" rounded={false} />
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-xs font-semibold text-gray-600 uppercase">
              <th className="px-3 py-2 text-left whitespace-nowrap">Date</th>
              <th className="px-3 py-2 text-center">Token</th>
              <th className="px-3 py-2 text-left">Doctor</th>
              <th className="px-3 py-2 text-left">Note</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visible.map((n) => (
              <tr key={n.prescriptionId} className="hover:bg-gray-50/60 align-top">
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{fmtDate(n.prescriptionDate)}</div>
                  {n.prescriptionNumber && (
                    <div className="text-[10px] text-gray-400 font-mono">{n.prescriptionNumber}</div>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  {n.tokenNumber ? (
                    <span className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-1.5 rounded-md bg-blue-50 text-blue-700 font-bold text-xs whitespace-nowrap">
                      {formatVisitToken(n)}
                    </span>
                  ) : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-3 py-2">
                  {n.doctorName ? (
                    <>
                      <div className="text-gray-800">{n.doctorName}</div>
                      {n.doctorSpecialization && (
                        <div className="text-xs text-gray-400">{n.doctorSpecialization}</div>
                      )}
                    </>
                  ) : <span className="text-gray-300">—</span>}
                </td>
                {/* The note is why this row exists, so it takes the slack: pre-wrap keeps the
                    doctor's own line breaks, w-full makes the fixed columns shrink first. */}
                <td className="px-3 py-2 w-full text-gray-800 whitespace-pre-wrap leading-relaxed">
                  {n.noteText}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePager paging={paging} noun="entry" nounPlural="entries" className="px-3" rounded={false} position="bottom" />
      </div>
    </div>
  );
};

// =================== Small reusable list/fetch hook ===================
// All the remaining list-style tabs share the exact same lifecycle: lazy fetch on mount,
// pass through loading / error / empty states, then render with show-5-load-more paging.
// Factoring it out keeps the per-tab code focused on row rendering.
const useTabList = (fetchFn, deps) => {
  const [entries, setEntries] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchFn();
        if (!cancelled) setEntries(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return { entries, loading, error };
};

const TabEmptyState = ({ icon, title, message }) => (
  <div className="flex items-center justify-center min-h-[300px]">
    <div className="text-center">
      {icon}
      <h3 className="text-lg font-semibold text-gray-700 mb-1 mt-3">{title}</h3>
      <p className="text-sm text-gray-500">{message}</p>
    </div>
  </div>
);

const fmtShortDate = (d) =>
  formatClinicDate(d, { day: '2-digit', month: 'short', year: 'numeric' });

// =================== PATIENT PRESCRIPTIONS TAB ===================
const PatientPrescriptionsTab = ({ patientId }) => {
  const { entries, loading, error } =
    useTabList(() => prescriptionAPI.getPatientSummaries(patientId), [patientId]);
  const notify = useToast();
  // Eye-button "view" — fetches the full prescription and opens the shared read-only detail view
  // (same view used in the Prescription module).
  const [viewRx, setViewRx] = useState(null);
  const [viewLoadingId, setViewLoadingId] = useState(null);
  // Private notes are encrypted at rest and deliberately absent from the prescription payload, so
  // they are fetched only when someone actually asks to read one. The printable view the eye button
  // opens never contains them, which is why they need their own way in from here.
  const [noteRx, setNoteRx] = useState(null);        // { id, number, date, doctor, notes }
  const [noteLoadingId, setNoteLoadingId] = useState(null);
  const noteDialogRef = useDialog(() => setNoteRx(null), { confirmDiscard: false });
  const openPrivateNotes = async (p) => {
    setNoteLoadingId(p.prescriptionId);
    try {
      const res = await prescriptionAPI.getPrivateNotes(p.prescriptionId);
      setNoteRx({
        id: p.prescriptionId,
        number: p.prescriptionNumber,
        date: p.prescriptionDate || p.createdAt,
        doctor: p.doctorName,
        notes: (res?.notes || '').trim(),
      });
    } catch (e) {
      notify('error', e.message || 'Failed to load the private note');
    } finally {
      setNoteLoadingId(null);
    }
  };
  const openView = async (id) => {
    setViewLoadingId(id);
    try {
      const full = await prescriptionAPI.getById(id);
      setViewRx(full);
    } catch (e) {
      notify('error', 'Failed to load prescription');
    } finally {
      setViewLoadingId(null);
    }
  };
  // Private notes had no way in except spotting a padlock while scrolling the full list, which
  // meant reading every row to find out whether there were any at all. They get their own child
  // tab, listing only the prescriptions that carry one.
  const [rxSubTab, setRxSubTab] = useState('all'); // 'all' | 'notes'
  const withNotes = (entries || []).filter(e => e.hasPrivateNotes);

  // The note text itself, per prescription: { loading } | { text } | { error }. The tab shows the
  // note on the row rather than behind a Read note button, so the doctor reads it without a click.
  // One request per row is the only way to do that: the ciphertext is WRITE_ONLY and never rides in
  // a list payload, so plaintext comes only from the per-prescription endpoint.
  const [noteTexts, setNoteTexts] = useState({});
  const notesRequested = useRef(new Set());
  const loadNoteText = useCallback(async (id) => {
    notesRequested.current.add(id);
    setNoteTexts(prev => ({ ...prev, [id]: { loading: true } }));
    try {
      const res = await prescriptionAPI.getPrivateNotes(id);
      setNoteTexts(prev => ({ ...prev, [id]: { text: (res?.notes || '').trim() } }));
    } catch (e) {
      setNoteTexts(prev => ({ ...prev, [id]: { error: e.message || 'Could not load this note' } }));
    }
  }, []);
  // Fetched when the tab is opened, not with the patient: a note is read rarely and is the most
  // sensitive thing here, so it is not pulled over the wire until someone asks to see it.
  const noteIdsKey = withNotes.map(p => p.prescriptionId).join(',');
  useEffect(() => {
    if (rxSubTab !== 'notes') return;
    withNotes.forEach(p => {
      if (!notesRequested.current.has(p.prescriptionId)) loadNoteText(p.prescriptionId);
    });
    // withNotes is rebuilt every render; noteIdsKey is the part that actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rxSubTab, noteIdsKey, loadNoteText]);

  const paging = usePagedRows(entries || [], { id: 'patient-prescriptions', resetOn: [patientId] });

  if (loading) return <div className="text-center text-gray-500 py-12">Loading prescriptions…</div>;
  if (error)   return <div className="text-center text-red-600 py-12">{error}</div>;
  if (!entries || entries.length === 0) {
    return <TabEmptyState
      icon={<Stethoscope className="w-12 h-12 mx-auto text-gray-300" />}
      title="No prescriptions yet"
      message="Prescriptions written for this patient will appear here." />;
  }

  const visible = paging.pageRows;
  // Only offered when a note actually exists, so the bar never promises a tab with nothing behind
  // it. Same rule the Lab Tests child tabs follow.
  const showSubTabs = withNotes.length > 0;
  const currentSubTab = showSubTabs ? rxSubTab : 'all';
  return (
    <div className="space-y-4">
      {showSubTabs && (
        <div className="flex gap-2">
          {[
            { key: 'all',   label: 'All Prescriptions', badge: entries.length },
            { key: 'notes', label: 'Private Notes',     badge: withNotes.length },
          ].map(c => {
            const on = currentSubTab === c.key;
            return (
              <button
                key={c.key}
                onClick={() => setRxSubTab(c.key)}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-all ${
                  on
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-800'
                }`}
              >
                {c.key === 'notes' && <Lock className="w-3.5 h-3.5" />}
                <span>{c.label}</span>
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                  on ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'
                }`}>{c.badge}</span>
              </button>
            );
          })}
        </div>
      )}

      {currentSubTab === 'notes' ? (
        <>
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 flex items-center gap-2">
            <Lock className="w-4 h-4 flex-shrink-0" />
            <span>
              <span className="font-semibold">{withNotes.length}</span> prescription{withNotes.length === 1 ? ' carries' : 's carry'} a
              private note. Notes are never shown to the patient and never printed.
            </span>
          </div>
          {/* A note is prose, not a field, so this is a list of notes rather than a table: put a
              paragraph in a column and every short note leaves half the row empty, which is
              exactly how it read. The prescription, its date and its doctor sit on one small line
              above the note, and the note itself has the full width. */}
          <div className="border border-gray-200 rounded-xl overflow-hidden bg-white divide-y divide-gray-100">
            {withNotes.map((p) => {
              const note = noteTexts[p.prescriptionId];
              return (
                <div key={p.prescriptionId} className="px-4 py-3 hover:bg-amber-50/40">
                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-gray-500">
                    <Lock className="w-3 h-3 text-amber-500 flex-shrink-0" />
                    <button
                      onClick={() => openView(p.prescriptionId)}
                      disabled={viewLoadingId === p.prescriptionId}
                      className="inline-flex items-center gap-1.5 font-mono text-xs font-semibold text-emerald-700 underline decoration-emerald-300 underline-offset-2 hover:text-emerald-800 hover:decoration-emerald-500 disabled:opacity-50 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      title="Open the prescription (printable, without private notes)"
                    >
                      {p.prescriptionNumber || 'Prescription'}
                      {viewLoadingId === p.prescriptionId && <RefreshCw className="w-3 h-3 animate-spin" />}
                    </button>
                    <span>{fmtShortDate(p.prescriptionDate)}</span>
                    {p.doctorName && <span className="text-gray-400">· {p.doctorName}</span>}
                  </div>
                  <div className="mt-1.5 text-sm text-gray-800">
                    {note?.loading && (
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-400">
                        <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Loading the note…
                      </span>
                    )}
                    {note?.error && (
                      <span className="inline-flex items-center gap-2 text-xs text-red-600">
                        {note.error}
                        <button
                          onClick={() => loadNoteText(p.prescriptionId)}
                          className="underline underline-offset-2 hover:text-red-700"
                        >
                          Try again
                        </button>
                      </span>
                    )}
                    {note && !note.loading && !note.error && (
                      note.text
                        ? <p className="whitespace-pre-wrap leading-relaxed">{note.text}</p>
                        : <span className="text-gray-300">Empty note</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
      <>
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
        <TablePager paging={paging} noun="prescription" className="px-3" rounded={false} />
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-xs font-semibold text-gray-600 uppercase">
              <th className="px-3 py-2 text-left whitespace-nowrap">Date</th>
              <th className="px-3 py-2 text-center">Token</th>
              <th className="px-3 py-2 text-left">Doctor</th>
              <th className="px-3 py-2 text-center">Status</th>
              <th className="px-3 py-2 text-left">Diagnosis</th>
              <th className="px-3 py-2 text-left">Medicines</th>
              <th className="px-3 py-2 text-left">Lab Tests</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visible.map((p) => (
              <tr key={p.prescriptionId} className="hover:bg-gray-50/60 align-top">
                <td className="px-3 py-2 whitespace-nowrap">
                  <div className="font-medium text-gray-900">{fmtShortDate(p.prescriptionDate)}</div>
                  {/* The number opens the prescription, the same as it does on the Private Notes
                      tab. The eye stays for the rest of the row. */}
                  {p.prescriptionNumber && (
                    <button
                      onClick={() => openView(p.prescriptionId)}
                      disabled={viewLoadingId === p.prescriptionId}
                      className="text-[10px] text-gray-400 font-mono underline decoration-gray-300 underline-offset-2 hover:text-emerald-700 hover:decoration-emerald-400 disabled:opacity-50 rounded focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      title="Open the prescription"
                    >
                      {p.prescriptionNumber}
                    </button>
                  )}
                  {/* Follow-up rides under the date rather than taking a column: it is set on a
                      minority of prescriptions and it is a date, so it reads naturally here. */}
                  {p.followupDate && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-amber-700">
                      <Calendar className="w-3 h-3" /> F/U {fmtShortDate(p.followupDate)}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  {p.tokenNumber ? (
                    <span className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-1.5 rounded-md bg-blue-50 text-blue-700 font-bold text-xs whitespace-nowrap">
                      {formatVisitToken(p)}
                    </span>
                  ) : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-3 py-2">
                  {p.doctorName ? (
                    <>
                      <div className="text-gray-800">{p.doctorName}</div>
                      {p.doctorSpecialization && (
                        <div className="text-xs text-gray-400">{p.doctorSpecialization}</div>
                      )}
                    </>
                  ) : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-3 py-2 text-center whitespace-nowrap">
                  {p.status ? <StatusPill style={RX_STATUS_STYLE[p.status]} fallback={p.status} /> : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-3 py-2 text-gray-800">{p.diagnosis || <span className="text-gray-300">—</span>}</td>
                <td className="px-3 py-2 w-full">
                  {p.medicines?.length > 0 ? (
                    <ul className="space-y-0.5">
                      {p.medicines.map((m, i) => (
                        <li key={i} className="text-xs text-gray-700 flex items-start gap-1.5">
                          <span className="text-emerald-500 mt-0.5 shrink-0">•</span>
                          <span>
                            <span className="font-medium text-gray-900">{m.name}</span>
                            {m.strength && <span className="text-gray-400 ml-1">{m.strength}</span>}
                            {m.dosage && <span className="text-gray-500 ml-1">· {m.dosage}</span>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-3 py-2 text-xs text-gray-700">
                  {p.labTestNames?.length > 0 ? p.labTestNames.join(', ') : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-1">
                    {p.hasPrivateNotes && (
                      <button
                        onClick={() => openPrivateNotes(p)}
                        disabled={noteLoadingId === p.prescriptionId}
                        className="p-1.5 text-amber-600 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-colors disabled:opacity-50"
                        title="Read the doctor's private note"
                      >
                        {noteLoadingId === p.prescriptionId
                          ? <RefreshCw className="w-4 h-4 animate-spin" />
                          : <Lock className="w-4 h-4" />}
                      </button>
                    )}
                    <button
                      onClick={() => openView(p.prescriptionId)}
                      disabled={viewLoadingId === p.prescriptionId}
                      className="p-1.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50"
                      title="View prescription (printable, without private notes)"
                    >
                      {viewLoadingId === p.prescriptionId
                        ? <RefreshCw className="w-4 h-4 animate-spin" />
                        : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePager paging={paging} noun="prescription" className="px-3" rounded={false} position="bottom" />
      </div>
      </>
      )}
      {viewRx && <PrescriptionDetailModal prescription={viewRx} onClose={() => setViewRx(null)} />}

      {/* Private note reader. Deliberately its own small modal rather than a section of the
          printable view: the printable prescription is what a patient may be handed, and these
          notes are the one part of a consultation that must never appear on it. */}
      {noteRx && (
        <div
          ref={noteDialogRef}
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4 print:hidden"
          onClick={(e) => { if (e.target === e.currentTarget) setNoteRx(null); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col overflow-hidden">
            <div className="flex-shrink-0 relative bg-gradient-to-r from-amber-600 to-orange-600 text-white">
              <button
                onClick={() => setNoteRx(null)}
                className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-xl transition-colors"
                title="Close"
              >
                <X className="h-5 w-5 text-white" />
              </button>
              <div className="p-5 pr-16">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <Lock className="w-4 h-4" /> Private note
                </h3>
                <p className="text-xs text-white text-opacity-90 mt-0.5">
                  {[noteRx.number, noteRx.doctor, noteRx.date ? fmtShortDate(noteRx.date) : null]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {noteRx.notes
                ? <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{noteRx.notes}</p>
                : <p className="text-sm text-gray-400">This prescription has no private note.</p>}
            </div>
            <div className="flex-shrink-0 border-t border-gray-200 px-5 py-3 bg-gray-50 flex items-center justify-between gap-3">
              <span className="text-[11px] text-gray-500">Never shown to the patient or printed.</span>
              <div className="flex items-center gap-2">
                {/* The note on its own rarely answers the question. This carries straight over to
                    what was actually prescribed, closing the note first so the two do not stack. */}
                <button
                  onClick={() => { const id = noteRx.id; setNoteRx(null); openView(id); }}
                  disabled={viewLoadingId === noteRx.id}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                >
                  {viewLoadingId === noteRx.id
                    ? <RefreshCw className="w-4 h-4 animate-spin" />
                    : <Eye className="w-4 h-4" />}
                  View prescription
                </button>
                <button
                  onClick={() => setNoteRx(null)}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// =================== PATIENT LAB TESTS TAB ===================
const URGENCY_COLOR = {
  STAT:      'bg-red-100 text-red-700 border-red-200',
  URGENT:    'bg-orange-100 text-orange-700 border-orange-200',
  ROUTINE:   'bg-gray-100 text-gray-700 border-gray-200',
  FOLLOW_UP: 'bg-blue-100 text-blue-700 border-blue-200',
};
// Exported for reuse in the Prescription module header ("Lab Reports" link).
export const PatientLabTestsTab = ({ patientId }) => {
  const { entries, loading, error } =
    useTabList(() => prescriptionAPI.getPatientLabTestsHistory(patientId), [patientId]);
  // Uploaded lab-report documents too, so a report always shows under the lab tab even when it
  // isn't attached to an ordered test (external reports, or uploads made without an order) —
  // mirroring how the Radiology tab lists imaging documents.
  const [labDocs, setLabDocs] = useState(null);
  useEffect(() => {
    let alive = true;
    patientDocumentAPI.getByPatient(patientId)
      .then(docs => { if (alive) setLabDocs((Array.isArray(docs) ? docs : []).filter(d => d.documentType === 'LAB_REPORT')); })
      .catch(() => { if (alive) setLabDocs([]); });
    return () => { alive = false; };
  }, [patientId]);

  const paging = usePagedRows(entries || [], { id: 'patient-lab-tests', resetOn: [patientId] });

  if (loading) return <div className="text-center text-gray-500 py-12">Loading lab tests…</div>;
  if (error)   return <div className="text-center text-red-600 py-12">{error}</div>;

  // Reports linked to an ordered test already surface on that test's card; this section is for
  // the rest.
  const linkedDocIds = new Set((entries || []).flatMap(t => t.documentIds || []));
  const standaloneDocs = (labDocs || []).filter(d => !linkedDocIds.has(d.id));
  const hasTests = entries && entries.length > 0;

  if (!hasTests && standaloneDocs.length === 0) {
    return <TabEmptyState
      icon={<FlaskConical className="w-12 h-12 mx-auto text-gray-300" />}
      title="No lab tests yet"
      message="Lab tests ordered across visits and uploaded lab reports will appear here." />;
  }

  const visible = paging.pageRows;
  return (
    <div className="space-y-4">
      {/* Summary banner — covers both ordered tests and uploaded reports */}
      <div className="text-sm text-gray-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
        {hasTests && (
          <><span className="font-semibold">{entries.length}</span> lab test{entries.length === 1 ? '' : 's'}</>
        )}
        {hasTests && standaloneDocs.length > 0 && ' · '}
        {standaloneDocs.length > 0 && (
          <><span className="font-semibold">{standaloneDocs.length}</span> uploaded lab report{standaloneDocs.length === 1 ? '' : 's'}</>
        )}
        .
      </div>
      {hasTests && (
      <>
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
        <TablePager paging={paging} noun="lab test" className="px-3" rounded={false} />
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-xs font-semibold text-gray-600 uppercase">
              <th className="px-3 py-2 text-left whitespace-nowrap">Date</th>
              <th className="px-3 py-2 text-left">Test</th>
              <th className="px-3 py-2 text-center">Status</th>
              <th className="px-3 py-2 text-left">Ordered By</th>
              <th className="px-3 py-2 text-left">Reason / Instructions</th>
              <th className="px-3 py-2 text-left">Result</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visible.map((t) => (
              <tr key={t.prescriptionLabTestId} className="hover:bg-gray-50/60 align-top">
                <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{fmtShortDate(t.prescriptionDate)}</td>
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-900">{t.testName}</div>
                  {/* Urgency sits under the name rather than in a column of its own: it is set on
                      a small minority of tests, so a dedicated column would be mostly dashes. */}
                  {t.urgency && t.urgency !== 'ROUTINE' && (
                    <span className={`mt-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${URGENCY_COLOR[t.urgency] || URGENCY_COLOR.ROUTINE}`}>
                      {t.urgency}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-center whitespace-nowrap">
                  {t.isCompleted ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200">
                      Completed
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                      Pending
                    </span>
                  )}
                  {t.documentIds && t.documentIds.length > 0 && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-emerald-700">
                      <FileText className="w-3 h-3" />
                      {t.documentIds.length} report{t.documentIds.length === 1 ? '' : 's'}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 text-gray-700">{t.doctorName || <span className="text-gray-300">—</span>}</td>
                <td className="px-3 py-2 text-xs text-gray-600">
                  {t.clinicalNotes && <div><span className="font-semibold uppercase tracking-wider text-gray-400 mr-1">Reason</span>{t.clinicalNotes}</div>}
                  {t.instructions && <div className="mt-0.5"><span className="font-semibold uppercase tracking-wider text-gray-400 mr-1">Instr</span>{t.instructions}</div>}
                  {!t.clinicalNotes && !t.instructions && <span className="text-gray-300">—</span>}
                </td>
                <td className="px-3 py-2 w-full">
                  {t.result ? (
                    <div className="text-gray-800 whitespace-pre-wrap leading-relaxed">
                      {t.resultDate && (
                        <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">
                          {fmtShortDate(t.resultDate)}
                        </div>
                      )}
                      {t.result}
                    </div>
                  ) : <span className="text-gray-300">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePager paging={paging} noun="lab test" className="px-3" rounded={false} position="bottom" />
      </div>
      </>
      )}
      {/* Uploaded lab reports not attached to any ordered test */}
      {standaloneDocs.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">
            Uploaded Lab Reports{hasTests ? ' (not linked to an ordered test)' : ''}
          </div>
          <DocReportsTable patientId={patientId} docs={standaloneDocs} typeLabel={() => 'Lab Report'} />
        </div>
      )}
    </div>
  );
};

// =================== PATIENT MEDICAL HISTORY TAB ===================
const ALERT_SEVERITY_COLOR = {
  HIGH:   'bg-red-50 border-red-200 text-red-700',
  MEDIUM: 'bg-amber-50 border-amber-200 text-amber-700',
  LOW:    'bg-blue-50 border-blue-200 text-blue-700',
};
// Exported for reuse in the Prescription module header ("Medical History" link).
export const PatientMedicalHistoryTab = ({ patientId }) => {
  const { entries, loading, error } =
    useTabList(() => prescriptionAPI.getPatientAlerts(patientId), [patientId]);
  // The first-visit intake, read back beside the alerts it raised. It carries its own loading
  // state rather than joining the tab's: the alerts are the urgent half and should not wait on it,
  // and a patient whose file predates the intake form simply has none.
  const [intake, setIntake] = useState(null);
  useEffect(() => {
    let cancelled = false;
    intakeAPI.forPatient(patientId)
      .then((d) => { if (!cancelled) setIntake(d); })
      .catch(() => { if (!cancelled) setIntake(null); });
    return () => { cancelled = true; };
  }, [patientId]);

  if (loading) return <div className="text-center text-gray-500 py-12">Loading medical history…</div>;
  if (error)   return <div className="text-center text-red-600 py-12">{error}</div>;

  const answers = Array.isArray(intake?.answers) ? intake.answers : [];
  const earlier = Array.isArray(intake?.earlier) ? intake.earlier : [];
  const visible = entries || [];
  if (visible.length === 0 && answers.length === 0 && earlier.length === 0) {
    return <TabEmptyState
      icon={<Heart className="w-12 h-12 mx-auto text-gray-300" />}
      title="No medical history on file"
      message="Alerts, and the history taken at the patient's first prescription, appear here." />;
  }

  const titleize = (s) => s ? s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '';

  return (
    <div className="space-y-6">
      {visible.length > 0 && (
      <div className="space-y-4">
      <div className="text-sm text-gray-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
        <span className="font-semibold">{visible.length}</span> alert{visible.length === 1 ? '' : 's'}.
      </div>
      <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-xs font-semibold text-gray-600 uppercase">
              <th className="px-3 py-2 text-left whitespace-nowrap">Alert</th>
              <th className="px-3 py-2 text-center">Severity</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-right whitespace-nowrap">Added</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visible.map((a) => {
              // Severity used to tint the whole card. In a table that would stripe the rows into
              // noise, so it moves onto a pill and the row stays neutral.
              const sev = ALERT_SEVERITY_COLOR[a.severity] || ALERT_SEVERITY_COLOR.MEDIUM;
              return (
                <tr key={a.id} className="hover:bg-gray-50/60 align-top">
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5 font-medium text-gray-900">
                      <AlertCircle className="w-4 h-4 text-gray-400" />
                      {titleize(a.alertType) || 'Alert'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {a.severity ? (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${sev}`}>
                        {a.severity}
                      </span>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 w-full text-gray-800 whitespace-pre-wrap leading-relaxed">
                    {a.description || <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-500 whitespace-nowrap">
                    {a.createdAt ? fmtShortDate(a.createdAt) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>
      )}

      <PatientIntakePanel intake={intake} answers={answers} alerts={visible} />
    </div>
  );
};

// The first-visit medical history, read back on the patient's file. The answers that matter
// clinically come first on their own: a doctor opening this wants "smokes, father had a heart
// attack, allergic to penicillin", not to read 20 rows of mostly No. The full record sits under
// them, folded away.
//
// "Serious" and the alert mapping are read off the ANSWER, not off the current catalogue, because
// each answer snapshots them. A question demoted in Settings therefore does not quietly drop out
// of records already taken.
//
// A merge brings a retired file's history across. When this file already had its own, that one
// arrives as an earlier record (`intake.earlier`), shown under the patient's own and headed by the
// MRN it was taken on. It is only ever read: the doctor amends the patient's own.
const PatientIntakePanel = ({ intake, answers, alerts = [] }) => {
  const own = Array.isArray(answers) ? answers : [];
  const earlier = Array.isArray(intake?.earlier) ? intake.earlier : [];
  if (own.length === 0 && earlier.length === 0) return null;
  const taken = intake?.intake || {};

  return (
    <div className="space-y-6">
      {own.length > 0 && (
        <IntakeRecord
          title="Medical history"
          takenBy={taken.completedByName}
          takenAt={taken.completedAt}
          answers={own}
          alerts={alerts}
        />
      )}
      {earlier.map((r) => (
        <IntakeRecord
          key={r.id}
          title="Earlier record"
          source={r.mergedFromMrn}
          takenBy={r.completedByName}
          takenAt={r.completedAt}
          answers={Array.isArray(r.answers) ? r.answers : []}
          alerts={alerts}
        />
      ))}
    </div>
  );
};

// One intake record: what was declared, then the whole of it folded away.
const IntakeRecord = ({ title, source, takenBy, takenAt, answers, alerts = [] }) => {
  const [showAll, setShowAll] = useState(false);
  if (!answers || answers.length === 0) return null;

  const isYes = (a) => String(a.answer || '').toUpperCase() === 'YES';
  // The badge has to mean "there is an alert for this", not "this question is mapped to raise
  // one". A record taken before the mapping existed raised nothing, and a doctor may since have
  // removed the alert; in both cases claiming otherwise would be a lie on a clinical screen.
  const onAlerts = (a) => !!a.alertType && (alerts || []).some((al) => al.alertType === a.alertType);
  const notable = answers.filter((a) => a.serious && isYes(a));

  const answerLabel = (a) => {
    const v = String(a.answer || '');
    if (v.toUpperCase() === 'YES') return 'Yes';
    if (v.toUpperCase() === 'NO') return 'No';
    if (v.toUpperCase() === 'NOT_KNOWN') return 'Not known';
    return v;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
            {title}
          </h4>
          {source && (
            <p className="text-xs text-gray-500 mt-0.5">
              From <span className="font-mono">{source}</span>, a file since merged into this one
            </p>
          )}
        </div>
        <span className="text-xs text-gray-500">
          {takenBy ? `Taken by ${takenBy}` : 'Taken at intake'}
          {takenAt ? ` · ${fmtShortDate(takenAt)}` : ''}
        </span>
      </div>

      {notable.length === 0 ? (
        <div className="text-sm text-gray-600 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
          Nothing significant was declared at intake.
        </div>
      ) : (
        <div className="border border-amber-200 rounded-xl overflow-hidden bg-amber-50/40">
          <ul className="divide-y divide-amber-100">
            {notable.map((a, i) => (
              <li key={a.questionId || i} className="px-3 py-2 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <span className="text-sm font-medium text-gray-900">{a.labelEnglish}</span>
                  {a.detail && <span className="text-sm text-gray-700">: {a.detail}</span>}
                  {onAlerts(a) && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-100 text-rose-700">
                      On alerts
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={() => setShowAll((v) => !v)}
        className="text-sm text-emerald-600 hover:text-emerald-700 font-medium hover:underline"
      >
        {showAll ? 'Hide the full intake' : `Show the full intake (${answers.length} answers)`}
      </button>

      {showAll && (
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-xs font-semibold text-gray-600 uppercase">
                <th className="px-3 py-2 text-left">Question</th>
                <th className="px-3 py-2 text-left whitespace-nowrap">Answer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {answers.map((a, i) => (
                <tr key={a.questionId || i} className="align-top">
                  <td className="px-3 py-2">
                    {/* The Urdu the patient was actually asked, kept with the answer. */}
                    <p className="urdu-text text-gray-900" dir="rtl" style={{ lineHeight: 2.2 }}>
                      {a.textUrdu}
                    </p>
                    <p className="text-xs text-gray-500">{a.labelEnglish}</p>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`font-medium ${isYes(a) && a.serious ? 'text-amber-700' : 'text-gray-800'}`}>
                      {answerLabel(a)}
                    </span>
                    {a.detail && <div className="text-xs text-gray-600">{a.detail}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// =================== PATIENT INVOICES TAB ===================
/**
 * Token fees and invoices as rows of one ledger, grouped by day, newest day first.
 *
 * A plain function rather than a hook: it is pure, it is called after the tab's early returns, and
 * a useMemo there is a conditionally-called hook - React renders a different number of hooks once
 * the data arrives and the component blows up. eslint catches that as an error, which is the only
 * reason this is not a bug in the shipped build.
 */
const buildLedger = (paidVisits, invoices) => {
    const rows = [
      ...paidVisits.map(v => {
        const fee = Number(v.finalFee || 0);
        const refunded = Number(v.refundedAmount || 0);
        return {
          kind: 'token',
          key: `t-${v.visitId || `${v.visitDate}-${v.tokenNumber}`}`,
          date: v.visitDate,
          ref: v.tokenPrefix ? `${v.tokenPrefix}-${v.tokenNumber}` : String(v.tokenNumber ?? '—'),
          detail: [v.serviceName, v.doctorName].filter(Boolean).join(' · '),
          total: fee,
          // A refunded fee was collected and given back, so it counts as neither.
          paid: fee - refunded,
          refunded,
          balance: 0, // a token is paid at the desk; there is no balance to carry
          status: refunded > 0 ? 'Refunded' : (PAYMENT_MODE_LABEL[v.paymentMode] || v.paymentMode || '—'),
          statusClass: refunded > 0
            ? 'bg-rose-50 text-rose-700 border-rose-200'
            : 'bg-gray-50 text-gray-600 border-gray-200',
          cancelled: false,
        };
      }),
      ...invoices.map(i => {
        const total = Number(i.grandTotal) || 0;
        const paid = Number(i.amountPaid) || 0;
        const items = i.items?.length || 0;
        return {
          kind: 'invoice',
          key: `i-${i.id}`,
          // The day the bill was completed, so it files under the day the money moved. A bill that
          // has not been completed has no such day and still has to appear here for its balance to
          // be visible, so that one falls back to the day it was raised.
          date: i.paidAt || i.invoiceDate || i.createdAt,
          ref: i.invoiceNumber || '—',
          detail: items > 0 ? `${items} item${items === 1 ? '' : 's'}` : '',
          total,
          paid,
          refunded: 0,
          balance: i.status === 'CANCELLED'
            ? 0
            : (i.amountPending != null ? Number(i.amountPending) || 0 : total - paid),
          status: i.status || 'DRAFT',
          statusClass: INVOICE_STATUS_COLOR[i.status] || INVOICE_STATUS_COLOR.DRAFT,
          cancelled: i.status === 'CANCELLED',
        };
      }),
    ];

    // Grouped on the date STRING, never a parsed Date: these are date-only values and parsing them
    // lands on the previous day west of UTC.
    const byDay = new Map();
    for (const r of rows) {
      const day = String(r.date || '').slice(0, 10);
      if (!byDay.has(day)) byDay.set(day, []);
      byDay.get(day).push(r);
    }
    return [...byDay.entries()]
      .sort((a, b) => (a[0] < b[0] ? 1 : a[0] > b[0] ? -1 : 0)) // newest day first
      .map(([day, items]) => ({
        day,
        // Token before invoice within a day: the fee is taken on arrival, the bill after.
        items: items.slice().sort((a, b) => (a.kind === b.kind ? 0 : a.kind === 'token' ? -1 : 1)),
        collected: items.reduce((sum, r) => sum + (r.cancelled ? 0 : r.paid), 0),
      }));
};

const INVOICE_STATUS_COLOR = {
  DRAFT:     'bg-gray-100 text-gray-700 border-gray-200',
  FINALIZED: 'bg-amber-100 text-amber-700 border-amber-200',
  PAID:      'bg-emerald-100 text-emerald-700 border-emerald-200',
  CANCELLED: 'bg-gray-200 text-gray-600 border-gray-300',
};
// =================== PATIENT RADIOLOGY REPORTS TAB ===================
// Surfaces all imaging-type documents uploaded for this patient: X-rays, CT scans, MRIs,
// ultrasounds, ECGs. Reuses the existing patientDocumentAPI and filters client-side — no
// new backend endpoint needed since the doc-type enum already distinguishes these.
const RADIOLOGY_DOC_TYPES = new Set(['XRAY', 'CT_SCAN', 'MRI', 'ULTRASOUND', 'ECG']);
const RADIOLOGY_TYPE_LABEL = {
  XRAY: 'X-Ray', CT_SCAN: 'CT Scan', MRI: 'MRI', ULTRASOUND: 'Ultrasound', ECG: 'ECG',
};

// Exported for reuse in the Prescription module header ("Radiology" link).
export const PatientRadiologyReportsTab = ({ patientId }) => {
  const { entries, loading, error } =
    useTabList(() => patientDocumentAPI.getByPatient(patientId), [patientId]);

  if (loading) return <div className="text-center text-gray-500 py-12">Loading radiology reports…</div>;
  if (error)   return <div className="text-center text-red-600 py-12">{error}</div>;

  const radiologyDocs = (entries || []).filter(d => RADIOLOGY_DOC_TYPES.has(d.documentType));

  if (radiologyDocs.length === 0) {
    return <TabEmptyState
      icon={<Image className="w-12 h-12 mx-auto text-gray-300" />}
      title="No radiology reports yet"
      message="X-rays, CT scans, MRIs, ultrasounds and ECG reports will appear here. Upload them under the Documents tab." />;
  }

  const visible = radiologyDocs;

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
        <span className="font-semibold">{radiologyDocs.length}</span> radiology report{radiologyDocs.length === 1 ? '' : 's'}.
      </div>
      <DocReportsTable patientId={patientId} docs={visible} typeLabel={(t) => RADIOLOGY_TYPE_LABEL[t] || t} />
    </div>
  );
};

// Table of uploaded report documents (lab / radiology) — same columns and styling as the
// Documents tab table so every file list in the patient modal reads identically. `typeLabel`
// maps a documentType to its display label.
const DocReportsTable = ({ patientId, docs, typeLabel }) => {
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
  const authToken = localStorage.getItem('clinic_auth_token');
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">
            <th className="px-3 py-2.5 w-14"></th>
            <th className="px-3 py-2.5">Name</th>
            <th className="px-3 py-2.5">Type</th>
            <th className="px-3 py-2.5">Uploaded</th>
            <th className="px-3 py-2.5 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {docs.map(doc => {
            const isPdf = doc.contentType === 'application/pdf';
            const docDateFmt = doc.documentDate ? fmtShortDate(doc.documentDate) : null;
            const nameTooltip = [
              doc.fileName,
              doc.description ? `Description: ${doc.description}` : null,
              docDateFmt ? `Document date: ${docDateFmt}` : null,
              doc.fileSizeDisplay ? `Size: ${doc.fileSizeDisplay}` : null,
            ].filter(Boolean).join('\n');
            return (
              <tr key={doc.id} className="hover:bg-gray-50/60">
                <td className="px-3 py-2">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                    {doc.hasThumbnail ? (
                      <img
                        src={authedMediaUrl(`${apiBase}/patients/${patientId}/documents/${doc.id}/thumbnail`)}
                        alt={doc.fileName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center text-gray-400 w-full h-full">
                        {isPdf ? <FileText className="h-5 w-5" /> : <Image className="h-5 w-5" />}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 max-w-xs">
                  <div className="font-medium text-gray-900 truncate" title={nameTooltip}>{doc.fileName}</div>
                  {doc.description && (
                    <div className="text-xs text-gray-500 truncate" title={nameTooltip}>{doc.description}</div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">
                    {typeLabel(doc.documentType)}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {formatClinicDateTime(doc.uploadedAt, { withZone: true })}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <a
                    href={`${apiBase}/patients/${patientId}/documents/${doc.id}/download?token=${authToken}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-emerald-600 hover:text-emerald-700 inline-flex"
                    title="View"
                  >
                    <Eye className="h-4 w-4" />
                  </a>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Exported for reuse in the Prescription module header ("Lab Reports" link). Shows uploaded
// lab-report DOCUMENTS (documentType LAB_REPORT) — distinct from the patient modal's "Lab Tests"
// tab, which lists ordered tests. Mirrors PatientRadiologyReportsTab.
export const PatientLabReportsTab = ({ patientId }) => {
  const { entries, loading, error } =
    useTabList(() => patientDocumentAPI.getByPatient(patientId), [patientId]);

  if (loading) return <div className="text-center text-gray-500 py-12">Loading lab reports…</div>;
  if (error)   return <div className="text-center text-red-600 py-12">{error}</div>;

  const labDocs = (entries || []).filter(d => d.documentType === 'LAB_REPORT');

  if (labDocs.length === 0) {
    return <TabEmptyState
      icon={<FlaskConical className="w-12 h-12 mx-auto text-gray-300" />}
      title="No lab reports yet"
      message="Uploaded lab-report documents will appear here. Upload them under the Documents tab and tag them as 'Lab Report'." />;
  }

  const visible = labDocs;

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
        <span className="font-semibold">{labDocs.length}</span> lab report{labDocs.length === 1 ? '' : 's'}.
      </div>
      <DocReportsTable patientId={patientId} docs={visible} typeLabel={() => 'Lab Report'} />
    </div>
  );
};

const PatientInvoicesTab = ({ patientId }) => {
  const { entries, loading, error } =
    useTabList(() => invoiceAPI.getByPatient(patientId), [patientId]);
  // Token fees never become invoices: they are collected at intake and live on the visit, so a tab
  // showing only invoices showed only the money taken AFTER the consultation. Fetched separately
  // and shown as its own section rather than merged into the invoice table, because the two are
  // genuinely different documents with different numbers on them.
  const [visits, setVisits] = useState(null);
  useEffect(() => {
    let alive = true;
    visitAPI.getPatientHistoryWithRx(patientId)
      .then(v => { if (alive) setVisits(Array.isArray(v) ? v : []); })
      .catch(() => { if (alive) setVisits([]); });
    return () => { alive = false; };
  }, [patientId]);
  // Credit held in the patient's name (from referrals) — shown here since this tab is the
  // money view. Applies automatically at the next token issuance.
  // Which day groups are folded shut. A Set of the day keys, empty by default so everything opens
  // expanded; a patient with a long history can then fold away the days they are not looking at.
  // Declared up here with the other hooks, above the early returns.
  const [collapsedDays, setCollapsedDays] = useState(() => new Set());
  const toggleDay = (day) => setCollapsedDays(prev => {
    const next = new Set(prev);
    if (next.has(day)) next.delete(day); else next.add(day);
    return next;
  });
  const [credit, setCredit] = useState(null);
  useEffect(() => {
    let alive = true;
    referralAPI.getCredit(patientId)
      .then(c => { if (alive) setCredit(c); })
      .catch(() => { if (alive) setCredit(null); });
    return () => { alive = false; };
  }, [patientId]);

  const creditBanner = credit && Number(credit.balance || 0) > 0 && (
    <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3">
      <span className="text-sm font-medium text-violet-800">
        Credit in this patient's name (offered automatically at the next token)
      </span>
      <span className="text-lg font-bold text-violet-800">Rs. {Number(credit.balance).toLocaleString()}</span>
    </div>
  );

  if (loading) return <div className="text-center text-gray-500 py-12">Loading invoices…</div>;
  if (error)   return <div className="text-center text-red-600 py-12">{error}</div>;
  // Nothing at all means no invoices AND no token fees. A patient who has only ever paid at the
  // desk still has a money history, and this used to tell them they had none.
  const hasTokenFees = (visits || []).some(
    v => v.visitStatus !== 'CANCELLED' && Number(v.finalFee || 0) > 0);
  if ((!entries || entries.length === 0) && !hasTokenFees) {
    return (
      <div>
        {creditBanner}
        <TabEmptyState
          icon={<Wallet className="w-12 h-12 mx-auto text-gray-300" />}
          title="Nothing billed yet"
          message="Token fees and invoices for this patient will appear here." />
      </div>
    );
  }

  // Stable sort by invoice date desc (defensive — server should already return ordered).
  const sorted = [...entries].sort((a, b) => {
    const da = new Date(a.invoiceDate || a.createdAt || 0).getTime();
    const db = new Date(b.invoiceDate || b.createdAt || 0).getTime();
    return db - da;
  });
  const visible = sorted;

  // A cancelled token collected no money, and a refunded one gave it back.
  const paidVisits = (visits || []).filter(
    v => v.visitStatus !== 'CANCELLED' && Number(v.finalFee || 0) > 0);
  const tokenFeesPaid = paidVisits.reduce(
    (s, v) => s + Number(v.finalFee || 0) - Number(v.refundedAmount || 0), 0);

  const invoicesPaid = sorted.reduce((s, i) => s + (Number(i.amountPaid) || 0), 0);
  // One ledger: token fees and invoices as rows of the same list, grouped by the day they fall on.
  const ledger = buildLedger(paidVisits, sorted);
  const totalPaid = invoicesPaid + tokenFeesPaid;
  // grandTotal, not totalAmount: the API has never returned a field by that name, so every Total
  // read as 0 and Outstanding came out NEGATIVE (0 minus whatever had been paid). amountPending is
  // the server's own figure, so the balance cannot drift from what the invoice screen shows.
  const totalDue = sorted
    .filter(i => i.status !== 'CANCELLED')
    .reduce((s, i) => s + (i.amountPending != null
      ? Number(i.amountPending) || 0
      : (Number(i.grandTotal) || 0) - (Number(i.amountPaid) || 0)), 0);

  return (
    <div className="space-y-4">
      {creditBanner}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl p-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">Invoices &middot; Tokens</div>
          <div className="text-xl font-bold text-gray-900">{entries.length} &middot; {paidVisits.length}</div>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Total Paid</div>
          <div className="text-xl font-bold text-emerald-800">Rs. {totalPaid.toLocaleString()}</div>
          {/* Split out, because "why is this bigger than the invoices?" is the obvious question. */}
          {tokenFeesPaid > 0 && entries.length > 0 && (
            <div className="text-[10px] text-emerald-700 mt-0.5">
              Rs. {invoicesPaid.toLocaleString()} billed + Rs. {tokenFeesPaid.toLocaleString()} token fees
            </div>
          )}
        </div>
        <div className={`${totalDue > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'} border rounded-xl p-3`}>
          <div className={`text-[10px] font-semibold uppercase tracking-wider ${totalDue > 0 ? 'text-red-700' : 'text-gray-500'}`}>Outstanding</div>
          <div className={`text-xl font-bold ${totalDue > 0 ? 'text-red-800' : 'text-gray-700'}`}>Rs. {totalDue.toLocaleString()}</div>
        </div>
      </div>
      {/* Fold the lot at once. With a long history the point of collapsing is to skim the days and
          open one, and doing that a group at a time is worse than scrolling. */}
      {ledger.length > 1 && (
        <div className="flex justify-end -mb-1">
          <button
            type="button"
            onClick={() => setCollapsedDays(prev =>
              prev.size === ledger.length ? new Set() : new Set(ledger.map(g => g.day)))}
            className="text-xs font-medium text-gray-500 hover:text-gray-700"
          >
            {collapsedDays.size === ledger.length ? 'Expand all' : 'Collapse all'}
          </button>
        </div>
      )}

      {/* ONE ledger, grouped by day. The two lists used to be separate tabs, which answered
          "show me the invoices" but never "what did this patient pay on the 15th" - and that is
          the question a day's money actually raises, because a token fee and the invoice from the
          same visit belong together. Tokens sit above invoices within a day: the fee is taken at
          the desk on arrival, the bill is raised after the consultation. */}
      {ledger.map(group => (
        <div key={group.day} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
          {/* The whole header is the toggle, not just the chevron: a 2px icon is a poor target for
              something clicked this often. The day's total stays visible while folded, so a
              collapsed group still answers what was taken that day. */}
          <button
            type="button"
            onClick={() => toggleDay(group.day)}
            aria-expanded={!collapsedDays.has(group.day)}
            className="w-full flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 border-b border-gray-200 hover:bg-gray-100 transition-colors text-left"
          >
            <span className="flex items-center gap-1.5 min-w-0">
              <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${
                collapsedDays.has(group.day) ? '-rotate-90' : ''
              }`} />
              <span className="text-sm font-semibold text-gray-800">{fmtShortDate(group.day)}</span>
              {/* Only while folded: open, the rows say this themselves. */}
              {collapsedDays.has(group.day) && (
                <span className="text-xs text-gray-400">
                  · {group.items.length} record{group.items.length === 1 ? '' : 's'}
                </span>
              )}
            </span>
            {group.collected > 0 && (
              <span className="text-xs text-gray-500 flex-shrink-0">
                collected <span className="font-semibold text-emerald-700">Rs. {group.collected.toLocaleString()}</span>
              </span>
            )}
          </button>
          {!collapsedDays.has(group.day) && (
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-100">
              {group.items.map(r => (
                <tr
                  key={r.key}
                  className={`transition-colors ${
                    r.kind === 'token' ? 'hover:bg-blue-50/70' : 'hover:bg-emerald-50/70'
                  } ${r.cancelled ? 'opacity-60' : ''}`}
                >
                  {/* Which document this is. The two are numbered differently and mean different
                      things, so the row says which rather than leaving the reference to imply it. */}
                  <td className="px-3 py-2.5 w-[1%] whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      r.kind === 'token'
                        ? 'bg-blue-50 text-blue-700 border border-blue-100'
                        : 'bg-emerald-50 text-emerald-700 border border-emerald-100'
                    }`}>
                      {r.kind === 'token' ? 'Token' : 'Invoice'}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 font-mono text-xs whitespace-nowrap text-gray-700">{r.ref}</td>
                  <td className="px-3 py-2.5 text-gray-700">
                    {r.detail || <span className="text-gray-300">&mdash;</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    <span className={r.refunded > 0 ? 'text-gray-400 line-through' : 'font-medium text-gray-900'}>
                      Rs. {r.total.toLocaleString()}
                    </span>
                    {r.refunded > 0 && (
                      <div className="text-[10px] font-semibold text-rose-600">
                        refunded Rs. {r.refunded.toLocaleString()}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap text-emerald-700">
                    {r.paid > 0 ? `Rs. ${r.paid.toLocaleString()}` : <span className="text-gray-300">&mdash;</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {r.balance > 0
                      ? <span className="font-medium text-red-700">Rs. {r.balance.toLocaleString()}</span>
                      : <span className="text-gray-300">&mdash;</span>}
                  </td>
                  <td className="px-3 py-2.5 text-center whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${r.statusClass}`}>
                      {r.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>
      ))}
    </div>
  );
};

// Single key/value chip on the "Most Recent Reading" card. Faded styling for missing values
// and a red alert ring for out-of-range readings to draw the eye.
const VitalChip = ({ label, value, unit, alert = false }) => {
  const isEmpty = value === null || value === undefined || value === '';
  return (
    <div className={`rounded-xl px-3 py-2 ${
      isEmpty
        ? 'bg-white/50 border border-gray-100'
        : alert
          ? 'bg-red-50 border-2 border-red-300'
          : 'bg-white border border-emerald-100'
    }`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`text-lg font-bold ${isEmpty ? 'text-gray-300' : alert ? 'text-red-700' : 'text-gray-900'}`}>
        {isEmpty ? '—' : value}
      </div>
      {!isEmpty && unit && <div className="text-[10px] text-gray-400">{unit}</div>}
    </div>
  );
};

// Map of tab metadata for the placeholder rendering. Kept in sync with the tab button
// list above — same labels + icons so the empty state matches what the user clicked.
const PLACEHOLDER_TAB_META = {
  'visits':           { label: 'Visits',             icon: <Calendar       className="h-8 w-8" />, hint: 'Chronological list of past tokens and visits with date, doctor, and status.' },
  'vitals':           { label: 'Vitals',             icon: <Activity       className="h-8 w-8" />, hint: 'Trend of BP, pulse, temperature, weight and other vitals across visits.' },
  'radiology-reports': { label: 'Radiology Reports', icon: <Image          className="h-8 w-8" />, hint: 'X-rays, CT scans, MRIs, ultrasounds and ECG reports uploaded for this patient.' },
  'clinical-notes':   { label: 'Clinical Notes',     icon: <ClipboardList  className="h-8 w-8" />, hint: 'Doctor\'s clinical observations and notes from past visits.' },
  'prescriptions':    { label: 'Prescriptions',      icon: <Stethoscope    className="h-8 w-8" />, hint: 'All prescriptions issued to this patient, in chronological order.' },
  'medicines':        { label: 'Medicines',          icon: <Pill           className="h-8 w-8" />, hint: 'Every medicine ever prescribed to this patient, aggregated across all visits.' },
  'lab-tests':        { label: 'Lab Tests',          icon: <FlaskConical   className="h-8 w-8" />, hint: 'Lab tests ordered or performed for this patient and their results.' },
  'medical-history':  { label: 'Medical History',    icon: <Heart          className="h-8 w-8" />, hint: 'Chronic conditions, allergies, and medical alerts on file.' },
  'invoices':         { label: 'Invoices',           icon: <Wallet         className="h-8 w-8" />, hint: 'Billing history - invoices, fees, payments, and outstanding balance.' },
};

// =================== LAB REPORT TESTS PICKER ===================
// Renders inside the Documents tab's upload card when the user picks "Lab Report" as the
// document type. Lets the user (a) flag the report as "external/not from PAMH" — no test
// linkage; or (b) tick which ordered tests this upload is the result for. Selected tests
// are auto-marked completed by the backend on upload.
const LabReportTestsPicker = ({
  tests, loading, selectedIds, setSelectedIds,
  isExternal, setIsExternal, showCompleted, setShowCompleted,
}) => {
  const pending = (tests || []).filter(t => !t.isCompleted);
  const completed = (tests || []).filter(t => t.isCompleted);

  const toggle = (id) => {
    if (isExternal) return; // selection ignored when external
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllPending = () => {
    if (isExternal) return;
    setSelectedIds(new Set(pending.map(t => t.prescriptionLabTestId)));
  };
  const clearSelection = () => setSelectedIds(new Set());

  const allPendingSelected = pending.length > 0 && pending.every(t => selectedIds.has(t.prescriptionLabTestId));
  const fmt = (d) => formatClinicDate(d, { day: '2-digit', month: 'short', year: 'numeric' }, '');

  return (
    <div className="mt-4 bg-white rounded-xl border border-emerald-200 p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-emerald-600" />
            This report covers
          </h4>
          <p className="text-xs text-gray-500 mt-0.5">
            Tick the ordered tests this upload is the result for - each will be marked completed automatically.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
          <input
            type="checkbox"
            checked={isExternal}
            onChange={(e) => { setIsExternal(e.target.checked); if (e.target.checked) setSelectedIds(new Set()); }}
            className="rounded border-gray-300 focus:ring-emerald-500"
          />
          <span>Not from PAMH (external lab report)</span>
        </label>
      </div>

      {isExternal ? (
        <div className="text-xs text-gray-500 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
          External report - will be saved as a lab document on this patient's record without
          linking to any ordered test.
        </div>
      ) : loading ? (
        <div className="text-xs text-gray-500 py-2">Loading ordered tests…</div>
      ) : !tests || tests.length === 0 ? (
        <div className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-3">
          No lab tests have been ordered for this patient. You can still upload this report.
          Tick "Not from PAMH" above to save it as an external report.
        </div>
      ) : (
        <>
          {pending.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-600">
                  Pending ({pending.length})
                </span>
                <div className="flex items-center gap-2">
                  {selectedIds.size > 0 && (
                    <button
                      type="button"
                      onClick={clearSelection}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Clear ({selectedIds.size})
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={allPendingSelected ? clearSelection : selectAllPending}
                    className="text-xs text-emerald-700 hover:text-emerald-800 font-medium"
                  >
                    {allPendingSelected ? 'Deselect all' : 'Select all pending'}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                {pending.map(t => {
                  const checked = selectedIds.has(t.prescriptionLabTestId);
                  return (
                    <label
                      key={t.prescriptionLabTestId}
                      className={`flex items-start gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                        checked ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(t.prescriptionLabTestId)}
                        className="mt-0.5 rounded border-gray-300 focus:ring-emerald-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900">{t.testName}</div>
                        <div className="text-xs text-gray-500">
                          {fmt(t.prescriptionDate)}
                          {t.doctorName && ` · by ${t.doctorName}`}
                          {t.urgency && t.urgency !== 'ROUTINE' && (
                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-semibold">
                              {t.urgency}
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </>
          )}

          {pending.length === 0 && (
            <div className="text-xs text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
              All ordered tests for this patient are already completed. Tick "Not from PAMH" to upload
              an external report, or attach this report to a completed test below.
            </div>
          )}

          {completed.length > 0 && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setShowCompleted(s => !s)}
                className="text-xs text-gray-500 hover:text-gray-700 font-medium inline-flex items-center gap-1"
              >
                {showCompleted ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {showCompleted ? 'Hide' : 'Show'} completed tests ({completed.length})
              </button>
              {showCompleted && (
                <div className="space-y-1.5 mt-2 max-h-40 overflow-y-auto pr-1">
                  {completed.map(t => {
                    const checked = selectedIds.has(t.prescriptionLabTestId);
                    return (
                      <label
                        key={t.prescriptionLabTestId}
                        className={`flex items-start gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-colors opacity-75 ${
                          checked ? 'bg-emerald-50 border border-emerald-200' : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(t.prescriptionLabTestId)}
                          className="mt-0.5 rounded border-gray-300 focus:ring-emerald-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-gray-700">{t.testName}</div>
                          <div className="text-xs text-gray-400">
                            {fmt(t.prescriptionDate)}
                            {t.documentIds && t.documentIds.length > 0 && (
                              <span className="ml-2 text-emerald-600">
                                · {t.documentIds.length} report{t.documentIds.length === 1 ? '' : 's'} on file
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {selectedIds.size === 0 && !isExternal && pending.length > 0 && (
            <p className="flex items-start gap-1.5 text-xs text-amber-700 mt-3">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>No tests selected. The report will be uploaded but won't be linked to any test.
              Tick the tests above or "Not from PAMH" to mark this intentional.</span>
            </p>
          )}
        </>
      )}
    </div>
  );
};

const PatientTabPlaceholder = ({ tabKey }) => {
  const meta = PLACEHOLDER_TAB_META[tabKey] || { label: 'Coming soon', icon: <History className="h-8 w-8" />, hint: '' };
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center max-w-md">
        <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-emerald-100 to-teal-100 text-emerald-600 rounded-2xl mb-5">
          {meta.icon}
        </div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">{meta.label}</h3>
        <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 border border-amber-200 text-amber-700 rounded-full text-xs font-semibold mb-4">
          <Sparkles className="h-3.5 w-3.5" />
          Coming soon
        </div>
        {meta.hint && (
          <p className="text-sm text-gray-500 leading-relaxed">{meta.hint}</p>
        )}
      </div>
    </div>
  );
};

// Beautiful Detail Row Component with Modern Styling
const DetailRow = ({ icon, label, value, highlight = false, isOptional = false }) => (
  <div className={`flex items-center justify-between gap-4 p-3.5 rounded-xl transition-all duration-200 hover:shadow-sm ${
    highlight
      ? 'bg-white bg-opacity-80 border border-blue-200 shadow-sm'
      : 'bg-white bg-opacity-40'
  }`}>
    {/* Left: icon + label */}
    <div className="flex items-center gap-3 min-w-0">
      <div className="flex-shrink-0">{icon}</div>
      <span className="text-sm font-medium text-gray-600 whitespace-nowrap">{label}</span>
    </div>
    {/* Right: value, right-aligned to the row's edge so it doesn't float in empty space */}
    <span className={`font-semibold text-sm text-right break-words ${
      isOptional ? 'text-gray-400 italic' : 'text-gray-900'
    }`}>
      {value}
    </span>
  </div>
);

// Add Patient Modal Component
const AddPatientModal = ({
  formData, formErrors, touched, loading, isEditing = false,
  ruleOptions = { originalStatus: null, onFile: null }, duplicateCnic = null,
  existingPatient = null, cnicFile = null, setCnicFile = () => {},
  cnicBackFile = null, setCnicBackFile = () => {},
  guardianCnicFile = null, setGuardianCnicFile = () => {},
  guardianCnicBackFile = null, setGuardianCnicBackFile = () => {},
  profileImageFile = null, setProfileImageFile = () => {},
  removeProfileImage = false, setRemoveProfileImage = () => {},
  onFieldChange, onFieldsChange, onFieldBlur, onRevealField = () => {}, onSubmit, onClose, onReset, onUseExisting = () => {},
  buildPhone, parsePhone, calculateAge, isAdult
}) => {
  const originalStatus = ruleOptions.originalStatus;
  const patientFormDialogRef = useDialog(onClose);
  const lookalikeRef = useRef(null);
  const formRef = useRef(null);
  // Ids for the footer lines, so Register is described by the reason it is off.
  const blockedIdBase = useId();
  const phoneCountry = usePhoneCountryCode(formData.phoneNumber);

  // "Is this one of these people?" Asked of the backend as the form is filled in, not on submit:
  // a little after the last keystroke, and only for a new registration. An answer that arrives
  // after the form has moved on is dropped. The acknowledgement is keyed to the request that
  // produced the matches, so a form that changes enough to match someone else asks again.
  const isMinorForm = !!(formData.dateOfBirth && !isAdult(formData.dateOfBirth));
  const lookalikeQuery = isEditing ? null : lookalikeQueryFrom(formData, isMinorForm);
  const lookalikeQueryKey = lookalikeQuery ? JSON.stringify(lookalikeQuery) : '';
  const [lookalikes, setLookalikes] = useState([]);
  const [lookalikeKey, setLookalikeKey] = useState('');
  const [acknowledgedKey, setAcknowledgedKey] = useState('');
  const lookalikeSeq = useRef(0);
  useEffect(() => {
    if (!lookalikeQueryKey) { setLookalikes([]); setLookalikeKey(''); return undefined; }
    const seq = ++lookalikeSeq.current;
    const timer = setTimeout(async () => {
      let found = [];
      try {
        const result = await patientAPI.lookalikes(JSON.parse(lookalikeQueryKey));
        found = Array.isArray(result) ? result : [];
      } catch {
        found = []; // a check that failed must never hold a registration
      }
      if (seq !== lookalikeSeq.current) return;
      setLookalikes(found);
      setLookalikeKey(lookalikeQueryKey);
    }, 450);
    return () => clearTimeout(timer);
  }, [lookalikeQueryKey]);
  const certain = certainOf(lookalikes);
  // A patient already holding the CNIC typed. Only from an answer about the number now in the box,
  // so a corrected number is not held on the strength of the answer about the old one.
  const answeredCnic = lookalikeKey ? JSON.parse(lookalikeKey).cnic : null;
  const cnicTaken = answeredCnic && lookalikeQuery && answeredCnic === lookalikeQuery.cnic
    ? cnicTakenBy(lookalikes)
    : null;

  // Age entry when the date is unknown. The age fields are how it was typed; the date they imply
  // is what is validated and saved, flagged as an estimate. Switching modes clears the date, so an
  // estimate never lingers as an exact date or the other way round. Each is one update, so the date
  // is always judged together with the mode and the age it came from.
  const onAgeChange = ({ years, months }) => {
    onFieldsChange({ ageYears: years, ageMonths: months, dateOfBirth: dobFromAge({ years, months }) || '' });
  };
  // Switching between a date and an age is not a visit to the field: it opens empty, not red.
  const onAgeModeChange = (estimated) => {
    onFieldsChange({ dobEstimated: estimated, ageYears: '', ageMonths: '', dateOfBirth: '' }, { touched: false });
  };
  // A repeated CNIC cannot be waved through like other matches: the server refuses it every time.
  const mustAcknowledge = !isEditing && (
    !!cnicTaken || (needsAcknowledgement(lookalikes) && acknowledgedKey !== lookalikeKey)
  );

  // What the save needs that the form does not have yet: every field a rule holds it on, touched or
  // not, in the order they sit on screen. The same list the save checks, so the button, the line
  // under it and the save cannot disagree. It used to be built from the messages already showing,
  // which only existed for fields that had been visited, so a fresh form had Register switched on
  // and the count went up as the receptionist worked through it.
  const blockers = patientFormBlockers(formData, ruleOptions);
  // Marks a field visited, so its own message shows, then takes focus to it.
  const goToField = (field) => {
    onRevealField(field);
    return focusFieldByName(formRef.current, field);
  };
  const goToFirstBlocker = () => (blockers[0] ? goToField(blockers[0].field) : false);
  const reviewLookalikes = () => {
    const el = lookalikeRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const confirm = el.querySelector('button');
    if (confirm) confirm.focus({ preventScroll: true });
  };

  // The field the cursor is in (its data-field), so the footer can tell a value still being typed
  // from one typed wrong and left (see whileTyping).
  const [focusedField, setFocusedField] = useState(null);

  // Why Register is unavailable, said in the footer where the button itself is. A look-alike comes
  // first: it is the surprising one, because the receptionist may have filled the form correctly
  // and the block comes from a record they have not seen. Then a CNIC the server has already refused
  // as someone else's. Then the fields, from the moment the form opens: amber while they are only
  // empty (or still being typed), red once a value is typed wrong and left.
  const blocked = [];
  if (mustAcknowledge) {
    const sure = cnicTaken || certain;
    blocked.push({
      key: 'lookalike',
      tone: sure ? 'red' : 'amber',
      text: cnicTaken
        ? `This CNIC is already on ${cnicTaken.fullName}'s file. Check the number, or open their file.`
        : sure
          ? `This ${sure.rules.includes('SAME_BFORM') ? 'B-Form' : 'CNIC'} is already on ${sure.fullName}'s file.`
          : `${lookalikes.length === 1 ? '1 patient looks' : `${lookalikes.length} patients look`} like this one. Register stays off until you say it is not one of them.`,
      action: 'Review',
      onAct: reviewLookalikes,
    });
  }
  if (duplicateCnic) {
    blocked.push({
      key: 'duplicate',
      tone: 'red',
      text: duplicateCnic.message,
      action: 'Take me there',
      onAct: () => goToField('cnic'),
    });
  }
  const fieldsLine = describeBlockers(whileTyping(blockers, focusedField));
  if (fieldsLine) {
    blocked.push({ key: 'fields', ...fieldsLine, action: 'Take me there', onAct: goToFirstBlocker });
  }
  const saveHeld = loading || mustAcknowledge || !!duplicateCnic || blockers.length > 0;
  // A focused Register that turns off drops keyboard focus to the top of the page. Send it to the
  // first field the save still needs instead (the list's order); failing that, to the refused CNIC
  // or the look-alike panel.
  const goToFirstHold = () => {
    if (goToFirstBlocker()) return;
    if (duplicateCnic) { goToField('cnic'); return; }
    if (mustAcknowledge) reviewLookalikes();
  };

  return (
  <div ref={patientFormDialogRef} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[95vh] flex flex-col overflow-hidden">
      <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">
            {isEditing ? 'Edit Patient' : 'Register New Patient'}
          </h2>
          <p className="text-gray-500 text-sm">
            {isEditing ? 'Update the patient\'s information' : 'Enter patient information to create a new record'}
          </p>
        </div>
        <button
          aria-label="Close"
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
        >
          <X className="h-6 w-6 text-gray-500" />
        </button>
      </div>
      
      <form
        ref={formRef}
        onSubmit={(e) => {
          const form = e.currentTarget;
          // The matches on screen go with the registration, so the new file remembers its warning.
          onSubmit(e, isEditing ? [] : lookalikes);
          // A save that fails validation shows its errors on the next render; take focus to the first.
          requestAnimationFrame(() => focusFirstInvalidField(form));
        }}
        onKeyDown={keepEnterFromSubmitting}
        onFocus={(e) => setFocusedField(e.target.closest('[data-field]')?.getAttribute('data-field') || null)}
        onBlur={() => setFocusedField(null)}
        className="flex flex-col flex-1 min-h-0"
      >
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {/* Profile picture — file OR camera capture. Optional. Uploaded after the
            patient row is saved (post-create or post-update). Shows existing photo
            when editing. */}
        <ProfilePicturePicker
          file={profileImageFile}
          existingUrl={removeProfileImage ? null : authedMediaUrl(existingPatient?.profileImageUrl)}
          onChange={(f) => { setProfileImageFile(f); if (f) setRemoveProfileImage(false); }}
          onRemoveExisting={() => setRemoveProfileImage(true)}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          {/* First Name (the Title field is intentionally hidden from the patient UI) */}
          <div className="flex gap-3">
            <FormField field="firstName" label="First Name" required error={touched.firstName && formErrors.firstName} className="flex-1" hintUrdu="مریض کا پہلا نام">
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => onFieldChange('firstName', e.target.value)}
                onBlur={() => onFieldBlur('firstName')}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 transition-colors ${
                  touched.firstName && formErrors.firstName
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-emerald-500'
                }`}
                placeholder="Enter first name"
                maxLength={NAME_MAX_LENGTH}
              />
            </FormField>
          </div>

          {/* Last Name */}
          <FormField field="lastName" label="Last Name" required error={touched.lastName && formErrors.lastName} hintUrdu="مریض کا خاندانی نام">
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => onFieldChange('lastName', e.target.value)}
              onBlur={() => onFieldBlur('lastName')}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 transition-colors ${
                touched.lastName && formErrors.lastName
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:border-emerald-500'
              }`}
              placeholder="Enter last name"
              maxLength={NAME_MAX_LENGTH}
            />
          </FormField>

          {/* Phone Number */}
          <FormField
            field="phoneNumber"
            label="Phone Number"
            required
            error={touched.phoneNumber && formErrors.phoneNumber}
            hintUrdu="پہلے ملک منتخب کریں، پھر نمبر لکھیں"
          >
            {(() => {
              const { localDigits } = parsePhone(formData.phoneNumber);
              const countryCode = phoneCountry.code;
              return (
                <div className={`flex items-stretch border rounded-lg focus-within:ring-2 transition-colors ${
                  touched.phoneNumber && formErrors.phoneNumber
                    ? 'border-red-500 focus-within:ring-red-200 focus-within:border-red-500'
                    : 'border-gray-300 focus-within:ring-emerald-200 focus-within:border-emerald-500'
                }`}>
                  <CountryCodeSelect
                    value={countryCode}
                    onChange={(newCode) => { phoneCountry.pick(newCode); onFieldChange('phoneNumber', buildPhone(newCode, localDigits)); }}
                  />
                  <input
                    type="tel"
                    inputMode="numeric"
                    value={localDigits}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, '');
                      const next = buildPhone(countryCode, digits);
                      phoneCountry.typed(next);
                      onFieldChange('phoneNumber', next);
                    }}
                    onBlur={() => onFieldBlur('phoneNumber')}
                    className="flex-1 px-3 py-2 border-0 focus:outline-none focus:ring-0 rounded-r-lg"
                    placeholder={countryCode === '92' ? '3001234567' : 'Phone number'}
                    maxLength={getPhoneLength(countryCode).max}
                  />
                </div>
              );
            })()}
          </FormField>

          {/* Date of Birth, or an age when the family does not know the date: the age becomes an
              estimated date (the middle of that year of life), flagged so the screen says "about 6"
              and the look-alike check matches it as a band rather than a day. */}
          {formData.dobEstimated ? (
            <FormField
              field="dateOfBirth"
              label="Age"
              required
              error={touched.dateOfBirth && formErrors.dateOfBirth}
              hintUrdu="عمر سال اور مہینوں میں لکھیں"
              hint={formData.dateOfBirth ? `Recorded as ${formatPatientAge({ dateOfBirth: formData.dateOfBirth, dobEstimated: true })}` : 'Date of birth not known'}
            >
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="120"
                  value={formData.ageYears}
                  onChange={(e) => onAgeChange({ years: e.target.value, months: formData.ageMonths })}
                  placeholder="Years"
                  aria-label="Age in years"
                  className={`w-24 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${touched.dateOfBirth && formErrors.dateOfBirth ? 'border-red-300' : 'border-gray-300'}`}
                />
                <span className="text-sm text-gray-500">years</span>
                <input
                  type="number"
                  min="0"
                  max="11"
                  value={formData.ageMonths}
                  onChange={(e) => onAgeChange({ years: formData.ageYears, months: e.target.value })}
                  placeholder="Months"
                  aria-label="Age in months"
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <span className="text-sm text-gray-500">months</span>
                <button
                  type="button"
                  onClick={() => onAgeModeChange(false)}
                  className="ml-auto text-sm font-medium text-emerald-700 hover:text-emerald-800"
                >
                  Enter the date instead
                </button>
              </div>
            </FormField>
          ) : (
            <FormField
              field="dateOfBirth"
              label="Date of Birth"
              required
              error={touched.dateOfBirth && formErrors.dateOfBirth}
              hintUrdu="تاریخ پیدائش۔ معلوم نہ ہو تو عمر لکھیں"
              hint={formData.dateOfBirth ? `Age: ${calculateAge(formData.dateOfBirth)}` : ''}
            >
              <DatePicker
                value={formData.dateOfBirth}
                onChange={(v) => onFieldChange('dateOfBirth', v)}
                max={clinicToday()}
                error={touched.dateOfBirth && !!formErrors.dateOfBirth}
                placeholder="Select date of birth"
              />
              <button
                type="button"
                onClick={() => onAgeModeChange(true)}
                className="mt-1 text-sm font-medium text-emerald-700 hover:text-emerald-800"
              >
                Date not known? Enter the age
              </button>
            </FormField>
          )}

          {/* Gender + Marital Status (marital status only for adults) */}
          <div className="flex gap-3">
            <FormField field="gender" label="Gender" required error={touched.gender && formErrors.gender} className="flex-1" hintUrdu="مرد، عورت یا دیگر">
              <Select
                value={formData.gender}
                onChange={(v) => onFieldChange('gender', v)}
                placeholder="--"
                options={[
                  { value: 'MALE', label: 'Male' },
                  { value: 'FEMALE', label: 'Female' },
                  { value: 'OTHER', label: 'Other' },
                ]}
                error={touched.gender && formErrors.gender ? ' ' : undefined}
                buttonClassName="px-3 py-2 rounded-lg"
              />
            </FormField>
            {(!formData.dateOfBirth || isAdult(formData.dateOfBirth)) && (
              <FormField label="Marital Status" className="flex-1" hintUrdu="شادی شدہ ہیں یا نہیں">
                <Select
                  value={formData.maritalStatus}
                  onChange={(v) => onFieldChange('maritalStatus', v)}
                  placeholder="--"
                  options={MARITAL_STATUSES}
                  buttonClassName="px-3 py-2 rounded-lg"
                />
              </FormField>
            )}
          </div>

          {/* B-Form: the child's own registration number, and the one certain identifier a child
              has (the look-alike check reads a repeat of it as a sure match). Rarely to hand in a
              rural family, so never required. It sits here, beside Gender, taking the slot an
              adult's CNIC occupies: it is the child's OWN number, the same kind of thing, and it
              read as a parent's detail while it sat inside the parents block. */}
          {formData.dateOfBirth && !isAdult(formData.dateOfBirth) && (
            <FormField
              field="bFormNumber"
              label="B-Form Number"
              error={touched.bFormNumber && formErrors.bFormNumber}
              hintUrdu="اگر خاندان کے پاس ہو۔ فارمیٹ: 42401-1234567-8"
            >
              <input
                type="text"
                value={formData.bFormNumber || ''}
                onChange={(e) => handleCnicInput(e, formData.bFormNumber || '', (v) => onFieldChange('bFormNumber', v))}
                onBlur={() => onFieldBlur('bFormNumber')}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 transition-colors ${
                  touched.bFormNumber && formErrors.bFormNumber
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-emerald-500'
                }`}
                placeholder="42401-1234567-8"
                maxLength={15}
              />
            </FormField>
          )}

          {/* CNIC — shown for adults (18+) and when DOB not yet entered */}
          {(!formData.dateOfBirth || isAdult(formData.dateOfBirth)) && (
            <FormField
              field="cnic"
              label="CNIC"
              required={!formData.dateOfBirth || isAdult(formData.dateOfBirth)}
              hintUrdu="شناختی کارڈ نمبر۔ فارمیٹ: 42401-1234567-8"
              error={touched.cnic && formErrors.cnic}
            >
              <input
                type="text"
                value={formData.cnic}
                onChange={(e) => handleCnicInput(e, formData.cnic, (v) => onFieldChange('cnic', v))}
                onBlur={() => onFieldBlur('cnic')}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 transition-colors ${
                  touched.cnic && formErrors.cnic
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-emerald-500'
                }`}
                placeholder="42401-1234567-8"
                maxLength={15}
              />
            </FormField>
          )}

          {/* CNIC document — front + back scan/photo. Staged here, uploaded after save. */}
          {(!formData.dateOfBirth || isAdult(formData.dateOfBirth)) && (
            <div className="md:col-span-2">
              <CnicDocUpload
                frontFile={cnicFile}
                backFile={cnicBackFile}
                onFront={setCnicFile}
                onBack={setCnicBackFile}
                existingFrontUrl={existingPatient?.cnicDocumentUrl}
                existingBackUrl={existingPatient?.cnicBackDocumentUrl}
              />
            </div>
          )}

          {/* A child is identified by their PARENTS, not by whoever walked in with them. The adult
              bringing a child changes from visit to visit; the parents do not, which is why the
              same child kept being registered twice under two different guardians. Since
              2026-09-20 both parents' names and one parent's CNIC are asked for outright, and the
              parent's CNIC is what links the child to a family. Who actually walked in with the
              child is NOT asked here: that changes from visit to visit, so it is collected when a
              token is issued (2026-09-23). */}
          {formData.dateOfBirth && !isAdult(formData.dateOfBirth) && (() => {
            const owner = formData.parentCnicOwner;
            const cnicField = owner === 'MOTHER' ? 'motherCnic' : 'fatherCnic';
            const cnicLabel = owner === 'MOTHER' ? "Mother's CNIC" : "Father's CNIC";
            // Two columns, not three: the parents' names read as a pair on the first row, and
            // "Whose CNIC is on file" sits on the second with the CNIC it asks for right beside
            // it. At three columns the dropdown shared a row with the names and its answer landed
            // underneath, away from the question.
            return (
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4 p-3 bg-emerald-50/40 border border-emerald-100 rounded-lg">
              <FormField
                field="fatherName"
                label="Father Name"
                required
                error={touched.fatherName && formErrors.fatherName}
                hintUrdu="بچے کے والد کا نام"
              >
                <input
                  type="text"
                  value={formData.fatherName}
                  onChange={(e) => onFieldChange('fatherName', e.target.value)}
                  onBlur={() => onFieldBlur('fatherName')}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 transition-colors ${
                    touched.fatherName && formErrors.fatherName
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:border-emerald-500'
                  }`}
                  placeholder="Enter father's name"
                  maxLength={100}
                />
              </FormField>

              <FormField
                field="motherName"
                label="Mother Name"
                required
                error={touched.motherName && formErrors.motherName}
                hintUrdu="بچے کی والدہ کا نام"
                hint={isEditing && !existingPatient?.motherName
                  ? 'Not on this file yet. It can be added whenever the family is next in.'
                  : undefined}
              >
                <input
                  type="text"
                  value={formData.motherName}
                  onChange={(e) => onFieldChange('motherName', e.target.value)}
                  onBlur={() => onFieldBlur('motherName')}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 transition-colors ${
                    touched.motherName && formErrors.motherName
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:border-emerald-500'
                  }`}
                  placeholder="Enter mother's name"
                  maxLength={100}
                />
              </FormField>

              {/* One parent CNIC is recorded, not two, and this picks which. It was labelled
                  "Whose CNIC is on file", which read as a question about records the clinic
                  already holds rather than a choice the receptionist is making now. The hint says
                  why it matters: that CNIC is what attaches the child to their family. */}
              <FormField
                field="parentCnicOwner"
                label="Which parent's CNIC"
                required
                error={touched.parentCnicOwner && formErrors.parentCnicOwner}
                hintUrdu="اسی شناختی کارڈ سے بچہ اپنے خاندان سے جڑتا ہے"
              >
                <Select
                  value={formData.parentCnicOwner}
                  onChange={(v) => onFieldChange('parentCnicOwner', v)}
                  placeholder="Father or Mother"
                  options={[{ value: 'FATHER', label: "Father" }, { value: 'MOTHER', label: "Mother" }]}
                  error={touched.parentCnicOwner && formErrors.parentCnicOwner ? ' ' : undefined}
                  buttonClassName="px-3 py-2 rounded-lg"
                />
              </FormField>

              {/* Only once the desk has said whose CNIC is on file. It used to sit here disabled,
                  reading "Choose whose CNIC first", which is a field asking to be filled while
                  refusing to be: the answer is one dropdown away and the field itself said so.
                  Which parent it belongs to, and the fact it is then required, both follow the
                  selection. Validation is keyed on that selection too, so nothing is demanded
                  while this is hidden. */}
              {owner && (
                <FormField
                  field={cnicField}
                  label={cnicLabel}
                  required
                  error={touched[cnicField] && formErrors[cnicField]}
                  hintUrdu="والد یا والدہ کا شناختی کارڈ۔ فارمیٹ: 42401-1234567-8"
                >
                  <input
                    type="text"
                    value={formData[cnicField] || ''}
                    onChange={(e) => handleCnicInput(e, formData[cnicField] || '', (v) => onFieldChange(cnicField, v))}
                    onBlur={() => onFieldBlur(cnicField)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 transition-colors ${
                      touched[cnicField] && formErrors[cnicField]
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-gray-300 focus:border-emerald-500'
                    }`}
                    placeholder="42401-1234567-8"
                    maxLength={15}
                  />
                </FormField>
              )}

              {/* The CNIC scan on file for a child. It belongs to the parent whose CNIC is above;
                  the column it is stored in is still called guardian_cnic_document_url, because
                  for 373 of QA's 392 children the guardian already WAS that parent. */}
              <div className="md:col-span-2">
                <CnicDocUpload
                  label={`${owner === 'MOTHER' ? "Mother's" : owner === 'FATHER' ? "Father's" : "Parent's"} CNIC Document`}
                  frontFile={guardianCnicFile}
                  backFile={guardianCnicBackFile}
                  onFront={setGuardianCnicFile}
                  onBack={setGuardianCnicBackFile}
                  existingFrontUrl={existingPatient?.guardianCnicDocumentUrl}
                  existingBackUrl={existingPatient?.guardianCnicBackDocumentUrl}
                />
              </div>
            </div>
            );
          })()}

          {/* Father Name vs Husband Name, for ADULTS. Husband Name takes over for adult women who
              are married or widowed, and Father Name is hidden in that case (Pakistani identity
              convention). A child's parents are both asked for in the panel above instead. */}
          {(() => {
            const isMinor = formData.dateOfBirth && !isAdult(formData.dateOfBirth);
            if (isMinor) return null;
            const showHusband =
              formData.gender === 'FEMALE'
              && (!formData.dateOfBirth || isAdult(formData.dateOfBirth))
              && (formData.maritalStatus === 'MARRIED' || formData.maritalStatus === 'WIDOWED');

            if (showHusband) {
              return (
                <FormField label="Husband Name" hintUrdu="شوہر کا نام">
                  <input
                    type="text"
                    value={formData.husbandName}
                    onChange={(e) => onFieldChange('husbandName', e.target.value)}
                    onBlur={() => onFieldBlur('husbandName')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Enter husband's name"
                    maxLength={100}
                  />
                </FormField>
              );
            }
            return (
              <FormField
                field="fatherName"
                label="Father Name"
                error={touched.fatherName && formErrors.fatherName}
                hintUrdu="والد کا نام"
              >
                <input
                  type="text"
                  value={formData.fatherName}
                  onChange={(e) => onFieldChange('fatherName', e.target.value)}
                  onBlur={() => onFieldBlur('fatherName')}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 transition-colors ${
                    touched.fatherName && formErrors.fatherName
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:border-emerald-500'
                  }`}
                  placeholder="Enter father's name"
                  maxLength={100}
                />
              </FormField>
            );
          })()}

        </div>

        {/* Address row — 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-x-4 gap-y-4">
          <FormField label="Address" hintUrdu="گھر، گلی یا گاؤں کا پتہ">
            <input
              type="text"
              value={formData.area}
              onChange={(e) => onFieldChange('area', e.target.value)}
              onBlur={() => onFieldBlur('area')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="House/Street, Village, etc."
            />
          </FormField>

          <FormField label="Town" hintUrdu="قصبہ یا چک نمبر">
            <input
              type="text"
              value={formData.town}
              onChange={(e) => onFieldChange('town', e.target.value)}
              onBlur={() => onFieldBlur('town')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="Shalimar Town, Chak No., etc."
            />
          </FormField>

          <FormField field="city" label="City" required error={touched.city && formErrors.city} hintUrdu="شہر کا نام">
            <input
              type="text"
              value={formData.city}
              onChange={(e) => onFieldChange('city', e.target.value)}
              onBlur={() => onFieldBlur('city')}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 transition-colors ${
                touched.city && formErrors.city
                  ? 'border-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:border-emerald-500'
              }`}
              placeholder="Lahore"
            />
          </FormField>
        </div>

        {/* Tags — multi-select from admin-managed list under Settings → Tags. Free-form
            entry was removed to avoid duplicates and drift. Capped at 2 per patient — the
            tag list is intentionally lean (area / VIP / referral source / etc.), and
            unbounded tagging makes the row chip strip noisy at scale.
            Hidden behind the patientTags feature flag (parked by business decision). */}
        {FEATURES.patientTags && (
          <div>
            <TagInput
              label="Tags"

              hintUrdu="مریض پر لگانے کے لیے نشان"              value={formData.tagNames || []}
              onChange={(names) => onFieldChange('tagNames', names)}
              placeholder="Click to pick tags"
              max={2}
            />
            <p className="text-xs text-gray-500 mt-1">
              Pick up to 2 tags. Don't see the tag you need? Add it under <span className="font-medium">Settings → Tags</span>.
            </p>
          </div>
        )}

        {/* Patient Status — edit-only. Defaults to whatever the patient already has.
            Whenever the admin changes the status (in either direction, including back
            to Active), the Reason + Date fields appear and are required. The date is
            pinned to today and rendered read-only — admins shouldn't be backdating
            status changes. */}
        {isEditing && (() => {
          // Date and Reason show whenever this save records a status: a change in either
          // direction, or a status the file holds without a date or reason (statusNeedsRecording).
          const statusChanged = statusNeedsRecording(formData, ruleOptions);
          const movedOffFile = originalStatus != null && formData.status !== originalStatus;
          // Today on the clinic's calendar (the browser's UTC date read as yesterday between
          // midnight and 5 AM in Pakistan), in ISO for the wire payload.
          const todayIso = clinicToday();
          const shownDate = formData.statusChangedAt || todayIso;
          const shownDateDisplay = formatClinicDate(shownDate, { day: 'numeric', month: 'long', year: 'numeric' });
          return (
            <div className="pt-4 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800 mb-3">Patient Status</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Status" required hintUrdu="مریض کی موجودہ حیثیت">
                  <Select
                    value={formData.status || 'ACTIVE'}
                    onChange={(next) => {
                      // One update. Stamp the change-date to today whenever the status moves
                      // away from its original value; clear it, and any reason started, when
                      // the admin reverts to the original (no change, no captured reason/date).
                      // None of it is a visit to Reason, so the box never opens already red.
                      const recordNext = statusNeedsRecording({ ...formData, status: next }, ruleOptions);
                      const dateOnFile = next === originalStatus ? existingPatient?.statusChangedAt : null;
                      onFieldsChange({
                        status: next,
                        statusChangedAt: recordNext ? (dateOnFile || todayIso) : '',
                        ...(recordNext ? {} : { statusReason: '' }),
                      }, { touched: false });
                    }}
                    options={[
                      { value: 'ACTIVE', label: 'Active' },
                      { value: 'DECEASED', label: 'Deceased' },
                      { value: 'SUSPENDED', label: 'Suspended' },
                      { value: 'BLACKLISTED', label: 'Blacklisted' },
                    ]}
                    buttonClassName="px-4 py-3 rounded-lg"
                  />
                </FormField>
                {statusChanged && (
                  <FormField field="statusChangedAt" label="Status Change Date" required hintUrdu="حیثیت تبدیل ہونے کی تاریخ">
                    {/* Read-only display chip — the actual value sent to the server is
                        formData.statusChangedAt, set in the Status onChange above (or, for a
                        status on file without a reason, the date already on file). Admins
                        can't pick a different date. */}
                    <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-semibold text-gray-700">{shownDateDisplay}</span>
                      <span className="ml-auto text-xs text-gray-500">{shownDate === todayIso ? 'Auto-set to today' : 'On file'}</span>
                    </div>
                  </FormField>
                )}
              </div>
              {statusChanged && (
                <div className="mt-4">
                  <FormField
                    field="statusReason"
                    label="Reason"
                    required
                    error={touched.statusReason && formErrors.statusReason}
                  >
                    <textarea
                      value={formData.statusReason || ''}
                      onChange={(e) => onFieldChange('statusReason', e.target.value)}
                      onBlur={() => onFieldBlur('statusReason')}
                      rows={3}
                      maxLength={500}
                      placeholder={
                        formData.status === 'DECEASED' ? 'e.g. Passed away on … due to …' :
                        formData.status === 'SUSPENDED' ? 'e.g. Account suspended pending review of …' :
                        formData.status === 'BLACKLISTED' ? 'e.g. Repeated no-shows / verbal abuse to staff on …' :
                        formData.status === 'ACTIVE' ? 'e.g. Account reactivated - patient returning after …' :
                        'Brief explanation for the status change'
                      }
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none ${
                        touched.statusReason && formErrors.statusReason ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                  </FormField>
                  <p className="text-xs text-gray-500 mt-1">
                    {movedOffFile
                      ? "Shown on the patient's view and as context wherever this status appears."
                      : 'This status is on file without a date or reason, so they are recorded with this save.'}
                  </p>
                </div>
              )}
            </div>
          );
        })()}

        {/* Already on file? Existing patients the form resembles, with the reason for each. A sure
            match (a repeated CNIC or B-Form) reads in red; a likely one in amber. Either holds
            Register until the receptionist says this is not one of them. Name, age and father are
            shown so an identity can be confirmed without opening anything. */}
        {!isEditing && lookalikes.length > 0 && (
          <section
            ref={lookalikeRef}
            aria-label="Already on file?"
            className={`rounded-xl border p-4 ${certain ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}
          >
            <p className={`font-semibold mb-3 ${certain ? 'text-red-700' : 'text-amber-800'}`}>
              {cnicTaken
                ? `This CNIC already belongs to ${cnicTaken.fullName} (${cnicTaken.mrn})`
                : certain
                ? `This ${certain.rules.includes('SAME_BFORM') ? 'B-Form' : 'CNIC'} already belongs to ${certain.fullName} (${certain.mrn})`
                : `Already on file? ${lookalikes.length === 1 ? '1 patient looks' : `${lookalikes.length} patients look`} like this one`}
            </p>
            <ul className="space-y-2">
              {lookalikes.map((c) => (
                <li key={c.id} className="flex items-center justify-between gap-3 bg-white border border-gray-200 rounded-lg px-3 py-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900">
                      {c.fullName}{c.age != null ? `, ${c.dobEstimated ? 'about ' : ''}${c.age === 0 ? 'under 1' : c.age}` : ''}
                      {c.fatherName && (
                        <span className="font-normal text-gray-600"> · {c.gender === 'FEMALE' ? 'd/o' : 's/o'} {c.fatherName}</span>
                      )}
                      <span className="ml-2 font-mono text-xs text-gray-500">{c.mrn}</span>
                    </div>
                    <div className="text-xs text-gray-600 mt-0.5">
                      <span className={`font-semibold ${c.strength === 'CERTAIN' ? 'text-red-700' : c.strength === 'LIKELY' ? 'text-amber-800' : 'text-gray-700'}`}>
                        {(c.reasons || []).join(' · ')}
                      </span>
                      {c.guardianName && ` · guardian ${c.guardianName}${c.guardianRelationship ? ` (${guardianRelLabel(c.guardianRelationship, null)})` : ''}`}
                      {c.phoneNumber && ` · ${c.phoneNumber}`}
                      {c.lastVisit && ` · last seen ${formatClinicDate(c.lastVisit, { day: 'numeric', month: 'short' })}`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onUseExisting(c)}
                    className="shrink-0 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700"
                  >
                    This is them
                  </button>
                </li>
              ))}
            </ul>
            {/* A repeated CNIC has no "different person" way out: the server keeps one patient
                per CNIC, so the only ways forward are to correct the number or open that file. */}
            {cnicTaken ? (
              <p className="text-xs text-gray-700 mt-3">
                A CNIC belongs to one person, so Register stays off while this number is typed. Check
                the number, or press &quot;This is them&quot; to open their file.
              </p>
            ) : mustAcknowledge && (
              <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
                <span className="text-xs text-gray-700">Register stays off until you confirm this is not one of them.</span>
                <button
                  type="button"
                  onClick={() => setAcknowledgedKey(lookalikeKey)}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50"
                >
                  {certain ? "It's a different person" : 'No, register as new'}
                </button>
              </div>
            )}
          </section>
        )}

      </div>

        {/* Form Actions. The body above scrolls; this bar does not, so the primary action and the
            reason it is unavailable stay on screen wherever the form has been scrolled to. Before
            this, a look-alike warning could sit unseen below the fold while the receptionist was
            still typing near the top. */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50 space-y-3">
          {blocked.map((line) => (
            <div
              key={line.key}
              className={`flex items-center justify-between gap-3 flex-wrap rounded-lg border px-3 py-2 ${
                line.tone === 'red' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
              }`}
            >
              <span
                id={`${blockedIdBase}-${line.key}`}
                className={`text-sm font-medium ${line.tone === 'red' ? 'text-red-800' : 'text-amber-900'}`}
              >
                {line.text}
              </span>
              <button
                type="button"
                onClick={line.onAct}
                className="shrink-0 px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50"
              >
                {line.action}
              </button>
            </div>
          ))}
          <div className="flex space-x-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200 font-medium"
          >
            Cancel
          </button>
          {!isEditing && (
            <button
              type="button"
              onClick={onReset}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200 font-medium"
            >
              Clear Form
            </button>
          )}
          <button
            type="submit"
            disabled={saveHeld}
            aria-describedby={blocked.length > 0 ? blocked.map((line) => `${blockedIdBase}-${line.key}`).join(' ') : undefined}
            onBlur={(e) => {
              // A focused button that turns disabled drops keyboard focus to the top of the page. Send
              // it to the first field the save still needs instead, showing that field's message.
              if (e.currentTarget.disabled) goToFirstHold();
            }}
            className="flex-1 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 font-medium"
          >
            {loading
              ? (isEditing ? 'Saving…' : 'Registering…')
              : (isEditing ? 'Save Changes' : 'Register Patient')}
          </button>
          </div>
        </div>
      </form>
    </div>
  </div>
  );
};

// Form Field Component
// A number written with separators (a CNIC, a phone number) inside an Urdu line has to be isolated
// as left-to-right text. After Arabic-script letters the bidi algorithm counts the digits as Arabic
// numbers, which a hyphen does not join, so each group was laid out as its own right-to-left piece:
// the example CNIC 42401-1234567-8 showed as 8-1234567-42401. A lone number is left alone; it
// already reads correctly ("5 دن").
const LTR_NUMBER = /(\+?\d+(?:[-+/:.]\d+)+)/;
const isolateNumbers = (text) => (typeof text === 'string'
  ? text.split(LTR_NUMBER).map((part, i) => (i % 2 ? <bdi key={i} dir="ltr">{part}</bdi> : part))
  : text);

const FormField = ({ label, required, error, hint, hintUrdu, children, className = '', field }) => (
  // data-invalid marks a field showing an error, so the form can send focus to the first one.
  // data-field names the form value it holds, so "Take me there" can find a field the save still
  // needs before it has shown any message.
  <div className={className} data-invalid={error ? 'true' : undefined} data-field={field}>
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {error && (
      <div className="mt-1 flex items-center space-x-1 text-red-600">
        <AlertCircle className="h-4 w-4" />
        <span className="text-sm">{error}</span>
      </div>
    )}
    {hint && !error && (
      <p className="mt-1 text-sm text-gray-500">{hint}</p>
    )}
    {/* The Urdu note under the field, for the receptionist reading the form in their own
        language. App.css right-aligns .urdu-text, which is correct for a line of RTL prose; the
        line-height is set here because Nastaliq's ink runs about 2.5x the font size and the
        default line box clips its descenders. */}
    {hintUrdu && !error && (
      <p className="urdu-text mt-1 text-sm text-gray-500" dir="rtl" style={{ lineHeight: 2.2 }}>
        {isolateNumbers(hintUrdu)}
      </p>
    )}
  </div>
);

// Registration Success Modal - Shows after patient is registered
const RegistrationSuccessModal = ({ patient, onClose, onIssueToken }) => {
  const successDialogRef = useDialog(onClose, { confirmDiscard: false });
  return (
  <div ref={successDialogRef} className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden">
      {/* Success Header */}
      <div className="bg-gradient-to-br from-navy-800 via-navy-900 to-navy-950 p-8 text-center relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative">
          <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <CheckCircle className="h-12 w-12 text-emerald-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Patient Registered Successfully!</h2>
          <p className="text-white text-opacity-90">The patient has been added to the system</p>
        </div>
      </div>

      {/* Patient Info Card */}
      <div className="p-6">
        <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-5 border border-gray-200 mb-6">
          <div className="flex items-center space-x-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-lg">
              {patient.firstName?.charAt(0)}{patient.lastName?.charAt(0)}
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {patient.fullName || `${patient.firstName} ${patient.lastName}`}
              </h3>
              <div className="flex items-center space-x-2 mt-1">
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                  patient.adult || patient.isAdult ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                }`}>
                  {patient.adult || patient.isAdult ? 'Adult' : 'Minor'}
                </span>
                <span className="text-gray-500 text-sm">•</span>
                <span className="text-gray-600 text-sm">{patient.dateOfBirth ? formatPatientAge(patient) : `${patient.age} years`}</span>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center space-x-2 p-3 bg-white rounded-lg">
              <Shield className="h-5 w-5 text-emerald-500" />
              <div>
                <div className="text-xs text-gray-500">MRN</div>
                <div className="font-bold text-gray-900">{patient.mrn}</div>
              </div>
            </div>
            <div className="flex items-center space-x-2 p-3 bg-white rounded-lg">
              <Phone className="h-5 w-5 text-blue-500" />
              <div>
                <div className="text-xs text-gray-500">Phone</div>
                <div className="font-bold text-gray-900">{patient.phoneNumber}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          {/* Primary: Issue Token */}
          <button
            onClick={onIssueToken}
            className="w-full flex items-center justify-center space-x-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-5 py-2.5 font-medium transition-colors"
          >
            <Sparkles className="h-5 w-5" />
            <span>Issue Token Now</span>
            <ArrowRight className="h-5 w-5" />
          </button>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium"
          >
            <X className="h-5 w-5" />
            <span>Close</span>
          </button>
        </div>

        {/* Helper text */}
        <p className="flex items-center justify-center gap-1.5 text-center text-gray-500 text-sm mt-4">
          <Lightbulb className="h-4 w-4 text-amber-500" />
          <span>Tip: Issue a token now to add the patient to today's queue</span>
        </p>
      </div>
    </div>
  </div>
  );
};

// Beautiful Token Success Toast Notification
const TokenSuccessToast = ({ tokenNumber, patientName, message, type, onClose }) => (
  <div className="fixed top-6 right-6 z-50 animate-slide-in">
    <div className={`${
      type === 'success' 
        ? 'bg-gradient-to-r from-navy-800 to-navy-700' 
        : 'bg-gradient-to-r from-red-500 to-rose-600'
    } rounded-2xl shadow-2xl p-1`}>
      <div className="bg-white rounded-xl p-4 flex items-start space-x-4 min-w-[320px] max-w-md">
        {type === 'success' && tokenNumber ? (
          <>
            {/* Token Badge */}
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-gradient-to-br from-navy-700 to-navy-900 rounded-xl flex flex-col items-center justify-center text-white shadow-lg">
                <span className="text-xs font-medium opacity-90">TOKEN</span>
                <span className="text-xl font-bold">{tokenNumber}</span>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <CheckCircle className="h-5 w-5 text-emerald-500" />
                <span className="font-bold text-gray-900">Token Issued!</span>
              </div>
              <p className="text-gray-600 text-sm truncate">
                {patientName && `For ${patientName}`}
              </p>
              <div className="flex items-center space-x-2 mt-2">
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded-full">
                  Added to Queue
                </span>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Error Icon */}
            <div className="flex-shrink-0">
              <div className={`w-12 h-12 ${
                type === 'error' ? 'bg-red-100' : 'bg-emerald-100'
              } rounded-xl flex items-center justify-center`}>
                {type === 'error' ? (
                  <AlertCircle className="h-6 w-6 text-red-500" />
                ) : (
                  <CheckCircle className="h-6 w-6 text-emerald-500" />
                )}
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1">
              <p className={`font-medium ${type === 'error' ? 'text-red-700' : 'text-gray-900'}`}>
                {message}
              </p>
            </div>
          </>
        )}
        
        {/* Close Button */}
        <button
          aria-label="Close"
          onClick={onClose}
          className="flex-shrink-0 p-1 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="h-4 w-4 text-gray-400" />
        </button>
      </div>
    </div>
    
    {/* Progress bar for auto-close */}
    <div className="mt-1 mx-2">
      <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${type === 'success' ? 'bg-emerald-500' : 'bg-red-500'} animate-shrink`}
        />
      </div>
    </div>
    
    <style jsx>{`
      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      @keyframes shrink {
        from {
          width: 100%;
        }
        to {
          width: 0%;
        }
      }
      .animate-slide-in {
        animation: slideIn 0.3s ease-out;
      }
      .animate-shrink {
        animation: shrink 4s linear forwards;
      }
    `}</style>
  </div>
);

export default PatientManagement;